import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { BsFire } from "react-icons/bs";
import {
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  Mail,
  MessageCircle,
  MapPin,
  Phone,
  RefreshCw,
  Users,
  X,
  TrendingUp,
  Clock,
  Star,
  Activity,
  FileText,
  Plus,
  Send,
  ChevronUp,
  Zap,
  UserCheck,
  Upload,
} from "lucide-react";
import WorkAuthorizationLabel from "./WorkAuthorizationLabel";
import RDCandidateSearchInput from "./RDCandidateSearchInput";
import RDEmailComposeDialog from "./RDEmailComposeDialog";
import CandidateUpdateModal from "../components/CandidateUpdateModal";
import CandidateEngagementModal from "../components/CandidateEngagementModal";
import { getHostlistGroupNames } from "../service/hotlistApi";

const RD_API_BASE_URL = "https://candi.vdpl.co/api/index.php/";
const SAVE_EXPERIENCE_URL = `${RD_API_BASE_URL}save_experience`;
const GET_EXPERIENCE_URL = `${RD_API_BASE_URL}api_candidate_experience`;
const SAVE_SUBMISSION_URL = `${RD_API_BASE_URL}save_submission`;
const GET_CANDIDATE_SUBMISSION_DETAILS_URL = `${RD_API_BASE_URL}getCandidate_submission_details`;

const buildUploadedDocumentUrl = (fileUrl, rdApiBaseUrl = RD_API_BASE_URL) => {
  const normalizedFileUrl = String(fileUrl || "").trim();
  if (!normalizedFileUrl) return "";
  if (/^https?:\/\//i.test(normalizedFileUrl)) return normalizedFileUrl;

  const baseUrl = String(rdApiBaseUrl || RD_API_BASE_URL || "").trim();
  const apiRoot = baseUrl.includes("/index.php")
    ? baseUrl.split("/index.php")[0]
    : baseUrl.replace(/\/$/, "");

  return `${apiRoot}/${normalizedFileUrl.replace(/^\/+/, "")}`;
};

const toHotlistGroups = (payload) => {
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.groups)
      ? payload.groups
      : Array.isArray(payload?.group_names)
        ? payload.group_names
        : Array.isArray(payload)
          ? payload
          : [];

  return rows
    .map((item) => {
      if (typeof item === "string") {
        const name = item.trim();
        return name ? { id: name, name } : null;
      }

      const name = String(
        item?.name ?? item?.group_name ?? item?.hotlist_group_name ?? "",
      ).trim();
      const id = item?.group_id ?? item?.id ?? name;
      return name ? { id: String(id), name } : null;
    })
    .filter(Boolean);
};

const buildPrimaryLocation = (city, state, country) => {
  const parts = [country, state, city]
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  return parts.join(", ");
};

const buildCandidateLocation = (item = {}, fallback = {}) =>
  buildPrimaryLocation(
    item?.project_city || item?.city || fallback?.candidateInfo?.city,
    item?.project_state || item?.state || fallback?.candidateInfo?.state,
    item?.project_country || item?.country || fallback?.candidateInfo?.country,
  ) ||
  item?.location ||
  fallback?.location ||
  "";

const SUBMISSION_TYPE_OPTIONS = [
  "Client Submission",
  "Closure",
  "Internal Submission",
  "Interview",
];

const getSubmissionEntrySortTimestamp = (entry) => {
  const raw =
    entry?.last_changed_at ||
    entry?.lastChangedAt ||
    entry?.updated_at ||
    entry?.updatedAt ||
    entry?.updated_on ||
    entry?.updatedOn ||
    entry?.submission_date ||
    entry?.submittedAt ||
    entry?.submitted_at ||
    entry?.date ||
    entry?.created_at ||
    entry?.createdAt ||
    null;
  const parsed = parseTimestamp(raw);
  return parsed ?? 0;
};

const buildSubmissionHistory = (submission) => {
  if (!submission) return [];
  const history = Array.isArray(submission.history) ? submission.history : [];
  const baseEntries =
    history.length > 0 ? history : [{ ...submission, history: undefined }];
  return baseEntries
    .filter(
      (entry) =>
        entry &&
        (entry.submittedAt ||
          entry.submissionStatus ||
          entry.submissionType ||
          entry.role ||
          entry.client ||
          entry.reason),
    )
    .map((entry, index) => ({
      ...entry,
      id: entry.id || `submission-${index}`,
    }))
    .sort(
      (a, b) =>
        getSubmissionEntrySortTimestamp(b) - getSubmissionEntrySortTimestamp(a),
    );
};

const toTitleCase = (value) =>
  value
    .split(" ")
    .map((part) =>
      part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : "",
    )
    .join(" ");

const getIsoWeekNumber = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const target = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
  return String(weekNo);
};

const getQuarterFromDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return String(Math.floor(date.getMonth() / 3) + 1);
};

const getDateInputValue = (date = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isFutureDateInputValue = (value) => {
  const dateValue = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return false;
  return dateValue > getDateInputValue();
};

const renderPortal = (node) => {
  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
};

const toSingleScalar = (value) => {
  if (Array.isArray(value)) {
    const firstValid = value.find(
      (item) =>
        item !== null && item !== undefined && String(item).trim() !== "",
    );
    return toSingleScalar(firstValid);
  }
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (text.includes(",")) return text.split(",")[0].trim();
  return text;
};
const getLinkedinUrl = (candidate) => {
  if (!candidate || typeof candidate !== "object") return "";
  const value =
    candidate.linkedinUrl ??
    candidate.linkedin_url ??
    candidate.candidateInfo?.linkedinUrl ??
    candidate.candidateInfo?.linkedin_url ??
    "";
  return String(value).trim();
};
const splitRecipients = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

const tokenizeSearchQuery = (query) => {
  const tokens = [];
  const re = /"([^"]+)"|\(|\)|\bAND\b|\bOR\b|\bNOT\b|[^\s()]+/gi;
  let match = re.exec(query);
  while (match) {
    tokens.push(match[1] || match[0]);
    match = re.exec(query);
  }
  return tokens;
};

const isBooleanOperator = (token) =>
  ["AND", "OR", "NOT"].includes(String(token || "").toUpperCase());

const isSearchOperand = (token) => {
  if (!token) return false;
  const value = String(token).toUpperCase();
  return (
    value !== "AND" &&
    value !== "OR" &&
    value !== "NOT" &&
    token !== "(" &&
    token !== ")"
  );
};

const buildBooleanSearchTokens = (query) => {
  const raw = tokenizeSearchQuery(query)
    .map((token) => {
      const normalized = String(token).trim();
      const upper = normalized.toUpperCase();
      if (upper === "AND" || upper === "OR" || upper === "NOT") return upper;
      return normalized;
    })
    .filter(Boolean);
  if (raw.length === 0) return [];
  const expanded = [];
  for (let i = 0; i < raw.length; i += 1) {
    const current = raw[i];
    const prev = expanded[expanded.length - 1];
    const needsImplicitAnd =
      (isSearchOperand(prev) || prev === ")") &&
      (isSearchOperand(current) || current === "(" || current === "NOT");
    if (needsImplicitAnd) expanded.push("AND");
    expanded.push(current);
  }
  return expanded;
};

const evaluateBooleanSearch = (query, text) => {
  const tokens = buildBooleanSearchTokens(query);
  if (tokens.length === 0) return true;
  const precedence = { OR: 1, AND: 2, NOT: 3 };
  const output = [];
  const operators = [];
  tokens.forEach((token) => {
    if (token === "(") {
      operators.push(token);
      return;
    }
    if (token === ")") {
      while (operators.length > 0 && operators[operators.length - 1] !== "(")
        output.push(operators.pop());
      if (operators[operators.length - 1] === "(") operators.pop();
      return;
    }
    if (isBooleanOperator(token)) {
      while (
        operators.length > 0 &&
        isBooleanOperator(operators[operators.length - 1]) &&
        precedence[operators[operators.length - 1]] >= precedence[token]
      )
        output.push(operators.pop());
      operators.push(token);
      return;
    }
    output.push(token);
  });
  while (operators.length > 0) output.push(operators.pop());
  const source = String(text || "").toLowerCase();
  const stack = [];
  for (let i = 0; i < output.length; i += 1) {
    const token = output[i];
    if (token === "NOT") {
      stack.push(!Boolean(stack.pop()));
      continue;
    }
    if (token === "AND" || token === "OR") {
      const right = Boolean(stack.pop());
      const left = Boolean(stack.pop());
      stack.push(token === "AND" ? left && right : left || right);
      continue;
    }
    stack.push(source.includes(String(token).toLowerCase()));
  }
  return stack.length ? Boolean(stack[stack.length - 1]) : true;
};

const tokenizeSimpleSearchTerms = (query) => {
  const re = /"([^"]+)"|[^\s]+/g;
  const terms = [];
  let match = re.exec(query);
  while (match) {
    terms.push((match[1] || match[0]).trim());
    match = re.exec(query);
  }
  return terms.filter(Boolean);
};

const pickFirstText = (entry, keys, fallback = "N/A") => {
  if (!entry || typeof entry !== "object") return fallback;
  for (const key of keys) {
    const value = entry?.[key];
    if (value !== null && value !== undefined && String(value).trim() !== "")
      return String(value).trim();
  }
  return fallback;
};

const extractCandidateEngagementRows = (payload) => {
  const candidates = [
    payload?.data,
    payload?.history,
    payload?.engagement,
    payload?.candidate_engagement,
    payload?.candidateEngagement,
    payload?.records,
    payload,
  ];
  for (const item of candidates) {
    if (Array.isArray(item)) return item;
  }
  const nested = payload?.data;
  if (nested && typeof nested === "object") {
    const nestedCandidates = [
      nested?.data,
      nested?.history,
      nested?.engagement,
      nested?.candidate_engagement,
      nested?.candidateEngagement,
      nested?.records,
    ];
    for (const item of nestedCandidates) {
      if (Array.isArray(item)) return item;
    }
    if (Object.keys(nested).length > 0) return [nested];
  }
  return [];
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const parseTimestamp = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "n/a") return null;
  const normalized =
    text.includes(" ") && !text.includes("T") ? text.replace(" ", "T") : text;
  const ms = Date.parse(normalized);
  if (Number.isFinite(ms)) return ms;
  const fallback = new Date(text).getTime();
  return Number.isFinite(fallback) ? fallback : null;
};
const getLatestCandidateResponseTimestamp = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const dateKeys = ["engagement_updated_at", "engagementUpdatedAt"];
  let latest = null;
  list.forEach((row) => {
    if (!row || typeof row !== "object") return;
    dateKeys.forEach((key) => {
      const ms = parseTimestamp(row?.[key]);
      if (ms !== null && (latest === null || ms > latest)) latest = ms;
    });
  });
  return latest;
};

const isCandidateOnBench = (candidate) => {
  if (!candidate) return false;
  const raw =
    candidate?.is_candidate_on_bench ??
    candidate?.isCandidateOnBench ??
    candidate?.candidateInfo?.is_candidate_on_bench ??
    candidate?.candidateInfo?.isCandidateOnBench;
  const normalized = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return true;
  const status = String(candidate?.status ?? "")
    .trim()
    .toLowerCase();
  return status === "on bench";
};

// ─── Reusable UI Primitives ────────────────────────────────────────────────

const getMappingImportRows = (preview) => {
  const candidates = [
    preview?.rows,
    preview?.changes,
    preview?.data?.rows,
    preview?.data?.changes,
    preview?.preview?.rows,
    preview?.preview?.changes,
  ];
  return candidates.find(Array.isArray) || [];
};

const getMappingImportSummary = (preview) => {
  const source = preview?.summary || preview?.data?.summary || preview || {};
  const numberFrom = (...keys) =>
    keys.reduce((value, key) => {
      if (value !== null) return value;
      const parsed = Number(source?.[key]);
      return Number.isFinite(parsed) ? parsed : null;
    }, null) || 0;

  return {
    inserts: numberFrom("inserts", "inserted", "insert_count", "new_rows"),
    updates: numberFrom("updates", "updated", "update_count"),
    skipped: numberFrom("skipped", "errors", "error_count", "skip_count"),
  };
};

const normalizeMappingKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getMappingCellValue = (row, keys = []) => {
  if (!row || typeof row !== "object") return "";

  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  const normalizedKeys = keys.map(normalizeMappingKey);
  const match = Object.entries(row).find(([key, value]) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return false;
    }
    return normalizedKeys.includes(normalizeMappingKey(key));
  });

  return match ? match[1] : "";
};

const getMappingRowChanges = (row) => {
  const rawChanges = row?.changes || row?.field_changes || row?.diff || [];
  if (Array.isArray(rawChanges)) {
    return rawChanges.map((change) => ({
      table: change?.table || change?.table_name || row?.table || "",
      field: change?.field || change?.column || change?.name || "",
      oldValue:
        change?.oldValue ??
        change?.old_value ??
        change?.from ??
        change?.before ??
        "",
      newValue:
        change?.newValue ??
        change?.new_value ??
        change?.to ??
        change?.after ??
        "",
    }));
  }
  if (rawChanges && typeof rawChanges === "object") {
    return Object.entries(rawChanges).map(([field, value]) => ({
      table: row?.table || "",
      field,
      oldValue: value?.oldValue ?? value?.old_value ?? value?.from ?? "",
      newValue: value?.newValue ?? value?.new_value ?? value?.to ?? "",
    }));
  }
  return [];
};

const Avatar = ({ name = "", size = "md", className = "" }) => {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
  const palettes = [
    "from-indigo-500 via-purple-500 to-pink-500",
    "from-cyan-500 via-blue-500 to-indigo-500",
    "from-emerald-400 via-teal-500 to-cyan-500",
    "from-rose-400 via-pink-500 to-fuchsia-500",
    "from-amber-400 via-orange-500 to-rose-500",
    "from-violet-500 via-purple-500 to-indigo-500",
  ];
  const color = palettes[name.charCodeAt(0) % palettes.length] || palettes[0];
  const sizes = {
    sm: "w-8 h-8 text-xs",
    md: "w-11 h-11 text-sm",
    lg: "w-16 h-16 text-lg",
  };
  return (
    <div
      className={`bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center font-black text-white shadow-lg ${sizes[size]} ${className} relative overflow-hidden`}
    >
      <div className="absolute inset-0 bg-white/10 rounded-2xl" />
      <span className="relative z-10 tracking-tight">{initials || "?"}</span>
    </div>
  );
};

const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-slate-100 text-slate-500 border border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border border-emerald-200/80",
    info: "bg-sky-50 text-sky-700 border border-sky-200/80",
    warning: "bg-amber-50 text-amber-700 border border-amber-200/80",
    danger: "bg-rose-50 text-rose-700 border border-rose-200/80",
    active:
      "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm shadow-indigo-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${variants[variant]} ${className}`}
    >
      {children}
    </span>
  );
};

const GlassCard = ({
  children,
  className = "",
  hover = false,
  accent = false,
}) => (
  <div
    className={`bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.04)] ${hover ? "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08),0_20px_40px_rgba(0,0,0,0.06)]" : ""} ${accent ? "ring-1 ring-indigo-200/60" : ""} ${className}`}
  >
    {children}
  </div>
);

const StatCard = ({
  label,
  value,
  icon: Icon,
  trend,
  color = "blue",
  iconClassName = "",
}) => {
  const themes = {
    blue: {
      grad: "from-sky-500 to-blue-600",
      light: "from-sky-50 to-blue-50",
      text: "text-blue-700",
      border: "border-blue-100",
    },
    violet: {
      grad: "from-violet-500 to-indigo-600",
      light: "from-violet-50 to-indigo-50",
      text: "text-indigo-700",
      border: "border-indigo-100",
    },
    emerald: {
      grad: "from-emerald-500 to-teal-600",
      light: "from-emerald-50 to-teal-50",
      text: "text-emerald-700",
      border: "border-emerald-100",
    },
    rose: {
      grad: "from-rose-500 to-pink-600",
      light: "from-rose-50 to-pink-50",
      text: "text-rose-700",
      border: "border-rose-100",
    },
    amber: {
      grad: "from-amber-500 to-orange-600",
      light: "from-amber-50 to-orange-50",
      text: "text-amber-700",
      border: "border-amber-100",
    },
  };
  const t = themes[color] || themes.blue;
  return (
    <div
      className={`relative flex min-h-[108px] overflow-hidden rounded-2xl bg-gradient-to-br ${t.light} border ${t.border} p-4 group`}
    >
      {Icon && (
        <div
          className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br ${t.grad} shadow-md ${iconClassName}`}
        >
          <Icon className="h-3.5 w-3.5 text-white" />
        </div>
      )}
      <div className="flex w-full items-end">
        <div className="min-w-0">
          <p className="whitespace-nowrap text-[9px] font-bold uppercase tracking-widest text-slate-400">
            {label}
          </p>
          <p className="mt-2 text-[28px] font-black leading-none text-slate-900 tabular-nums">
            {value}
          </p>
          {trend && (
            <p className="mt-0.5 text-[11px] text-slate-400">{trend}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const getNormalizedSubmissionText = (entry) => {
  const type = String(entry?.submissionType || entry?.submission_type || "")
    .trim()
    .toLowerCase();
  const status = String(
    entry?.submissionStatus || entry?.submission_status || "",
  )
    .trim()
    .toLowerCase();
  const reason = String(entry?.reason || "")
    .trim()
    .toLowerCase();
  return `${type} ${status} ${reason}`.trim();
};

const isRejectedOrNotPlacedEntry = (entry) => {
  const text = getNormalizedSubmissionText(entry);
  return (
    text.includes("reject") ||
    text.includes("not placed") ||
    text.includes("not joined") ||
    text.includes("backed out") ||
    text.includes("backout")
  );
};

const getSubmissionDisplayStage = (entry) => {
  const typeText = String(entry?.submissionType || entry?.submission_type || "")
    .trim()
    .toLowerCase();
  const statusText = String(
    entry?.submissionStatus || entry?.submission_status || "",
  )
    .trim()
    .toLowerCase();
  const reasonText = String(entry?.reason || "")
    .trim()
    .toLowerCase();
  const text = `${typeText} ${statusText} ${reasonText}`.trim();

  if (!text) return "Pipeline";
  if (isRejectedOrNotPlacedEntry(entry)) return "Not Joined";

  // Placement-level outcomes
  if (
    statusText.includes("candidate has started") ||
    statusText.includes("has started") ||
    statusText.includes("placed") ||
    statusText.includes("joined") ||
    statusText.includes("bgv clearance") ||
    text.includes("placement")
  )
    return "Placement";

  if (statusText.includes("confirm") || text.includes("confirmation"))
    return "Confirmation";
  if (
    statusText.includes("interview") ||
    statusText.includes("screen") ||
    typeText.includes("interview")
  )
    return "Interview";
  if (typeText.includes("client submission")) return "Client Submission";
  if (
    typeText.includes("internal submission") ||
    statusText.includes("submission") ||
    statusText.includes("started") ||
    statusText.includes("awaiting")
  )
    return "Submission";

  if (typeText.includes("closure") || statusText.includes("closure"))
    return "Confirmation";
  return "Pipeline";
};

const PipelineProgress = ({ stages, activeIndex, isDanger = false }) => (
  <div className="relative mt-5">
    <div className="flex items-center gap-0">
      {stages.map((stage, i) => {
        const isActive = i === activeIndex;
        const isComplete = i < activeIndex;
        return (
          <React.Fragment key={stage}>
            <div className="flex flex-col items-center relative">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black border-2 transition-all duration-300 ${
                  isComplete
                    ? isDanger
                      ? "bg-rose-600 border-rose-600 text-white shadow-sm shadow-rose-200"
                      : "bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-200"
                    : isActive
                      ? isDanger
                        ? "bg-white border-rose-500 text-rose-600 shadow-md shadow-rose-100"
                        : "bg-white border-indigo-500 text-indigo-600 shadow-md shadow-indigo-100"
                      : "bg-white border-slate-200 text-slate-300"
                }`}
              >
                {isComplete ? "✓" : i + 1}
              </div>
              <p
                className={`mt-1.5 text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${
                  isActive
                    ? isDanger
                      ? "text-rose-600"
                      : "text-indigo-600"
                    : isComplete
                      ? "text-slate-600"
                      : "text-slate-300"
                }`}
              >
                {stage}
              </p>
            </div>
            {i < stages.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-1 mb-3.5 rounded-full transition-all duration-500 ${
                  i < activeIndex
                    ? isDanger
                      ? "bg-gradient-to-r from-rose-500 to-rose-400"
                      : "bg-gradient-to-r from-indigo-500 to-indigo-400"
                    : "bg-slate-100"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

const InputField = ({ label, children, className = "" }) => {
  const normalizedLabel = typeof label === "string" ? label.trim() : label;
  const isRequiredLabel =
    typeof normalizedLabel === "string" && normalizedLabel.endsWith("*");
  const labelText = isRequiredLabel
    ? normalizedLabel.slice(0, -1).trim()
    : normalizedLabel;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {labelText}
          {isRequiredLabel ? (
            <span className="ml-1 text-rose-500">*</span>
          ) : null}
        </label>
      )}
      {children}
    </div>
  );
};

// ─── Fullscreen Loading Overlay ────────────────────────────────────────────
const inputCls =
  "w-full rounded-xl border border-slate-200/80 bg-slate-50/60 px-3.5 py-2.5 text-sm text-slate-700 placeholder:text-slate-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all duration-150";
const selectCls = `${inputCls} cursor-pointer`;
const inputErrorCls =
  "border-rose-300 bg-rose-50/70 placeholder:text-rose-300 focus:border-rose-500 focus:ring-rose-200";

// ─── Main Component ────────────────────────────────────────────────────────

const RDMappingImportModal = ({
  isOpen,
  onClose,
  onPreview,
  onConfirm,
  onDownloadTemplate,
  preview,
  loading,
  confirming,
  error,
}) => {
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!isOpen) setFile(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const rows = getMappingImportRows(preview);
  const summary = getMappingImportSummary(preview);
  const canConfirm = Boolean(preview) && !confirming && !loading;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-950/60 backdrop-blur-md p-4">
      <div className="w-full max-w-6xl rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 scale-in overflow-hidden">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-200">
              <Upload className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-black text-slate-900">
                Import RD Mapping
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Validates Emp, Team Name, and BU before applying changes.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[calc(95vh-90px)] overflow-y-auto p-6">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-xs font-black uppercase tracking-wide text-slate-500">
                    Upload Excel/CSV
                  </label>
                  <button
                    type="button"
                    onClick={onDownloadTemplate}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-black text-emerald-700 transition-all hover:border-emerald-300 hover:bg-emerald-100"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Template
                  </button>
                </div>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                  className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-indigo-700"
                  disabled={loading || confirming}
                />
                <button
                  type="button"
                  onClick={() => file && onPreview(file)}
                  disabled={!file || loading || confirming}
                  className="mt-3 w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-black text-white shadow-sm transition-all hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Checking mapping..." : "Preview Changes"}
                </button>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <h4 className="text-sm font-black text-slate-900">
                  Required columns
                </h4>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  {["Emp", "BU", "Recruiters name", "Role", "Team Name"].map(
                    (column) => (
                      <span
                        key={column}
                        className="rounded-lg bg-slate-100 px-2.5 py-1.5 font-bold"
                      >
                        {column}
                      </span>
                    ),
                  )}
                </div>
              </div>

              {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">
                  {error}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  ["Inserts", summary.inserts, "emerald"],
                  ["Updates", summary.updates, "amber"],
                  ["Skipped", summary.skipped, "rose"],
                ].map(([label, count, color]) => (
                  <div
                    key={label}
                    className={`rounded-2xl border p-4 ${
                      color === "emerald"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : color === "amber"
                          ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-rose-200 bg-rose-50 text-rose-700"
                    }`}
                  >
                    <div className="text-2xl font-black">{count}</div>
                    <div className="text-xs font-bold uppercase tracking-wide">
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <h4 className="text-sm font-black text-slate-900">
                    Preview changes
                  </h4>
                </div>
                <div className="max-h-[420px] overflow-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="sticky top-0 bg-white text-left text-xs font-black uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Row</th>
                        <th className="px-4 py-3">Emp</th>
                        <th className="px-4 py-3">Recruiter</th>
                        <th className="px-4 py-3">Team</th>
                        <th className="px-4 py-3">BU</th>
                        <th className="px-4 py-3">Action</th>
                        <th className="px-4 py-3">Changes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-4 py-8 text-center text-sm font-semibold text-slate-400"
                          >
                            Upload a file and preview to see mapping changes.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, index) => {
                          const changes = getMappingRowChanges(row);
                          const action = String(
                            row?.action || row?.status || row?.change_type || "",
                          ).toLowerCase();
                          const rowNumber = getMappingCellValue(row, [
                            "row_number",
                            "row",
                            "rowNumber",
                          ]);
                          const emp = getMappingCellValue(row, [
                            "emp",
                            "Emp",
                            "EMP",
                            "employee_id",
                            "employeeId",
                            "employeeID",
                            "emp_id",
                            "empId",
                            "Emp ID",
                            "Employee ID",
                          ]);
                          const recruiter = getMappingCellValue(row, [
                            "recruiter_name",
                            "recruiterName",
                            "Recruiters name",
                            "Recruiter Name",
                            "recruiter",
                            "name",
                          ]);
                          const team = getMappingCellValue(row, [
                            "team_name",
                            "teamName",
                            "Team Name",
                          ]);
                          const bu = getMappingCellValue(row, [
                            "bu",
                            "BU",
                            "bu_name",
                            "business_unit",
                            "Business Unit",
                          ]);
                          return (
                            <tr
                              key={`${rowNumber || index}-${emp || index}`}
                            >
                              <td className="px-4 py-3 font-semibold text-slate-500">
                                {rowNumber || index + 2}
                              </td>
                              <td className="px-4 py-3 font-bold text-slate-800">
                                {emp || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {recruiter || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {team || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {bu || "-"}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`rounded-full px-2 py-1 text-xs font-black ${
                                    action.includes("skip") ||
                                    action.includes("error")
                                      ? "bg-rose-100 text-rose-700"
                                      : action.includes("insert") ||
                                          action.includes("new")
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {row?.action || row?.status || "update"}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {row?.error || row?.message ? (
                                  <span className="font-semibold text-rose-600">
                                    {row.error || row.message}
                                  </span>
                                ) : changes.length > 0 ? (
                                  <div className="space-y-1">
                                    {changes
                                      .slice(0, 3)
                                      .map((change, changeIndex) => (
                                        <div
                                          key={`${change.field}-${changeIndex}`}
                                          className="text-xs text-slate-600"
                                        >
                                          <span className="font-black text-slate-800">
                                            {change.table
                                              ? `${change.table}.`
                                              : ""}
                                            {change.field}:
                                          </span>{" "}
                                          <span className="text-rose-600">
                                            {String(change.oldValue || "blank")}
                                          </span>{" "}
                                          -&gt;{" "}
                                          <span className="text-emerald-700">
                                            {String(change.newValue || "blank")}
                                          </span>
                                        </div>
                                      ))}
                                    {changes.length > 3 && (
                                      <div className="text-xs font-semibold text-slate-400">
                                        +{changes.length - 3} more
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs font-semibold text-slate-400">
                                    No field changes
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-2.5 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50"
              disabled={confirming}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!canConfirm}
              className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-black text-white shadow-sm transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {confirming ? "Importing..." : "Confirm Import"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RDTeamSection = ({
  rdOverviewCandidate,
  rdSubmissions,
  setRdSubmissions,
  getRdAvailabilityStatus,
  searchTerm,
  setSearchTerm,
  rdAdvancedFilters,
  setRdAdvancedFilters,
  rdLocationOptions,
  rdExperienceOptions,
  onOpenBulkEmail,
  selectedRdCandidate,
  setSelectedRdCandidate,
  setSubmissionForm,
  getQuarterDefaults,
  setShowSubmissionModal,
  getRdPipelineStage,
  pipelineSelectedStage,
  setPipelineSelectedStage,
  showExperienceModal,
  setShowExperienceModal,
  newExperience,
  setNewExperience,
  showSubmissionModal,
  submissionForm,
  submissionRoleOptions,
  setSubmissionRoleOptions,
  submissionClientOptions,
  setSubmissionClientOptions,
  currentUser,
  rdVisibleCandidates,
  dataLoading,
  rdStatusOptions,
  setShowAddRdModal,
  showAddRdModal,
  setRdCandidates,
  setRdSidebarCandidate,
  onAddToHotlist,
  onSaveCandidateInfo,
  onBenchUpdated,
  workAuthMap,
  workAuthMapStatus,
  workAuthMapError,
  rdApiBaseUrl,
  rdAllCandidatesCount = 0,
  previewOnly = false,
  onClosePreview,
}) => {
  const [showCandidateInfoModal, setShowCandidateInfoModal] = useState(false);
  const [candidateInfoTarget, setCandidateInfoTarget] = useState(null);
  const [candidateInfoDraft, setCandidateInfoDraft] = useState(null);
  const [showRdAdvancedFilters, setShowRdAdvancedFilters] = useState(false);
  const [showSubmissionHistory, setShowSubmissionHistory] = useState(false);
  const [submissionLogFilter, setSubmissionLogFilter] = useState("all");
  const [submissionCompanyQuery, setSubmissionCompanyQuery] = useState("");
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [submissionRecruiterQuery, setSubmissionRecruiterQuery] = useState("");
  const [isRecruiterDropdownOpen, setIsRecruiterDropdownOpen] = useState(false);
  const [isRecruiterModalDropdownOpen, setIsRecruiterModalDropdownOpen] =
    useState(false);
  const [activeRecruiterEditEntryId, setActiveRecruiterEditEntryId] =
    useState(null);
  const [submissionRecruiterOptions, setSubmissionRecruiterOptions] = useState(
    [],
  );
  const [recruiterDirectory, setRecruiterDirectory] = useState([]);
  const [buOptions, setBuOptions] = useState([]);
  const [buDirectory, setBuDirectory] = useState([]);
  const [teamNameOptions, setTeamNameOptions] = useState([]);
  const [teamDirectory, setTeamDirectory] = useState([]);
  const [submissionStatusOptions, setSubmissionStatusOptions] = useState([]);
  const [submissionStatusDirectory, setSubmissionStatusDirectory] = useState(
    [],
  );
  const [submissionHistoryEdits, setSubmissionHistoryEdits] = useState({});
  const [showSubmissionEditors, setShowSubmissionEditors] = useState({});
  const [recentSubmissionChange, setRecentSubmissionChange] = useState({
    candidateId: null,
    entryId: null,
  });
  const [submissionLogPage, setSubmissionLogPage] = useState(1);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [requirementForm, setRequirementForm] = useState([]);
  const [isSavingRequirement, setIsSavingRequirement] = useState(false);
  const [requirementError, setRequirementError] = useState("");
  const [showHotlistDialog, setShowHotlistDialog] = useState(false);
  const [hotlistSelection, setHotlistSelection] = useState("");
  const [shareWithTeam, setShareWithTeam] = useState(true);
  const [hotlistError, setHotlistError] = useState("");
  const [hotlistOptions, setHotlistOptions] = useState([]);
  const [hotlistLoading, setHotlistLoading] = useState(false);
  const [hotlistSubmitting, setHotlistSubmitting] = useState(false);
  const [showMappingImportModal, setShowMappingImportModal] = useState(false);
  const [mappingImportPreview, setMappingImportPreview] = useState(null);
  const [mappingImportLoading, setMappingImportLoading] = useState(false);
  const [mappingImportConfirming, setMappingImportConfirming] = useState(false);
  const [mappingImportError, setMappingImportError] = useState("");
  const [showEmailComposeDialog, setShowEmailComposeDialog] = useState(false);
  const [emailDialogInitialTo, setEmailDialogInitialTo] = useState("");
  const [selectedCandidateIds, setSelectedCandidateIds] = useState(
    () => new Set(),
  );
  const [selectionMode, setSelectionMode] = useState(false);
  const [isSavingExperience, setIsSavingExperience] = useState(false);
  const [experienceError, setExperienceError] = useState("");
  const [experienceItems, setExperienceItems] = useState([]);
  const [experienceLoading, setExperienceLoading] = useState(false);
  const [experienceLoadError, setExperienceLoadError] = useState("");
  const [showAllExperience, setShowAllExperience] = useState(false);
  const [isSavingSubmission, setIsSavingSubmission] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [submissionFieldErrors, setSubmissionFieldErrors] = useState({});
  const [isMovingToBench, setIsMovingToBench] = useState(false);
  const [moveToBenchError, setMoveToBenchError] = useState("");
  const [showRemoveBenchDialog, setShowRemoveBenchDialog] = useState(false);
  const [removeBenchReason, setRemoveBenchReason] = useState("");
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [showCandidateEngagementModal, setShowCandidateEngagementModal] =
    useState(false);
  const [showResponseHistoryDialog, setShowResponseHistoryDialog] =
    useState(false);
  const [responseHistoryTab, setResponseHistoryTab] = useState("response");
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsSaving, setCommentsSaving] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [candidateCommentsList, setCandidateCommentsList] = useState([]);
  const [responseHistoryLoading, setResponseHistoryLoading] = useState(false);
  const [responseHistoryError, setResponseHistoryError] = useState("");
  const [candidateResponseHistoryList, setCandidateResponseHistoryList] =
    useState([]);
  const [benchActivityLoading, setBenchActivityLoading] = useState(false);
  const [benchActivityError, setBenchActivityError] = useState("");
  const [candidateBenchActivityList, setCandidateBenchActivityList] = useState(
    [],
  );
  const [commentDraft, setCommentDraft] = useState("");
  const [searchInputValue, setSearchInputValue] = useState(searchTerm || "");
  const [showListSkeleton, setShowListSkeleton] = useState(() =>
    Boolean(dataLoading),
  );
  const [sidebarTab, setSidebarTab] = useState("overview");
  const [smallNotice, setSmallNotice] = useState("");
  const [candidateListTab, setCandidateListTab] = useState("bench");
  const [benchCurrentPage, setBenchCurrentPage] = useState(1);
  const [nonBenchCandidates, setNonBenchCandidates] = useState([]);
  const [nonBenchLoading, setNonBenchLoading] = useState(false);
  const [nonBenchError, setNonBenchError] = useState("");
  const [candidateDocsLoading, setCandidateDocsLoading] = useState(false);
  const [candidateDocsMenuOpen, setCandidateDocsMenuOpen] = useState(false);
  const [candidateDocsMenuPosition, setCandidateDocsMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 320,
    maxHeight: 360,
  });
  const [candidateDocs, setCandidateDocs] = useState([]);
  const noticeTimerRef = useRef(null);
  const candidateDocsButtonRef = useRef(null);
  const candidateDetailRequestRef = useRef(0);
  const candidateModalRequestRef = useRef(0);
  const submissionDetailsCacheRef = useRef(new Map());

  const commentTemplates = [
    "Shared candidate profile with client; awaiting feedback.",
    "Followed up with client for interview availability.",
    "Updated candidate details based on latest discussion.",
  ];

  const showSmallNotice = useCallback((message) => {
    if (!message) return;
    setSmallNotice(String(message));
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => {
      setSmallNotice("");
      noticeTimerRef.current = null;
    }, 2800);
  }, []);

  const buildEmptySubmissionForm = () => ({
    ...getQuarterDefaults(),
    date: "",
    quarter: "",
    week: "",
  });
  const resetSubmissionValidation = () => {
    setSubmissionError("");
    setSubmissionFieldErrors({});
  };
  const closeSubmissionModal = () => {
    setShowSubmissionModal(false);
    setSubmissionForm(buildEmptySubmissionForm());
    resetSubmissionValidation();
  };
  const openSubmissionModal = () => {
    setSubmissionForm(buildEmptySubmissionForm());
    resetSubmissionValidation();
    setShowSubmissionModal(true);
  };
  const normalizeCandidateId = useCallback(
    (value) => String(toSingleScalar(value || "")).trim(),
    [],
  );
  const clearSubmissionFieldError = (fieldName) => {
    if (!fieldName) return;
    setSubmissionFieldErrors((prev) => {
      if (!prev?.[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };
  const getSubmissionFieldClass = (fieldName, baseClass) =>
    submissionFieldErrors?.[fieldName]
      ? `${baseClass} ${inputErrorCls}`
      : baseClass;

  const commentsApiBaseUrl = useMemo(() => {
    const baseUrl = String(rdApiBaseUrl || RD_API_BASE_URL || "").trim();
    if (!baseUrl) return RD_API_BASE_URL;
    return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  }, [rdApiBaseUrl]);
  const requirementApiBaseUrl = commentsApiBaseUrl;

  useEffect(() => {
    setSearchInputValue(searchTerm || "");
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        typeof setSearchTerm === "function" &&
        searchInputValue !== searchTerm
      )
        setSearchTerm(searchInputValue);
    }, 180);
    return () => clearTimeout(timer);
  }, [searchInputValue, searchTerm, setSearchTerm]);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const message = String(
        event?.reason?.message || event?.reason || "",
      ).trim();
      if (!message.toLowerCase().includes("failed to fetch")) return;
      event.preventDefault();
      showSmallNotice("Network issue. Please try again.");
    };
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
      if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    };
  }, [showSmallNotice]);

  useEffect(() => {
    if (dataLoading) {
      setShowListSkeleton(true);
      return;
    }
    const timer = setTimeout(() => setShowListSkeleton(false), 220);
    return () => clearTimeout(timer);
  }, [dataLoading]);

  const getCandidateCommentsEndpoint = `${commentsApiBaseUrl}api_get_candidate_comments`;
  const getCandidateEngagementEndpoint = `${commentsApiBaseUrl}getCandidateEngagement`;
  const getCandidateBenchActivityEndpoint = `${commentsApiBaseUrl}candidate_bench_activity`;
  const addCandidateCommentsEndpoint = `${commentsApiBaseUrl}addCandidateComments`;
  const addRequirementEndpoint = `${requirementApiBaseUrl}addRequirement`;
  const removeRequirementEndpoint = `${requirementApiBaseUrl}removeRequirement`;
  const moveCandidateToBenchEndpoint = `${commentsApiBaseUrl}moveCandidateToBench`;
  const removeCandidateFromBenchEndpoint = `${commentsApiBaseUrl}removeCandidateFromBench`;
  const selectedCandidateIsOnBench = useMemo(
    () => isCandidateOnBench(selectedRdCandidate),
    [selectedRdCandidate],
  );
  const selectedLinkedinUrl = useMemo(
    () => getLinkedinUrl(selectedRdCandidate),
    [selectedRdCandidate],
  );
  const fetchCandidateById = useCallback(
    async (candidateId, fallbackCandidate = null) => {
      const selectedCandidateId = String(
        toSingleScalar(candidateId) || "",
      ).trim();
      if (!selectedCandidateId) return null;
      const resolveWorkAuthName = (value) => {
        if (!value) return "";
        const key = String(value).trim();
        if (!key || key === "0") return "";
        if (workAuthMap?.[key]) return workAuthMap[key];
        if (/^\d+$/.test(key)) {
          const normalized = String(Number(key));
          if (workAuthMap?.[normalized]) return workAuthMap[normalized];
        }
        const matchKey = Object.keys(workAuthMap || {}).find(
          (mapKey) => workAuthMap?.[mapKey] === key,
        );
        if (matchKey) return workAuthMap[matchKey];
        if (/^\d+$/.test(key)) return "";
        return key;
      };
      const normalizeRdCandidateList = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (payload && Array.isArray(payload.data)) return payload.data;
        if (payload && Array.isArray(payload.records)) return payload.records;
        if (payload && typeof payload === "object") return [payload];
        return [];
      };
      const toSkillList = (value) => {
        if (Array.isArray(value))
          return value
            .map((entry) => String(entry || "").trim())
            .filter(Boolean);
        return String(value || "")
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean);
      };

      try {
        const response = await fetch(
          `${commentsApiBaseUrl}api_get_candidate?candidate_id=${encodeURIComponent(selectedCandidateId)}`,
          { method: "GET" },
        );
        if (!response.ok) return null;

        const result = await response.json().catch(() => null);
        if (!result) return null;
        if (result?.status && result.status !== "success" && !result?.data)
          return null;

        const rows = normalizeRdCandidateList(result?.data ?? result);
        const item =
          rows.find(
            (entry) =>
              String(
                toSingleScalar(
                  entry?.candidate_id ?? entry?.candidateId ?? entry?.id ?? "",
                ),
              ) === selectedCandidateId,
          ) ?? (rows.length === 1 ? rows[0] : null);
        if (!item || typeof item !== "object") return null;

        const fallback = fallbackCandidate || {};
        const resolvedCandidateId = toSingleScalar(
          item?.candidate_id ??
            item?.candidateId ??
            item?.id ??
            fallback?.candidate_id ??
            fallback?.candidateId ??
            fallback?.id ??
            "",
        );
        if (!resolvedCandidateId) return null;
        const resolvedRdId = toSingleScalar(
          item?.rd_id ??
            item?.rdId ??
            item?.user_id ??
            item?.userId ??
            fallback?.rd_id ??
            fallback?.rdId ??
            fallback?.candidateInfo?.rd_id ??
            fallback?.candidateInfo?.rdId ??
            "",
        );
        const workAuthIdRaw =
          item?.work_authorization_id ??
          item?.work_authorization ??
          item?.workAuth ??
          fallback?.workAuthId ??
          fallback?.workAuth ??
          fallback?.candidateInfo?.workAuthorizationId ??
          fallback?.candidateInfo?.workAuthorization ??
          "";
        const workAuthId = toSingleScalar(workAuthIdRaw);
        const workAuthName =
          resolveWorkAuthName(workAuthId) ||
          String(
            item?.work_authorization_name ||
              item?.workAuth ||
              fallback?.workAuth ||
              "",
          ).trim();
        const rawBenchFlag = String(
          item?.is_candidate_on_bench ??
            item?.isCandidateOnBench ??
            fallback?.is_candidate_on_bench ??
            fallback?.isCandidateOnBench ??
            "",
        )
          .trim()
          .toLowerCase();
        const isOnBench =
          ["1", "true", "yes"].includes(rawBenchFlag) ||
          String(item?.status ?? fallback?.status ?? "")
            .trim()
            .toLowerCase() === "on bench";
        const location = buildCandidateLocation(item, fallback);

        // ── resolved once, used everywhere ──────────────────────────────────────
        const resolvedName =
          item?.candidate_name ||
          item?.name ||
          fallback?.name ||
          fallback?.candidateInfo?.candidate_name ||
          fallback?.candidateInfo?.name ||
          "";

        const resolvedLinkedinUrl =
          item?.linkedin_url ||
          fallback?.linkedinUrl ||
          fallback?.linkedin_url ||
          fallback?.candidateInfo?.linkedinUrl ||
          fallback?.candidateInfo?.linkedin_url ||
          "";

        const resolvedExpectedSalary =
          item?.expected_salary ||
          item?.expectedSalary ||
          fallback?.expected_salary ||
          fallback?.expectedSalary ||
          fallback?.contractDetails?.expectedSalary ||
          fallback?.candidateInfo?.expected_salary ||
          fallback?.candidateInfo?.expectedSalary ||
          "";

        const resolvedWorkMode =
          item?.work_mode ||
          item?.workMode ||
          fallback?.work_mode ||
          fallback?.workMode ||
          fallback?.candidateInfo?.work_mode ||
          fallback?.candidateInfo?.workMode ||
          "";

        const resolvedRdComments =
          item?.rd_comments ||
          item?.rdComments ||
          fallback?.rd_comments ||
          fallback?.rdComments ||
          fallback?.candidateInfo?.rd_comments ||
          fallback?.candidateInfo?.rdComments ||
          "";
        // ────────────────────────────────────────────────────────────────────────

        return {
          ...fallback,
          id: resolvedCandidateId,
          candidate_id: resolvedCandidateId,
          candidateId: resolvedCandidateId,
          rd_id: resolvedRdId || fallback?.rd_id || "",
          name: resolvedName,
          title: item?.job_title || item?.title || fallback?.title || "",
          location,
          status: isOnBench
            ? "On Bench"
            : item?.status || fallback?.status || "",
          is_candidate_on_bench: isOnBench ? "1" : "0",
          isCandidateOnBench: isOnBench,
          benchDays:
            item?.bench_days ?? item?.benchDays ?? fallback?.benchDays ?? 0,
          experienceYears:
            item?.experience_years ??
            item?.experienceYears ??
            item?.experience ??
            fallback?.experienceYears ??
            "",
          availabilityStatus:
            item?.availability_status ||
            item?.availabilityStatus ||
            fallback?.availabilityStatus ||
            "",
          availability: item?.availability || fallback?.availability || "",
          candidate_connect:
            item?.candidate_connect ||
            item?.candidateConnect ||
            fallback?.candidate_connect ||
            "",
          connected_via:
            item?.connected_via ||
            item?.candidate_connect_via ||
            item?.connectedVia ||
            item?.candidateConnectVia ||
            fallback?.connected_via ||
            "",
          bench_status_id:
            item?.bench_status_id && String(item?.bench_status_id) !== "0"
              ? String(item.bench_status_id)
              : String(
                  fallback?.bench_status_id || fallback?.benchStatus || "",
                ),
          benchStatus:
            item?.bench_status_id && String(item?.bench_status_id) !== "0"
              ? String(item.bench_status_id)
              : String(
                  fallback?.benchStatus || fallback?.bench_status_id || "",
                ),
          candidate_response:
            item?.candidate_response ||
            item?.candidateResponse ||
            fallback?.candidate_response ||
            fallback?.candidateResponse ||
            "",
          candidateResponse:
            item?.candidate_response ||
            item?.candidateResponse ||
            fallback?.candidateResponse ||
            fallback?.candidate_response ||
            "",
          candidate_comments:
            item?.candidate_comments ||
            item?.candidateComments ||
            item?.comments ||
            fallback?.candidate_comments ||
            fallback?.comments ||
            "",
          comments:
            item?.candidate_comments ||
            item?.candidateComments ||
            item?.comments ||
            fallback?.comments ||
            fallback?.candidate_comments ||
            "",
          candidate_response_datetime:
            item?.candidate_response_datetime ||
            item?.candidateResponseDatetime ||
            fallback?.candidate_response_datetime ||
            null,
          engagement_updated_at:
            item?.engagement_updated_at ||
            item?.engagementUpdatedAt ||
            fallback?.engagement_updated_at ||
            null,
          last_updated_on:
            item?.last_updated_on ||
            item?.lastUpdatedOn ||
            fallback?.last_updated_on ||
            null,
          reporting_updated_at:
            item?.reporting_updated_at ||
            item?.reportingUpdatedAt ||
            fallback?.reporting_updated_at ||
            null,
          project_updated_at:
            item?.project_updated_at ||
            item?.projectUpdatedAt ||
            fallback?.project_updated_at ||
            null,
          client:
            item?.client || item?.previous_client || fallback?.client || "",
          cv_link: item?.cv_link || fallback?.cv_link || "",
          visa_link:
            item?.visa_link || item?.visa_document || fallback?.visa_link || "",
          resumeFileName: item?.cv_link || fallback?.resumeFileName || "",
          resumeFileUrl: item?.cv_link || fallback?.resumeFileUrl || "",
          visaFileName:
            item?.visa_link ||
            item?.visa_document ||
            fallback?.visaFileName ||
            "",
          visaFileUrl:
            item?.visa_link ||
            item?.visa_document ||
            fallback?.visaFileUrl ||
            "",
          linkedinUrl: resolvedLinkedinUrl,
          linkedin_url: resolvedLinkedinUrl,
          expected_salary: resolvedExpectedSalary,
          expectedSalary: resolvedExpectedSalary,
          work_mode: resolvedWorkMode,
          workMode: resolvedWorkMode,
          rd_comments: resolvedRdComments,
          rdComments: resolvedRdComments,
          workAuth: workAuthName || fallback?.workAuth || "",
          workAuthId: workAuthId || fallback?.workAuthId || "",
          locationDetail:
            location || item?.locationDetail || fallback?.locationDetail || "",
          score: item?.score || fallback?.score || 0,
          contractDetails: {
            noticePeriod:
              item?.notice_period ||
              item?.noticePeriod ||
              fallback?.contractDetails?.noticePeriod ||
              "",
            rate:
              item?.pay_rate ||
              item?.rate ||
              fallback?.contractDetails?.rate ||
              "",
            expectedSalary: resolvedExpectedSalary,
            employmentType:
              item?.contractor_status ||
              item?.employmentType ||
              fallback?.contractDetails?.employmentType ||
              "",
          },
          skillGroups: {
            primary: toSkillList(
              item?.primary_skills ||
                item?.skillGroups?.primary ||
                fallback?.skillGroups?.primary,
            ),
            secondary: toSkillList(
              item?.secondary_skills ||
                item?.skillGroups?.secondary ||
                fallback?.skillGroups?.secondary,
            ),
          },
          candidateInfo: {
            ...(fallback?.candidateInfo || {}),
            ...item,
            rd_id: resolvedRdId || fallback?.candidateInfo?.rd_id || "",
            name: resolvedName,
            candidate_name: resolvedName,
            title: item?.job_title || item?.title || fallback?.title || "",
            emailId:
              item?.candidate_email ||
              item?.emailId ||
              item?.email ||
              fallback?.candidateInfo?.emailId ||
              fallback?.email ||
              "",
            phoneNumber:
              item?.candidate_phone ||
              item?.phoneNumber ||
              fallback?.candidateInfo?.phoneNumber ||
              "",
            workAuthorization:
              workAuthName || fallback?.candidateInfo?.workAuthorization || "",
            workAuthorizationId:
              workAuthId || fallback?.candidateInfo?.workAuthorizationId || "",
            is_candidate_on_bench: isOnBench ? "1" : "0",
            isCandidateOnBench: isOnBench,
            previousClient:
              item?.previous_client ||
              item?.client ||
              item?.previousClient ||
              fallback?.client ||
              fallback?.candidateInfo?.previousClient ||
              "",
            experience:
              item?.experience_years ||
              item?.experience ||
              fallback?.experienceYears ||
              "",
            city:
              item?.project_city ||
              item?.city ||
              fallback?.candidateInfo?.city ||
              "",
            state:
              item?.project_state ||
              item?.state ||
              fallback?.candidateInfo?.state ||
              "",
            country:
              item?.project_country ||
              item?.country ||
              fallback?.candidateInfo?.country ||
              "",
            candidate_connect:
              item?.candidate_connect ||
              item?.candidateConnect ||
              fallback?.candidateInfo?.candidate_connect ||
              "",
            connected_via:
              item?.connected_via ||
              item?.candidate_connect_via ||
              item?.connectedVia ||
              item?.candidateConnectVia ||
              fallback?.candidateInfo?.connected_via ||
              "",
            linkedinUrl: resolvedLinkedinUrl,
            linkedin_url: resolvedLinkedinUrl,
            expected_salary: resolvedExpectedSalary,
            expectedSalary: resolvedExpectedSalary,
            work_mode: resolvedWorkMode,
            workMode: resolvedWorkMode,
            rd_comments: resolvedRdComments,
            rdComments: resolvedRdComments,
            bench_status_id:
              item?.bench_status_id && String(item?.bench_status_id) !== "0"
                ? String(item.bench_status_id)
                : String(
                    fallback?.candidateInfo?.bench_status_id ||
                      fallback?.candidateInfo?.benchStatus ||
                      "",
                  ),
            benchStatus:
              item?.bench_status_id && String(item?.bench_status_id) !== "0"
                ? String(item.bench_status_id)
                : String(
                    fallback?.candidateInfo?.benchStatus ||
                      fallback?.candidateInfo?.bench_status_id ||
                      "",
                  ),
            candidate_response:
              item?.candidate_response ||
              item?.candidateResponse ||
              fallback?.candidateInfo?.candidate_response ||
              fallback?.candidateInfo?.candidateResponse ||
              "",
            candidateResponse:
              item?.candidate_response ||
              item?.candidateResponse ||
              fallback?.candidateInfo?.candidateResponse ||
              fallback?.candidateInfo?.candidate_response ||
              "",
            candidate_comments:
              item?.candidate_comments ||
              item?.candidateComments ||
              item?.comments ||
              fallback?.candidateInfo?.candidate_comments ||
              fallback?.candidateInfo?.comments ||
              "",
            comments:
              item?.candidate_comments ||
              item?.candidateComments ||
              item?.comments ||
              fallback?.candidateInfo?.comments ||
              fallback?.candidateInfo?.candidate_comments ||
              "",
            candidate_response_datetime:
              item?.candidate_response_datetime ||
              item?.candidateResponseDatetime ||
              fallback?.candidateInfo?.candidate_response_datetime ||
              null,
            engagement_updated_at:
              item?.engagement_updated_at ||
              item?.engagementUpdatedAt ||
              fallback?.candidateInfo?.engagement_updated_at ||
              null,
            last_updated_on:
              item?.last_updated_on ||
              item?.lastUpdatedOn ||
              fallback?.candidateInfo?.last_updated_on ||
              null,
            reporting_updated_at:
              item?.reporting_updated_at ||
              item?.reportingUpdatedAt ||
              fallback?.candidateInfo?.reporting_updated_at ||
              null,
            project_updated_at:
              item?.project_updated_at ||
              item?.projectUpdatedAt ||
              fallback?.candidateInfo?.project_updated_at ||
              null,
          },
        };
      } catch (_error) {
        return null;
      }
    },
    [commentsApiBaseUrl, workAuthMap],
  );

  const fetchCommentsFromDb = useCallback(
    async (candidateId, rdIdValue) => {
      if (!candidateId) return { success: true, data: [] };
      const rdId = toSingleScalar(rdIdValue);
      try {
        const payload = new URLSearchParams({
          candidate_id: toSingleScalar(candidateId),
          rd_id: rdId || "",
        });
        const response = await fetch(getCandidateCommentsEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        let data = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        if (data?.success === false)
          return {
            success: false,
            error: data?.message || "Unable to load comments.",
          };
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.comments)
            ? data.comments
            : Array.isArray(data)
              ? data
              : [];
        return { success: true, data: list };
      } catch (err) {
        return {
          success: false,
          error: err?.message || "Unable to load comments.",
        };
      }
    },
    [getCandidateCommentsEndpoint],
  );

  const commentRdIdByCandidateId = useMemo(() => {
    const map = new Map();
    const sources = [
      ...(Array.isArray(rdVisibleCandidates) ? rdVisibleCandidates : []),
      selectedRdCandidate,
      rdOverviewCandidate,
    ].filter(Boolean);
    sources.forEach((entry) => {
      const candidateId = toSingleScalar(
        entry?.candidate_id ?? entry?.candidateId ?? entry?.id ?? "",
      );
      const rdId = toSingleScalar(
        entry?.rd_id ??
          entry?.rdId ??
          entry?.candidateInfo?.rd_id ??
          entry?.candidateInfo?.rdId ??
          "",
      );
      if (candidateId && rdId && !map.has(String(candidateId)))
        map.set(String(candidateId), rdId);
    });
    return map;
  }, [rdOverviewCandidate, rdVisibleCandidates, selectedRdCandidate]);

  const resolveCommentRdId = useCallback(
    (candidate) => {
      const source =
        candidate || selectedRdCandidate || rdOverviewCandidate || null;
      return toSingleScalar(
        source?.rd_id ??
          source?.rdId ??
          source?.candidateInfo?.rd_id ??
          source?.candidateInfo?.rdId ??
          "",
      );
    },
    [rdOverviewCandidate, selectedRdCandidate],
  );

  const resolveCommentContext = useCallback(
    async (candidate) => {
      const candidateId = toSingleScalar(
        candidate?.candidate_id ??
          candidate?.candidateId ??
          candidate?.id ??
          null,
      );
      let rdId = resolveCommentRdId(candidate);
      if (!rdId && candidateId)
        rdId = commentRdIdByCandidateId.get(String(candidateId)) || "";
      if (!rdId && candidateId) {
        try {
          const fullCandidate = await fetchCandidateById(
            candidateId,
            candidate,
          );
          rdId = toSingleScalar(
            fullCandidate?.rd_id ??
              fullCandidate?.rdId ??
              fullCandidate?.candidateInfo?.rd_id ??
              fullCandidate?.candidateInfo?.rdId ??
              "",
          );
        } catch (_err) {
          rdId = "";
        }
      }
      return { candidateId, rdId };
    },
    [commentRdIdByCandidateId, fetchCandidateById, resolveCommentRdId],
  );

  const loadCandidateComments = useCallback(
    async (candidate) => {
      const { candidateId, rdId } = await resolveCommentContext(candidate);
      if (!candidateId) {
        setCandidateCommentsList([]);
        return;
      }
      setCommentsLoading(true);
      setCommentsError("");
      const result = await fetchCommentsFromDb(candidateId, rdId);
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        setCandidateCommentsList(
          [...list].sort((a, b) => {
            const aTime = new Date(a?.added_on || a?.added_at || 0).getTime();
            const bTime = new Date(b?.added_on || b?.added_at || 0).getTime();
            return bTime - aTime;
          }),
        );
      } else {
        setCommentsError(result.error || "Failed to load comments.");
        setCandidateCommentsList([]);
      }
      setCommentsLoading(false);
    },
    [fetchCommentsFromDb, resolveCommentContext],
  );

  const fetchCandidateEngagementRowsByCandidateId = useCallback(
    async (candidateIdValue) => {
      const candidateId = toSingleScalar(candidateIdValue);
      if (!candidateId) return [];
      const payload = new URLSearchParams();
      payload.append("candidate_id", String(candidateId));
      const response = await fetch(getCandidateEngagementEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: payload.toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      let result = null;
      try {
        result = await response.json();
      } catch {
        result = null;
      }
      if (!result) throw new Error("Invalid engagement response.");
      const failed =
        result?.success === false ||
        String(result?.status || "").toLowerCase() === "error";
      if (failed)
        throw new Error(
          result?.message || "Unable to load candidate response history.",
        );
      return extractCandidateEngagementRows(result);
    },
    [getCandidateEngagementEndpoint],
  );

  const loadCandidateResponseHistory = useCallback(
    async (candidate) => {
      const candidateId = toSingleScalar(
        candidate?.candidate_id ??
          candidate?.candidateId ??
          candidate?.id ??
          "",
      );
      if (!candidateId) {
        setCandidateResponseHistoryList([]);
        setResponseHistoryError("Candidate id not found.");
        return;
      }
      setResponseHistoryLoading(true);
      setResponseHistoryError("");
      try {
        const rows =
          await fetchCandidateEngagementRowsByCandidateId(candidateId);
        setCandidateResponseHistoryList(Array.isArray(rows) ? rows : []);
      } catch (err) {
        setResponseHistoryError(
          err?.message || "Unable to load candidate response history.",
        );
        setCandidateResponseHistoryList([]);
      } finally {
        setResponseHistoryLoading(false);
      }
    },
    [fetchCandidateEngagementRowsByCandidateId],
  );

  const fetchCandidateBenchActivityRowsByCandidateId = useCallback(
    async (candidateIdValue) => {
      const candidateId = toSingleScalar(candidateIdValue);
      if (!candidateId) return [];
      const payload = new URLSearchParams();
      payload.append("candidate_id", String(candidateId));
      const response = await fetch(getCandidateBenchActivityEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: payload.toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      let result = null;
      try {
        result = await response.json();
      } catch {
        result = null;
      }
      if (!result) throw new Error("Invalid bench activity response.");
      const failed =
        result?.success === false ||
        String(result?.status || "").toLowerCase() === "error";
      if (failed)
        throw new Error(result?.message || "Unable to load bench history.");
      return Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : [];
    },
    [getCandidateBenchActivityEndpoint],
  );

  const loadCandidateBenchActivityHistory = useCallback(
    async (candidate) => {
      const candidateId = toSingleScalar(
        candidate?.candidate_id ??
          candidate?.candidateId ??
          candidate?.id ??
          "",
      );
      if (!candidateId) {
        setCandidateBenchActivityList([]);
        setBenchActivityError("Candidate id not found.");
        return;
      }
      setBenchActivityLoading(true);
      setBenchActivityError("");
      try {
        const rows =
          await fetchCandidateBenchActivityRowsByCandidateId(candidateId);
        const sortedRows = Array.isArray(rows)
          ? [...rows].sort((a, b) => {
              const aTime =
                parseTimestamp(
                  a?.moved_on ?? a?.movedOn ?? a?.created_at ?? a?.createdAt,
                ) ?? 0;
              const bTime =
                parseTimestamp(
                  b?.moved_on ?? b?.movedOn ?? b?.created_at ?? b?.createdAt,
                ) ?? 0;
              return bTime - aTime;
            })
          : [];
        setCandidateBenchActivityList(sortedRows);
      } catch (err) {
        setBenchActivityError(err?.message || "Unable to load bench history.");
        setCandidateBenchActivityList([]);
      } finally {
        setBenchActivityLoading(false);
      }
    },
    [fetchCandidateBenchActivityRowsByCandidateId],
  );

  const saveCandidateComment = useCallback(async () => {
    const text = commentDraft.trim();
    const candidate = selectedRdCandidate;
    const { candidateId, rdId } = await resolveCommentContext(candidate);
    if (!candidateId || !text) return;
    if (!rdId) {
      setCommentsError("Unable to resolve RD id for selected candidate.");
      return;
    }
    const payload = {
      candidate_id: toSingleScalar(candidateId),
      rd_id: toSingleScalar(rdId),
      comment: text,
      added_by: toSingleScalar(
        currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId ?? "",
      ),
    };
    setCommentsSaving(true);
    setCommentsError("");
    try {
      const response = await fetch(addCandidateCommentsEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      let result = null;
      try {
        result = await response.json();
      } catch {
        result = null;
      }
      const saved =
        result?.success === true ||
        String(result?.status || "").toLowerCase() === "success";
      if (!saved && result?.success === false)
        throw new Error(result?.message || "Failed to save comment.");
      setCommentDraft("");
      await loadCandidateComments(candidate);
    } catch (err) {
      setCommentsError(err?.message || "Failed to save comment.");
    } finally {
      setCommentsSaving(false);
    }
  }, [
    addCandidateCommentsEndpoint,
    commentDraft,
    currentUser,
    loadCandidateComments,
    resolveCommentContext,
    selectedRdCandidate,
  ]);

  useEffect(() => {
    if (!showCommentsDialog) return;
    loadCandidateComments(selectedRdCandidate);
  }, [showCommentsDialog, selectedRdCandidate, loadCandidateComments]);
  useEffect(() => {
    if (!showResponseHistoryDialog) return;
    loadCandidateResponseHistory(selectedRdCandidate);
  }, [
    showResponseHistoryDialog,
    selectedRdCandidate,
    loadCandidateResponseHistory,
  ]);
  useEffect(() => {
    if (!showResponseHistoryDialog) return;
    loadCandidateBenchActivityHistory(selectedRdCandidate);
  }, [
    showResponseHistoryDialog,
    selectedRdCandidate,
    loadCandidateBenchActivityHistory,
  ]);

  useEffect(() => {
    if (!showHotlistDialog) return;
    let active = true;
    const loadHotlists = async () => {
      setHotlistLoading(true);
      try {
        const payload = await getHostlistGroupNames();
        const groups = toHotlistGroups(payload);
        if (active) setHotlistOptions(groups);
      } catch (_error) {
        if (active) {
          setHotlistOptions([]);
          setHotlistError("Failed to load hotlists.");
        }
      } finally {
        if (active) setHotlistLoading(false);
      }
    };
    loadHotlists();
    return () => {
      active = false;
    };
  }, [showHotlistDialog]);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const normalizeRecruiterName = (e) => {
      if (!e) return "";
      return String(
        e.name ||
          e.recruiter_name ||
          e.recruiter ||
          e.full_name ||
          e.employee_name ||
          e.employeeName ||
          e.emp_name ||
          "",
      ).trim();
    };
    const normalizeRecruiterPrimaryId = (e) => {
      if (!e) return null;
      return e.id || e.recruiter_id || null;
    };
    const normalizeRecruiterEmployeeId = (e) => {
      if (!e) return null;
      return e.id || null;
    };
    const normalizeRecruiterTeamId = (e) => {
      if (!e) return null;
      return e.team_id || e.teamId || e.team_name_id || null;
    };
    const normalizeRecruiterTeamName = (e) => {
      if (!e) return "";
      const teamValue = String(e.team_name || e.teamName || "").trim();
      return /^\d+$/.test(teamValue) ? "" : teamValue;
    };
    const normalizeRecruiterBuId = (e) => {
      if (!e) return null;
      return e.bu_id || e.buId || null;
    };
    const normalizeRecruiterBuName = (e) => {
      if (!e) return "";
      const buValue = String(
        e.business_unit || e.bu_name || e.buHead || e.bu || "",
      ).trim();
      return /^\d+$/.test(buValue) ? "" : buValue;
    };
    const extractRecruiterRows = (result) => {
      if (!result) return [];
      const candidates = [
        result?.data?.recruiter,
        result?.data?.recruiters,
        result?.data?.recruiter_data,
        result?.recruiter,
        result?.recruiters,
        result?.recruiter_data,
        result?.data,
        result,
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        if (Array.isArray(candidates[i])) return candidates[i];
      }
      return [];
    };
    const applyRecruiterRows = (rawList) => {
      const normalizedDirectory = (Array.isArray(rawList) ? rawList : [])
        .map((entry) => ({
          id: normalizeRecruiterPrimaryId(entry),
          employeeId: normalizeRecruiterEmployeeId(entry),
          name: normalizeRecruiterName(entry),
          teamId: normalizeRecruiterTeamId(entry),
          teamName: normalizeRecruiterTeamName(entry),
          buId: normalizeRecruiterBuId(entry),
          buName: normalizeRecruiterBuName(entry),
        }))
        .filter((entry) => entry.name);
      if (!isActive || normalizedDirectory.length === 0) return false;
      setRecruiterDirectory(normalizedDirectory);
      const recruiterNames = normalizedDirectory
        .map((entry) => entry.name)
        .filter(Boolean);
      setSubmissionRecruiterOptions((prev) => {
        const merged = new Set((prev || []).map((name) => String(name).trim()));
        recruiterNames.forEach((name) => merged.add(String(name).trim()));
        return Array.from(merged).filter(Boolean);
      });
      return true;
    };
    const loadRecruiterDirectory = async () => {
      try {
        const response = await fetch(`${RD_API_BASE_URL}get_recruiter_data`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        applyRecruiterRows(extractRecruiterRows(result));
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    };
    loadRecruiterDirectory();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const loadClientOptions = async () => {
      try {
        const response = await fetch(`${RD_API_BASE_URL}api_getClient`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const rawList = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const names = rawList
          .map((e) =>
            String(
              e.client_name || e.clientName || e.client || e.name || "",
            ).trim(),
          )
          .filter(Boolean);
        if (!isActive || names.length === 0) return;
        if (typeof setSubmissionClientOptions === "function")
          setSubmissionClientOptions((prev) => {
            const merged = new Set(prev || []);
            names.forEach((name) => merged.add(name));
            return Array.from(merged).sort((a, b) => a.localeCompare(b));
          });
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    };
    loadClientOptions();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [setSubmissionClientOptions]);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const loadSubmissionStatuses = async () => {
      try {
        const response = await fetch(
          `${RD_API_BASE_URL}get_submission_status`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const rawList = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const normalizedDirectory = rawList
          .map((entry) => ({
            id:
              entry.id || entry.status_id || entry.submission_status_id || null,
            name: String(
              entry.status_name ||
                entry.status ||
                entry.submission_status ||
                entry.name ||
                entry.label ||
                "",
            ).trim(),
          }))
          .filter((e) => e.name);
        if (!isActive) return;
        setSubmissionStatusDirectory(normalizedDirectory);
        setSubmissionStatusOptions(
          Array.from(new Set(normalizedDirectory.map((e) => e.name))).sort(
            (a, b) => a.localeCompare(b),
          ),
        );
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (isActive) {
          setSubmissionStatusDirectory([]);
          setSubmissionStatusOptions([]);
        }
      }
    };
    loadSubmissionStatuses();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const loadBuOptions = async () => {
      try {
        const response = await fetch(`${RD_API_BASE_URL}api_bu_head`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const rawList = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const normalizedDirectory = rawList
          .map((entry) => ({
            id: entry.id || entry.bu_id || entry.buId || null,
            name: String(
              entry.bu_head ||
                entry.buHead ||
                entry.bu ||
                entry.bu_name ||
                entry.name ||
                "",
            ).trim(),
          }))
          .filter((e) => e.name);
        if (!isActive || normalizedDirectory.length === 0) return;
        setBuDirectory(normalizedDirectory);
        setBuOptions(
          Array.from(new Set(normalizedDirectory.map((e) => e.name))).sort(
            (a, b) => a.localeCompare(b),
          ),
        );
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    };
    loadBuOptions();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();
    const loadTeamNames = async () => {
      try {
        const response = await fetch(`${RD_API_BASE_URL}api_team_name`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const rawList = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : [];
        const normalizedDirectory = rawList
          .map((entry) => ({
            id: entry.id || entry.team_id || entry.teamId || null,
            name: String(entry.team_name || entry.teamName || "").trim(),
          }))
          .filter((e) => e.name && !/^\d+$/.test(e.name));
        if (!isActive || normalizedDirectory.length === 0) return;
        setTeamDirectory(normalizedDirectory);
        setTeamNameOptions(
          Array.from(new Set(normalizedDirectory.map((e) => e.name))).sort(
            (a, b) => a.localeCompare(b),
          ),
        );
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    };
    loadTeamNames();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  const loadExperience = async (candidateId) => {
    const selectedCandidateId = String(
      toSingleScalar(candidateId) || "",
    ).trim();
    if (!selectedCandidateId) return;
    setExperienceLoading(true);
    setExperienceLoadError("");
    try {
      const formData = new FormData();
      formData.append("candidate_id", selectedCandidateId);
      const response = await fetch(GET_EXPERIENCE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: new URLSearchParams(formData).toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result?.status && result.status !== "success")
        throw new Error(result?.message || "Unable to load experience.");
      const items = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : [];
      setExperienceItems(
        items.filter(
          (item) => String(item.candidate_id) === selectedCandidateId,
        ),
      );
    } catch (err) {
      setExperienceLoadError(
        err?.message || "Unable to load experience history.",
      );
      setExperienceItems([]);
    } finally {
      setExperienceLoading(false);
    }
  };

  const selectedRdCandidateId =
    selectedRdCandidate?.candidate_id ??
    selectedRdCandidate?.candidateId ??
    selectedRdCandidate?.id ??
    null;
  useEffect(() => {
    if (!selectedRdCandidateId) {
      setExperienceItems([]);
      setExperienceLoadError("");
      return;
    }
    loadExperience(selectedRdCandidateId);
  }, [selectedRdCandidateId]);
  useEffect(() => {
    setShowAllExperience(false);
  }, [selectedRdCandidateId]);

  const updateCandidateDocsMenuPosition = useCallback(() => {
    if (typeof window === "undefined") return;

    const panelWidth = Math.min(360, Math.max(280, window.innerWidth - 24));
    const anchorRect = candidateDocsButtonRef.current?.getBoundingClientRect();
    const fallbackTop = 96;
    const fallbackLeft = Math.max(12, window.innerWidth - panelWidth - 24);
    const top = anchorRect ? anchorRect.bottom + 8 : fallbackTop;
    const left = anchorRect
      ? Math.min(
          Math.max(12, anchorRect.right - panelWidth),
          window.innerWidth - panelWidth - 12,
        )
      : fallbackLeft;
    const availableBelow = window.innerHeight - top - 16;

    setCandidateDocsMenuPosition({
      top,
      left,
      width: panelWidth,
      maxHeight: Math.max(240, Math.min(420, availableBelow)),
    });
  }, []);

  const loadCandidateDocuments = async () => {
    const candidateId = String(
      toSingleScalar(selectedRdCandidateId) || "",
    ).trim();
    if (!candidateId || candidateDocsLoading) return;

    setCandidateDocsLoading(true);
    try {
      const baseUrl = String(rdApiBaseUrl || RD_API_BASE_URL || "").trim();
      const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
      const response = await fetch(
        `${normalizedBaseUrl}uploaded_docs/${encodeURIComponent(candidateId)}`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      const docs = (
        Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
            ? result
            : []
      )
        .filter((doc) => String(doc?.is_active ?? "1") === "1")
        .filter((doc) => buildUploadedDocumentUrl(doc?.file_url, rdApiBaseUrl))
        .sort((a, b) => {
          const order = { RESUME: 0, VISA: 1 };
          const aType = String(a?.document_type || "")
            .trim()
            .toUpperCase();
          const bType = String(b?.document_type || "")
            .trim()
            .toUpperCase();
          return (order[aType] ?? 9) - (order[bType] ?? 9);
        });
      setCandidateDocs(docs);
      updateCandidateDocsMenuPosition();
      setCandidateDocsMenuOpen(true);
      if (docs.length === 0) throw new Error("No uploaded documents found.");
    } catch (err) {
      setCandidateDocs([]);
      setCandidateDocsMenuOpen(false);
      showSmallNotice(err?.message || "Unable to load uploaded documents.");
    } finally {
      setCandidateDocsLoading(false);
    }
  };

  useEffect(() => {
    setCandidateDocs([]);
    setCandidateDocsMenuOpen(false);
  }, [selectedRdCandidateId]);

  useEffect(() => {
    if (!candidateDocsMenuOpen) return undefined;
    updateCandidateDocsMenuPosition();
    window.addEventListener("resize", updateCandidateDocsMenuPosition);
    window.addEventListener("scroll", updateCandidateDocsMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateCandidateDocsMenuPosition);
      window.removeEventListener("scroll", updateCandidateDocsMenuPosition, true);
    };
  }, [candidateDocsMenuOpen, updateCandidateDocsMenuPosition]);

  const normalizeDateInputValue = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  };

  const resetExperienceForm = useCallback(() => {
    setNewExperience({
      role: "",
      company: "",
      period: "",
      status: "Active",
      completedOn: "",
      summary: "",
      tags: "",
      update_key: "",
    });
  }, [setNewExperience]);

  const closeExperienceModal = useCallback(() => {
    setShowExperienceModal(false);
    setExperienceError("");
    resetExperienceForm();
  }, [resetExperienceForm, setShowExperienceModal]);

  const openAddExperienceModal = useCallback(() => {
    setExperienceError("");
    resetExperienceForm();
    setShowExperienceModal(true);
  }, [resetExperienceForm, setShowExperienceModal]);

  const openEditExperienceModal = useCallback(
    (item) => {
      if (!item) return;
      const fromDate = normalizeDateInputValue(
        item.from_date || item.period || "",
      );
      const toDate = normalizeDateInputValue(
        item.to_date || item.completed_on || item.completedOn || "",
      );
      const normalizedStatusRaw = String(item.job_status || "")
        .trim()
        .toLowerCase();
      const status =
        normalizedStatusRaw === "completed" || toDate ? "Completed" : "Active";
      const updateKey = String(item.exp_id || "").trim();

      setExperienceError("");
      setNewExperience({
        role: String(item.role || "").trim(),
        company: String(item.client || item.company || "").trim(),
        period: fromDate,
        status,
        completedOn: status === "Completed" ? toDate : "",
        summary: String(item.summary || "").trim(),
        tags: String(item.tags || "").trim(),
        update_key: updateKey,
      });
      setShowExperienceModal(true);
    },
    [setNewExperience, setShowExperienceModal],
  );

  const closeCandidateInfoModal = useCallback(() => {
    candidateModalRequestRef.current += 1;
    setShowCandidateInfoModal(false);
    setCandidateInfoTarget(null);
  }, []);

  const openCandidateInfoModal = async (candidate = null) => {
    const target = candidate || null;
    const targetId = normalizeCandidateId(
      target?.candidate_id ?? target?.candidateId ?? target?.id ?? null,
    );
    const requestId = candidateModalRequestRef.current + 1;
    candidateModalRequestRef.current = requestId;
    let nextTarget = target;
    if (targetId) {
      try {
        const latest = await fetchCandidateById(targetId, target);
        if (candidateModalRequestRef.current !== requestId) return;
        if (latest) nextTarget = latest;
      } catch (error) {
        if (candidateModalRequestRef.current !== requestId) return;
        showSmallNotice(error?.message || "Failed to fetch candidate details.");
      }
    }
    if (candidateModalRequestRef.current !== requestId) return;
    setCandidateInfoTarget(nextTarget);
    setShowCandidateInfoModal(true);
  };

  const openHotlistDialog = (event) => {
    event?.stopPropagation?.();
    setHotlistSelection("");
    setHotlistError("");
    setShareWithTeam(true);
    setShowHotlistDialog(true);
  };

  const openMappingImportModal = useCallback(() => {
    setMappingImportPreview(null);
    setMappingImportError("");
    setShowMappingImportModal(true);
  }, []);

  const downloadMappingTemplate = useCallback(() => {
    const headers = ["Emp", "BU", "Recruiters name", "Role", "Team Name"];
    const sampleRows = [
      ["10269", "Oliver", "Celestine S", "Delivery Account Lead", "Felix"],
      ["11631", "Rohit", "Venkatesan Sudharsanam", "Team Lead - I", "Venkatesan"],
    ];
    const csv = [headers, ...sampleRows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "rd_mapping_import_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const refreshMappingDirectories = useCallback(async () => {
    const readRows = (result, keys = []) => {
      for (const key of keys) {
        const value = key
          .split(".")
          .reduce((current, part) => current?.[part], result);
        if (Array.isArray(value)) return value;
      }
      if (Array.isArray(result?.data)) return result.data;
      if (Array.isArray(result)) return result;
      return [];
    };

    const [buResult, teamResult, recruiterResult] = await Promise.all([
      fetch(`${RD_API_BASE_URL}api_bu_head`).then((response) => {
        if (!response.ok) throw new Error(`BU refresh failed: ${response.status}`);
        return response.json();
      }),
      fetch(`${RD_API_BASE_URL}api_team_name`).then((response) => {
        if (!response.ok)
          throw new Error(`Team refresh failed: ${response.status}`);
        return response.json();
      }),
      fetch(`${RD_API_BASE_URL}get_recruiter_data`).then((response) => {
        if (!response.ok)
          throw new Error(`Recruiter refresh failed: ${response.status}`);
        return response.json();
      }),
    ]);

    const nextBuDirectory = readRows(buResult).map((entry) => ({
      id: entry.id || entry.bu_id || entry.buId || null,
      name: String(
        entry.bu_head ||
          entry.buHead ||
          entry.bu ||
          entry.bu_name ||
          entry.name ||
          "",
      ).trim(),
    })).filter((entry) => entry.name);

    const nextTeamDirectory = readRows(teamResult).map((entry) => ({
      id: entry.id || entry.team_id || entry.teamId || null,
      name: String(entry.team_name || entry.teamName || "").trim(),
      buId: entry.bu_id || entry.buId || null,
    })).filter((entry) => entry.name && !/^\d+$/.test(entry.name));

    const nextRecruiterDirectory = readRows(recruiterResult, [
      "data.recruiter",
      "data.recruiters",
      "data.recruiter_data",
      "recruiter",
      "recruiters",
      "recruiter_data",
    ]).map((entry) => ({
      id: entry.id || entry.recruiter_id || null,
      employeeId: entry.employee_id || entry.emp_id || null,
      name: String(
        entry.name ||
          entry.recruiter_name ||
          entry.recruiter ||
          entry.full_name ||
          entry.employee_name ||
          entry.employeeName ||
          entry.emp_name ||
          "",
      ).trim(),
      teamId: entry.team_id || entry.teamId || entry.team_name_id || null,
      teamName: /^\d+$/.test(String(entry.team_name || entry.teamName || "").trim())
        ? ""
        : String(entry.team_name || entry.teamName || "").trim(),
      buId: entry.bu_id || entry.buId || null,
      buName: /^\d+$/.test(
        String(entry.business_unit || entry.bu_name || entry.buHead || entry.bu || "").trim(),
      )
        ? ""
        : String(entry.business_unit || entry.bu_name || entry.buHead || entry.bu || "").trim(),
    })).filter((entry) => entry.name);

    if (nextBuDirectory.length > 0) {
      setBuDirectory(nextBuDirectory);
      setBuOptions(
        Array.from(new Set(nextBuDirectory.map((entry) => entry.name))).sort(
          (a, b) => a.localeCompare(b),
        ),
      );
    }
    if (nextTeamDirectory.length > 0) {
      setTeamDirectory(nextTeamDirectory);
      setTeamNameOptions(
        Array.from(new Set(nextTeamDirectory.map((entry) => entry.name))).sort(
          (a, b) => a.localeCompare(b),
        ),
      );
    }
    if (nextRecruiterDirectory.length > 0) {
      setRecruiterDirectory(nextRecruiterDirectory);
      setSubmissionRecruiterOptions(
        Array.from(
          new Set(nextRecruiterDirectory.map((entry) => entry.name).filter(Boolean)),
        ),
      );
    }
  }, []);

  const handleMappingImportPreview = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setMappingImportLoading(true);
    setMappingImportError("");
    setMappingImportPreview(null);
    try {
      const response = await fetch(`${RD_API_BASE_URL}preview_mapping_import`, {
        method: "POST",
        body: formData,
      });
      const text = await response.text();
      const result = text ? JSON.parse(text) : {};
      if (!response.ok || result?.success === false || result?.status === "error") {
        throw new Error(result?.message || `Preview failed: HTTP ${response.status}`);
      }
      setMappingImportPreview(result);
    } catch (error) {
      const message = error?.message || "Failed to preview mapping import.";
      setMappingImportError(message);
      showSmallNotice(message);
    } finally {
      setMappingImportLoading(false);
    }
  };

  const handleMappingImportConfirm = async () => {
    if (!mappingImportPreview || mappingImportConfirming) return;
    setMappingImportConfirming(true);
    setMappingImportError("");
    try {
      const response = await fetch(`${RD_API_BASE_URL}confirm_mapping_import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          preview_id:
            mappingImportPreview.preview_id ||
            mappingImportPreview?.data?.preview_id ||
            null,
          preview: mappingImportPreview,
        }),
      });
      const text = await response.text();
      const result = text ? JSON.parse(text) : {};
      if (!response.ok || result?.success === false || result?.status === "error") {
        throw new Error(result?.message || `Import failed: HTTP ${response.status}`);
      }
      await refreshMappingDirectories();
      setShowMappingImportModal(false);
      setMappingImportPreview(null);
      showSmallNotice(result?.message || "RD mapping import completed.");
    } catch (error) {
      const message = error?.message || "Failed to confirm mapping import.";
      setMappingImportError(message);
      showSmallNotice(message);
    } finally {
      setMappingImportConfirming(false);
    }
  };

  const handleHotlistSubmit = async () => {
    if (!hotlistSelection) {
      setHotlistError("Select a hotlist.");
      return;
    }
    if (hotlistSubmitting) return;
    setHotlistSubmitting(true);
    try {
      const selectedCandidates = activeVisibleCandidates.filter((c) =>
        selectedCandidateIds.has(c.id),
      );
      const result = await Promise.resolve(
        onAddToHotlist?.({
          candidate: selectedRdCandidate,
          candidates: selectedCandidates,
          hotlistName: hotlistSelection,
          selectedHotlist: hotlistSelection,
          newHotlistName: "",
          shouldCreateGroup: false,
          shareWithTeam,
        }),
      );
      if (result && result.ok === false) {
        throw new Error(result.message || "Failed to add to hotlist.");
      }
      setShowHotlistDialog(false);
    } catch (error) {
      const message = error?.message || "Failed to add to hotlist.";
      setHotlistError(message);
      showSmallNotice(message);
    } finally {
      setHotlistSubmitting(false);
    }
  };

  const selectedCount = selectedCandidateIds.size;
  const benchPageSize = 10;
  const filterCandidatesBySearch = useCallback((candidates) => {
    const baseCandidates = Array.isArray(candidates) ? candidates : [];
    const query = String(searchTerm || "").trim();
    if (!query) return baseCandidates;
    const hasBooleanOperators = /\b(AND|OR|NOT)\b|[()]/i.test(query);
    return baseCandidates.filter((candidate) => {
      const toSkillList = (value) => {
        if (Array.isArray(value)) {
          return value.map((item) => String(item || "").trim()).filter(Boolean);
        }
        return String(value || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      };
      const haystack = [
        candidate?.name,
        candidate?.title,
        candidate?.headline,
        candidate?.status,
        candidate?.location,
        candidate?.client,
        candidate?.availability,
        candidate?.availabilityStatus,
        candidate?.experienceYears,
        ...toSkillList(candidate?.skills),
        ...toSkillList(candidate?.primarySkills),
        ...toSkillList(candidate?.secondarySkills),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (hasBooleanOperators) return evaluateBooleanSearch(query, haystack);
      const terms = tokenizeSimpleSearchTerms(query);
      return terms.every((term) => haystack.includes(term.toLowerCase()));
    });
  }, [searchTerm]);
  const benchVisibleCandidates = useMemo(() => {
    const baseCandidates = Array.isArray(rdVisibleCandidates)
      ? rdVisibleCandidates.filter((candidate) => isCandidateOnBench(candidate))
      : [];
    return filterCandidatesBySearch(baseCandidates);
  }, [filterCandidatesBySearch, rdVisibleCandidates]);
  const nonBenchVisibleCandidates = useMemo(
    () => filterCandidatesBySearch(nonBenchCandidates),
    [filterCandidatesBySearch, nonBenchCandidates],
  );
  const activeVisibleCandidates =
    candidateListTab === "nonBench"
      ? nonBenchVisibleCandidates
      : benchVisibleCandidates;
  const benchCandidatesCount = benchVisibleCandidates.length;
  const nonBenchCandidatesCount = nonBenchVisibleCandidates.length;
  const activeCandidatesCount = activeVisibleCandidates.length;
  const hasActiveCandidates = activeCandidatesCount > 0;
  const activeListLoading =
    candidateListTab === "nonBench" ? nonBenchLoading : showListSkeleton;
  const activeListError =
    candidateListTab === "nonBench" ? nonBenchError : "";
  const getCandidatePrimaryEmail = useCallback((candidate) => {
    const info = candidate?.candidateInfo || {};
    return toSingleScalar(
      candidate?.email ||
        candidate?.emailId ||
        info?.emailId ||
        info?.email ||
        "",
    );
  }, []);
  const selectedCandidatePrimaryEmail = useMemo(
    () => getCandidatePrimaryEmail(selectedRdCandidate),
    [getCandidatePrimaryEmail, selectedRdCandidate],
  );
  const selectedBulkEmailRecipients = useMemo(() => {
    const recipients = activeVisibleCandidates
      .filter((candidate) => selectedCandidateIds.has(candidate.id))
      .map((candidate) => getCandidatePrimaryEmail(candidate))
      .filter(Boolean);
    return Array.from(new Set(recipients));
  }, [activeVisibleCandidates, getCandidatePrimaryEmail, selectedCandidateIds]);

  const normalizeNonBenchCandidateList = useCallback((payload) => {
    const rows = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.records)
        ? payload.records
        : Array.isArray(payload)
          ? payload
          : [];
    const toSkillList = (value) => {
      if (Array.isArray(value)) {
        return value.map((item) => String(item || "").trim()).filter(Boolean);
      }
      return String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    };

    return rows
      .map((item, index) => {
        if (!item || typeof item !== "object") return null;
        const candidateId = toSingleScalar(
          item?.candidate_id ?? item?.candidateId ?? item?.id ?? "",
        );
        if (!candidateId) return null;
        const name =
          item?.candidate_name ||
          item?.name ||
          item?.candidateInfo?.candidate_name ||
          item?.candidateInfo?.name ||
          "Unnamed Candidate";
        const title = item?.job_title || item?.title || "";
        const location = buildCandidateLocation(item, item);
        const primarySkills = toSkillList(
          item?.primary_skills || item?.skillGroups?.primary,
        );
        const secondarySkills = toSkillList(
          item?.secondary_skills || item?.skillGroups?.secondary,
        );

        return {
          ...item,
          id: String(candidateId),
          candidate_id: String(candidateId),
          candidateId: String(candidateId),
          name,
          title,
          status: item?.status || "Non Bench",
          is_candidate_on_bench: "0",
          isCandidateOnBench: false,
          location,
          client: item?.client || item?.previous_client || "",
          availability:
            item?.availability || item?.availability_status || "Available",
          availabilityStatus:
            item?.availability_status || item?.availabilityStatus || "",
          experienceYears:
            item?.experience_years ??
            item?.experienceYears ??
            item?.experience ??
            "",
          candidate_response:
            item?.candidate_response || item?.candidateResponse || "",
          candidateResponse:
            item?.candidateResponse || item?.candidate_response || "",
          candidate_response_datetime:
            item?.candidate_response_datetime ||
            item?.candidateResponseDatetime ||
            null,
          engagement_updated_at:
            item?.engagement_updated_at ||
            item?.engagementUpdatedAt ||
            null,
          last_updated_on: item?.last_updated_on || item?.lastUpdatedOn || null,
          linkedinUrl: item?.linkedin_url || item?.linkedinUrl || "",
          linkedin_url: item?.linkedin_url || item?.linkedinUrl || "",
          skills: [...primarySkills, ...secondarySkills],
          primarySkills,
          secondarySkills,
          skillGroups: {
            primary: primarySkills,
            secondary: secondarySkills,
          },
          candidateInfo: {
            ...item,
            name,
            candidate_name: name,
            title,
            is_candidate_on_bench: "0",
            isCandidateOnBench: false,
            emailId: item?.candidate_email || item?.emailId || item?.email || "",
            phoneNumber:
              item?.candidate_phone || item?.phoneNumber || item?.phone || "",
          },
          _listIndex: index,
        };
      })
      .filter(Boolean);
  }, []);

  const fetchNonBenchCandidates = useCallback(async () => {
    setNonBenchLoading(true);
    setNonBenchError("");
    try {
      const response = await fetch(`${commentsApiBaseUrl}non_bench_candidates`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json().catch(() => null);
      setNonBenchCandidates(normalizeNonBenchCandidateList(result?.data ?? result));
    } catch (error) {
      const message = error?.message || "Unable to load non-bench candidates.";
      setNonBenchCandidates([]);
      setNonBenchError(message);
      showSmallNotice(message);
    } finally {
      setNonBenchLoading(false);
    }
  }, [commentsApiBaseUrl, normalizeNonBenchCandidateList, showSmallNotice]);

  useEffect(() => {
    fetchNonBenchCandidates();
  }, [fetchNonBenchCandidates]);
  const openEmailComposeDialog = useCallback(
    (event, explicitRecipients = "") => {
      event?.stopPropagation?.();
      const mergedRecipients = [
        ...splitRecipients(explicitRecipients),
        ...selectedBulkEmailRecipients,
        ...splitRecipients(selectedCandidatePrimaryEmail),
      ];
      const uniqueRecipients = Array.from(
        new Set(
          mergedRecipients.map((item) => String(item).trim()).filter(Boolean),
        ),
      );
      setEmailDialogInitialTo(uniqueRecipients.join(", "));
      setShowEmailComposeDialog(true);
    },
    [selectedBulkEmailRecipients, selectedCandidatePrimaryEmail],
  );
  const rdJobTitleSuggestions = useMemo(() => {
    const titles = (
      Array.isArray(rdVisibleCandidates) ? rdVisibleCandidates : []
    )
      .map((candidate) =>
        String(
          candidate?.title ||
            candidate?.headline ||
            candidate?.candidateInfo?.title ||
            candidate?.job_title ||
            "",
        ).trim(),
      )
      .filter(Boolean);
    return Array.from(new Set(titles)).sort((a, b) => a.localeCompare(b));
  }, [rdVisibleCandidates]);
  const rdLocationSuggestions = useMemo(() => {
    const items = (Array.isArray(rdLocationOptions) ? rdLocationOptions : [])
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((item) => item.toLowerCase() !== "all");
    return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
  }, [rdLocationOptions]);
  const rdExperienceSuggestions = useMemo(() => {
    const items = (
      Array.isArray(rdExperienceOptions) ? rdExperienceOptions : []
    )
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((item) => item.toLowerCase() !== "all");
    return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
  }, [rdExperienceOptions]);
  const rdSkillSuggestions = useMemo(() => {
    const toSkillList = (value) => {
      if (Array.isArray(value)) {
        return value.map((item) => String(item || "").trim()).filter(Boolean);
      }
      return String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    };

    const skills = [];
    (Array.isArray(rdVisibleCandidates) ? rdVisibleCandidates : []).forEach(
      (candidate) => {
        skills.push(
          ...toSkillList(candidate?.skillGroups?.primary),
          ...toSkillList(candidate?.skillGroups?.secondary),
          ...toSkillList(
            candidate?.candidateInfo?.primarySkills ??
              candidate?.candidateInfo?.primary_skills,
          ),
          ...toSkillList(
            candidate?.candidateInfo?.secondarySkills ??
              candidate?.candidateInfo?.secondary_skills,
          ),
          ...toSkillList(candidate?.primarySkills ?? candidate?.primary_skills),
          ...toSkillList(
            candidate?.secondarySkills ?? candidate?.secondary_skills,
          ),
          ...toSkillList(candidate?.skills),
        );
      },
    );

    return Array.from(
      new Set(skills.map((item) => String(item).trim()).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
  }, [rdVisibleCandidates]);
  const rdEmploymentTypeSuggestions = useMemo(() => {
    const items = (
      Array.isArray(rdVisibleCandidates) ? rdVisibleCandidates : []
    )
      .map((candidate) =>
        String(
          candidate?.contractDetails?.employmentType ||
            candidate?.employmentType ||
            candidate?.candidateInfo?.employmentType ||
            candidate?.candidateInfo?.contractor_status ||
            candidate?.contractor_status ||
            "",
        ).trim(),
      )
      .filter(Boolean);
    return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
  }, [rdVisibleCandidates]);
  const lastConnectDaysByCandidateId = useMemo(() => {
    const next = {};
    (Array.isArray(activeVisibleCandidates)
      ? activeVisibleCandidates
      : []
    ).forEach((candidate) => {
      const candidateId = toSingleScalar(
        candidate?.candidate_id ??
          candidate?.candidateId ??
          candidate?.id ??
          "",
      );
      if (!candidateId) return;
      const latestTimestamp = getLatestCandidateResponseTimestamp([
        candidate,
        candidate?.candidateInfo,
      ]);
      const days =
        latestTimestamp === null
          ? null
          : Math.max(0, Math.ceil((Date.now() - latestTimestamp) / ONE_DAY_MS));
      next[String(candidateId)] = days;
    });
    return next;
  }, [activeVisibleCandidates]);
  const benchTotalPages = useMemo(
    () => Math.max(1, Math.ceil(activeVisibleCandidates.length / benchPageSize)),
    [activeVisibleCandidates.length],
  );
  const paginatedBenchCandidates = useMemo(() => {
    const safePage = Math.min(Math.max(benchCurrentPage, 1), benchTotalPages);
    const start = (safePage - 1) * benchPageSize;
    return activeVisibleCandidates.slice(start, start + benchPageSize);
  }, [activeVisibleCandidates, benchCurrentPage, benchTotalPages]);
  useEffect(() => {
    const allowed = new Set(
      activeVisibleCandidates.map((candidate) => candidate.id),
    );
    setSelectedCandidateIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [activeVisibleCandidates]);
  useEffect(() => {
    setBenchCurrentPage((prev) => Math.min(Math.max(1, prev), benchTotalPages));
  }, [benchTotalPages]);
  const handleBenchPageChange = useCallback(
    async (nextPage) => {
      const safePage = Math.min(Math.max(1, nextPage), benchTotalPages);
      if (safePage === benchCurrentPage) return;
      setBenchCurrentPage(safePage);
      if (candidateListTab === "bench") {
        try {
          await Promise.resolve(onBenchUpdated?.());
        } catch (error) {
          showSmallNotice(
            error?.message || "Unable to refresh bench candidates.",
          );
        }
      }
    },
    [
      benchCurrentPage,
      benchTotalPages,
      candidateListTab,
      onBenchUpdated,
      showSmallNotice,
    ],
  );
  const toggleCandidateSelection = (event, candidateId) => {
    event.stopPropagation();
    setSelectedCandidateIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  };
  const selectAllCandidates = () =>
    setSelectedCandidateIds(new Set(activeVisibleCandidates.map((c) => c.id)));
  const clearSelectedCandidates = () => setSelectedCandidateIds(new Set());

  const parseSkillList = useCallback((value) => {
    if (!value) return [];
    if (Array.isArray(value))
      return value.map((item) => String(item).trim()).filter(Boolean);
    const text = String(value).trim();
    if (!text) return [];
    if (text.startsWith("[") && text.endsWith("]")) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed))
          return parsed.map((item) => String(item).trim()).filter(Boolean);
      } catch (_error) {}
    }
    return text
      .split(/[,;\n|/]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }, []);
  const dedupeSkills = useCallback(
    (items) =>
      Array.from(
        new Set(
          (items || [])
            .map((item) => String(item || "").trim())
            .filter(Boolean),
        ),
      ),
    [],
  );
  const getCandidateSkillGroups = useCallback(
    (candidate) => {
      if (!candidate) return { primary: [], secondary: [] };
      const primary = dedupeSkills([
        ...parseSkillList(candidate?.skillGroups?.primary),
        ...parseSkillList(candidate?.candidateInfo?.skillGroups?.primary),
        ...parseSkillList(
          candidate?.candidateInfo?.primarySkills ??
            candidate?.candidateInfo?.primary_skills,
        ),
        ...parseSkillList(candidate?.candidateInfo?.primary_skill),
        ...parseSkillList(
          candidate?.primarySkills ??
            candidate?.primary_skills ??
            candidate?.primary_skill,
        ),
      ]);
      const secondary = dedupeSkills([
        ...parseSkillList(candidate?.skillGroups?.secondary),
        ...parseSkillList(candidate?.candidateInfo?.skillGroups?.secondary),
        ...parseSkillList(
          candidate?.candidateInfo?.secondarySkills ??
            candidate?.candidateInfo?.secondary_skills,
        ),
        ...parseSkillList(candidate?.candidateInfo?.secondary_skill),
        ...parseSkillList(
          candidate?.secondarySkills ??
            candidate?.secondary_skills ??
            candidate?.secondary_skill,
        ),
      ]).filter((skill) => !primary.includes(skill));
      if (primary.length > 0 || secondary.length > 0)
        return { primary, secondary };
      return {
        primary: dedupeSkills(parseSkillList(candidate?.skills)),
        secondary: [],
      };
    },
    [dedupeSkills, parseSkillList],
  );
  const overviewSkillGroups = useMemo(
    () => getCandidateSkillGroups(rdOverviewCandidate),
    [getCandidateSkillGroups, rdOverviewCandidate],
  );
  const selectedSkillGroups = useMemo(
    () => getCandidateSkillGroups(selectedRdCandidate),
    [getCandidateSkillGroups, selectedRdCandidate],
  );
  const sortedExperienceItems = useMemo(() => {
    const toTimestamp = (value) => {
      const text = String(value || "").trim();
      if (!text) return 0;
      const parsed = Date.parse(text);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const items = Array.isArray(experienceItems) ? [...experienceItems] : [];
    const activeItems = [];
    const completedItems = [];

    items.forEach((item) => {
      const fromDate = normalizeDateInputValue(
        item?.from_date || item?.period || "",
      );
      const toDate = normalizeDateInputValue(
        item?.to_date || item?.completed_on || item?.completedOn || "",
      );
      const status = String(item?.job_status || "")
        .trim()
        .toLowerCase();
      const isCompleted = status === "completed" || Boolean(toDate);
      const withMeta = {
        item,
        fromTs: toTimestamp(fromDate),
        toTs: toTimestamp(toDate),
      };
      if (isCompleted) completedItems.push(withMeta);
      else activeItems.push(withMeta);
    });

    activeItems.sort((a, b) => b.fromTs - a.fromTs);
    completedItems.sort((a, b) => (b.toTs || b.fromTs) - (a.toTs || a.fromTs));

    return [...activeItems, ...completedItems].map((entry) => entry.item);
  }, [experienceItems]);
  const visibleExperienceItems = useMemo(
    () =>
      showAllExperience
        ? sortedExperienceItems
        : sortedExperienceItems.slice(0, 2),
    [showAllExperience, sortedExperienceItems],
  );

  const handleCreateCandidateFromInfo = async ({ info }) => {
    if (!info || typeof setRdCandidates !== "function") return false;
    const normalizeText = (value) =>
      value ? toTitleCase(String(value).trim().replace(/\s+/g, " ")) : "";
    const normalizeSkill = (value) =>
      value === value.toUpperCase() ? value : toTitleCase(value);
    const normalizeWorkMode = Array.isArray(info?.workMode)
      ? info.workMode.filter(Boolean).join(", ")
      : typeof info?.workMode === "string"
        ? info.workMode
        : "Onsite";
    try {
      const userSession = JSON.parse(localStorage.getItem("user") || "null");
      const baseUrl = String(rdApiBaseUrl || RD_API_BASE_URL || "").trim();
      const RD_API_URL = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
      const createEndpoint = `${RD_API_URL}save_candidate`;
      const phoneCountryCode = String(info?.phoneCountryCode || "").trim();
      const phoneNumber = String(info?.phoneNumber || "").trim();
      const secondaryPhoneCountryCode = String(
        info?.secondaryPhoneCountryCode || "",
      ).trim();
      const secondaryPhoneNumber = String(
        info?.secondaryPhoneNumber || "",
      ).trim();
      const formatPhoneWithCode = (code, number) => {
        const c = String(code || "").trim();
        const n = String(number || "").trim();
        if (!c && !n) return " ";
        if (!c) return n;
        if (!n) return `${c} `;
        return `${c} ${n}`;
      };
      const primaryPhoneValue = formatPhoneWithCode(
        phoneCountryCode,
        phoneNumber,
      );
      const secondaryPhoneValue = formatPhoneWithCode(
        secondaryPhoneCountryCode,
        secondaryPhoneNumber,
      );
      const payload = {
        name: info?.name || "",
        title: info?.title || "",
        emailId: info?.emailId || "",
        candidate_email_secondary: info?.secondaryEmailId || "",
        linkedinUrl: info?.linkedinUrl || "",
        phoneNumber: primaryPhoneValue,
        secondaryPhoneNumber: secondaryPhoneValue,
        workedWithVdart: info?.workedWithVdart || "No",
        referredBy: info?.referredById || "",
        previousClient: info?.previousClient || "",
        city: info?.city || "",
        state: info?.state || "",
        country: info?.country || "",
        experience: info?.experience || "",
        timeZonePrimary: info?.timeZonePrimary || "",
        timeZoneSecondary: info?.timeZoneSecondary || "",
        relocation: info?.relocation || "No",
        workMode: normalizeWorkMode,
        expectedSalary: info?.expectedSalary || "",
        workAuthorization: info?.workAuthorization || "",
        candidateType: info?.candidateType || "",
        candidate_added_source: info?.candidate_added_source || "",
        benchStatus: info?.benchStatus || "",
        comments: info?.comments || "",
        primary_skills: info?.primarySkills || "",
        secondary_skills: info?.secondarySkills || "",
        created_by: info?.created_by ?? userSession?.id ?? null,
        updatedBy: userSession?.id || null,
      };
      const formData = new FormData();
      const visaFiles = Array.isArray(info?.visaFiles)
        ? info.visaFiles.filter((file) => file instanceof File)
        : info?.visaFile instanceof File
          ? [info.visaFile]
          : [];
      formData.append("input", JSON.stringify(payload));
      if (info?.resumeFile instanceof File)
        formData.append("resumeFile", info.resumeFile);
      visaFiles.forEach((file) => formData.append("visaFile[]", file));
      const response = await fetch(createEndpoint, {
        method: "POST",
        body: formData,
      });
      const responseText = await response.text();
      if (!response.ok)
        throw new Error(
          `HTTP ${response.status}${responseText ? ` - ${responseText}` : ""}`,
        );
      let result = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }
      const isSuccess =
        result?.status === "success" ||
        result?.success === true ||
        String(result?.status || "").toLowerCase() === "ok";
      if (!isSuccess)
        throw new Error(result?.message || "Unable to create candidate.");
      const createdCandidateId =
        result?.candidate_id ||
        result?.data?.candidate_id ||
        result?.data?.id ||
        result?.id ||
        `rd-${Date.now()}`;
      const primarySkills = parseSkillList(info.primarySkills);
      const secondarySkills = parseSkillList(info.secondarySkills);
      const skills = [...primarySkills, ...secondarySkills]
        .map((skill) => normalizeSkill(skill))
        .filter(Boolean);
      const location = buildPrimaryLocation(
        info.city,
        info.state,
        info.country,
      );
      const status =
        info?.candidateType === "nonBench"
          ? "Non Bench"
          : rdStatusOptions?.includes(info.benchStatus) && info.benchStatus
          ? info.benchStatus
          : "On Bench";
      const nextCandidate = {
        id: createdCandidateId,
        candidate_id: createdCandidateId,
        name: normalizeText(info.name),
        title: normalizeText(info.title),
        headline: normalizeText(info.title) || normalizeText(info.name),
        location,
        referredBy: normalizeText(info.referredBy),
        referredById: info.referredById || "",
        workedWithVdart: info.workedWithVdart || "No",
        status,
        score: 0,
        skills,
        availability: "Available now",
        client: normalizeText(info.previousClient) || "Unassigned",
        experienceYears: normalizeText(info.experience) || "N/A",
        availabilityStatus: "Available now",
        workAuthId: info.workAuthorization
          ? String(info.workAuthorization)
          : "",
        workAuth: info.workAuthorization ? String(info.workAuthorization) : "",
        locationDetail: location,
        benchDays: 0,
        contractDetails: {
          noticePeriod: "N/A",
          rate: "N/A",
          employmentType: "N/A",
          expectedSalary: info.expectedSalary || "N/A",
        },
        skillGroups: {
          primary: primarySkills.map((skill) => normalizeSkill(skill)),
          secondary: secondarySkills.map((skill) => normalizeSkill(skill)),
        },
        experienceItems: [],
        documents: [],
        pipeline: [],
        candidateInfo: { ...info },
      };
      if (info?.candidateType === "nonBench") {
        await fetchNonBenchCandidates();
      } else {
        setRdCandidates((prev) => [...prev, nextCandidate]);
      }
      setShowAddRdModal(false);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSaveCandidateInfo = async ({ candidateId, info }) => {
    if (!candidateId) {
      setCandidateInfoDraft({ ...info });
      return false;
    }
    const targetId = normalizeCandidateId(candidateId);
    if (!targetId) return false;
    let refreshedCandidate = null;
    if (typeof onSaveCandidateInfo === "function") {
      try {
        const result = await onSaveCandidateInfo({
          candidateId,
          info: { ...info },
        });
        if (result === false) return false;
        if (result && typeof result === "object") refreshedCandidate = result;
      } catch (error) {
        showSmallNotice(error?.message || "Failed to save candidate.");
        return false;
      }
    }
    if (!refreshedCandidate) {
      refreshedCandidate = await fetchCandidateById(targetId, {
        ...(selectedRdCandidate || candidateInfoTarget || {}),
        id: targetId,
        candidate_id: targetId,
        candidateId: targetId,
        name: info?.name || selectedRdCandidate?.name || "",
        title: info?.title || selectedRdCandidate?.title || "",
        candidateInfo: {
          ...(selectedRdCandidate?.candidateInfo || {}),
          ...info,
        },
      });
    }
    if (refreshedCandidate) {
      candidateDetailRequestRef.current += 1;
      if (typeof setRdCandidates === "function") {
        setRdCandidates((prev) => {
          if (!Array.isArray(prev)) return prev;
          const hasTarget = prev.some(
            (candidate) =>
              normalizeCandidateId(
                candidate?.id ??
                  candidate?.candidate_id ??
                  candidate?.candidateId,
              ) === targetId,
          );
          if (!hasTarget) return [refreshedCandidate, ...prev];
          return prev.map((candidate) =>
            normalizeCandidateId(
              candidate?.id ??
                candidate?.candidate_id ??
                candidate?.candidateId,
            ) === targetId
              ? { ...candidate, ...refreshedCandidate }
              : candidate,
          );
        });
      }
      setSelectedRdCandidate((prev) =>
        normalizeCandidateId(
          prev?.id ?? prev?.candidate_id ?? prev?.candidateId,
        ) === targetId
          ? refreshedCandidate
          : prev,
      );
      setRdSidebarCandidate((prev) =>
        normalizeCandidateId(
          prev?.id ?? prev?.candidate_id ?? prev?.candidateId,
        ) === targetId
          ? refreshedCandidate
          : prev,
      );
      setCandidateInfoTarget(refreshedCandidate);
      return true;
    }
    candidateDetailRequestRef.current += 1;
    if (typeof setRdCandidates === "function") {
      setRdCandidates((prev) =>
        Array.isArray(prev)
          ? prev.map((candidate) =>
              normalizeCandidateId(
                candidate?.id ??
                  candidate?.candidate_id ??
                  candidate?.candidateId,
              ) === targetId
                ? {
                    ...candidate,
                    name: info?.name || candidate.name,
                    title: info?.title || candidate.title,
                    candidateInfo: { ...info },
                  }
                : candidate,
            )
          : prev,
      );
    }
    setSelectedRdCandidate((prev) =>
      normalizeCandidateId(
        prev?.id ?? prev?.candidate_id ?? prev?.candidateId,
      ) === targetId
        ? {
            ...prev,
            name: info?.name || prev.name,
            title: info?.title || prev.title,
            candidateInfo: { ...info },
          }
        : prev,
    );
    return true;
  };

  const handleMoveToBench = useCallback(async () => {
    if (!selectedRdCandidate) return;
    if (isMovingToBench) return;
    const selectedId = String(
      selectedRdCandidate.id ??
        selectedRdCandidate.candidate_id ??
        selectedRdCandidate.candidateId ??
        "",
    );
    if (!selectedId) return;
    const shouldMoveToBench = !selectedCandidateIsOnBench;
    const trimmedRemoveBenchReason = String(removeBenchReason || "").trim();
    if (!shouldMoveToBench && !trimmedRemoveBenchReason) {
      setMoveToBenchError("Reason is required.");
      return;
    }
    setMoveToBenchError("");
    setIsMovingToBench(true);
    const userSession = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "null");
      } catch {
        return null;
      }
    })();
    const movedBy = toSingleScalar(
      currentUser?.id ??
        currentUser?.user_id ??
        currentUser?.userId ??
        userSession?.id ??
        userSession?.user_id ??
        userSession?.userId ??
        "",
    );
    if (!movedBy) {
      setMoveToBenchError("Moved by user id is missing. Please login again.");
      setIsMovingToBench(false);
      return;
    }
    const endpoint = shouldMoveToBench
      ? moveCandidateToBenchEndpoint
      : removeCandidateFromBenchEndpoint;
    try {
      const formData = new FormData();
      formData.append("candidate_id", selectedId);
      formData.append("moved_by", movedBy);
      if (!shouldMoveToBench) {
        formData.append("reason", trimmedRemoveBenchReason);
        formData.append("candidate_reason", trimmedRemoveBenchReason);
      }
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
      });
      const responseText = await response.text();
      let result = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }
      if (!response.ok)
        throw new Error(result?.message || `HTTP ${response.status}`);
      if (
        result?.success === false ||
        String(result?.status || "").toLowerCase() === "error"
      ) {
        throw new Error(
          result?.message || "Unable to move candidate to bench.",
        );
      }
      const applyBenchUpdate = (candidate) => {
        if (!candidate) return candidate;
        const candidateId = String(
          candidate.id ?? candidate.candidate_id ?? candidate.candidateId ?? "",
        );
        if (candidateId !== selectedId) return candidate;
        const nextOnBench = shouldMoveToBench;
        const nextStatus = nextOnBench
          ? "On Bench"
          : String(candidate?.status || "").toLowerCase() === "on bench"
            ? "Available"
            : candidate?.status;
        return {
          ...candidate,
          status: nextStatus,
          is_candidate_on_bench: nextOnBench ? "1" : "0",
          isCandidateOnBench: nextOnBench,
          candidateInfo: {
            ...(candidate?.candidateInfo || {}),
            is_candidate_on_bench: nextOnBench ? "1" : "0",
            isCandidateOnBench: nextOnBench,
          },
        };
      };
      if (typeof setRdCandidates === "function")
        setRdCandidates((prev) =>
          Array.isArray(prev)
            ? prev.map((candidate) => applyBenchUpdate(candidate))
            : prev,
        );
      if (typeof setRdSidebarCandidate === "function")
        setRdSidebarCandidate((prev) => applyBenchUpdate(prev));
      setSelectedRdCandidate((prev) => applyBenchUpdate(prev));
      if (!shouldMoveToBench) {
        try {
          await Promise.resolve(onBenchUpdated?.());
        } catch (refreshError) {
          const message =
            refreshError?.message ||
            "Candidate removed, but bench refresh failed.";
          setMoveToBenchError(message);
          showSmallNotice(message);
        }
        setShowCandidateEngagementModal(false);
        setRemoveBenchReason("");
        if (previewOnly && typeof onClosePreview === "function")
          onClosePreview();
        else setSelectedRdCandidate(null);
      }
    } catch (err) {
      setMoveToBenchError(
        err?.message ||
          (selectedCandidateIsOnBench
            ? "Unable to remove candidate from bench."
            : "Unable to move candidate to bench."),
      );
    } finally {
      setIsMovingToBench(false);
    }
  }, [
    currentUser?.id,
    currentUser?.user_id,
    currentUser?.userId,
    isMovingToBench,
    moveCandidateToBenchEndpoint,
    onBenchUpdated,
    onClosePreview,
    previewOnly,
    removeBenchReason,
    removeCandidateFromBenchEndpoint,
    selectedCandidateIsOnBench,
    selectedRdCandidate,
    setRdCandidates,
    setRdSidebarCandidate,
    setSelectedRdCandidate,
    showSmallNotice,
  ]);

  const closeRemoveBenchDialog = useCallback(() => {
    setShowRemoveBenchDialog(false);
    setRemoveBenchReason("");
    setMoveToBenchError("");
  }, []);

  const handleMoveToBenchClick = useCallback(() => {
    if (isMovingToBench) return;
    if (selectedCandidateIsOnBench) {
      setRemoveBenchReason("");
      setMoveToBenchError("");
      setShowRemoveBenchDialog(true);
      return;
    }
    handleMoveToBench();
  }, [handleMoveToBench, isMovingToBench, selectedCandidateIsOnBench]);

  const handleConfirmRemoveBench = useCallback(() => {
    if (!String(removeBenchReason || "").trim()) {
      setMoveToBenchError("Reason is required.");
      return;
    }
    setShowRemoveBenchDialog(false);
    handleMoveToBench();
  }, [handleMoveToBench, removeBenchReason]);

  const handleRemoveBenchFromEngagement = useCallback(() => {
    if (!selectedCandidateIsOnBench || isMovingToBench) return;
    setShowCandidateEngagementModal(false);
    setMoveToBenchError("");
    setRemoveBenchReason("");
    setShowRemoveBenchDialog(true);
  }, [isMovingToBench, selectedCandidateIsOnBench]);

  const handleBackToCandidates = useCallback(async () => {
    candidateDetailRequestRef.current += 1;
    candidateModalRequestRef.current += 1;
    if (previewOnly && typeof onClosePreview === "function") {
      onClosePreview();
      return;
    }
    setSelectedRdCandidate(null);
    try {
      await Promise.resolve(onBenchUpdated?.());
    } catch (error) {
      showSmallNotice(error?.message || "Unable to refresh bench candidates.");
    }
  }, [
    onBenchUpdated,
    onClosePreview,
    previewOnly,
    setSelectedRdCandidate,
    showSmallNotice,
  ]);

  useEffect(() => {
    if (!selectedCandidateIsOnBench) setShowRemoveBenchDialog(false);
  }, [selectedCandidateIsOnBench, selectedRdCandidate]);

  const getSubmissionLogStage = (entry) =>
    getSubmissionDisplayStage(entry) || getRdPipelineStage(entry);
  const submissionLogStages = [
    { key: "pipeline", label: "Pipeline" },
    { key: "all", label: "All" },
    { key: "Client Submission", label: "Client Subs" },
    { key: "Interview", label: "Interviews" },
    { key: "Confirmation", label: "Confirmations" },
    { key: "Placement", label: "Placements" },
    { key: "Not Joined", label: "Not Joined" },
  ];
  const getSubmissionLogCount = (history, filterKey) => {
    if (filterKey === "pipeline" || filterKey === "all") return history.length;
    return history.filter((entry) => getSubmissionLogStage(entry) === filterKey)
      .length;
  };
  const getSubmissionEditValue = (entryId, field, fallback) =>
    submissionHistoryEdits?.[entryId]?.[field] ?? fallback ?? "";
  const getFirstSubmissionValue = (entry, keys) => {
    if (!entry || !Array.isArray(keys)) return "";
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const value = entry?.[key];
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return "";
  };
  const withCurrentOption = (options, currentValue) => {
    const list = Array.isArray(options) ? options.filter(Boolean) : [];
    const current = String(currentValue || "").trim();
    if (!current) return list;
    const exists = list.some(
      (option) =>
        String(option || "")
          .trim()
          .toLowerCase() === current.toLowerCase(),
    );
    return exists ? list : [current, ...list];
  };
  const resolveOptionValue = (value, options) => {
    const current = String(value || "").trim();
    if (!current) return "";
    const match = (Array.isArray(options) ? options : []).find(
      (option) =>
        String(option || "")
          .trim()
          .toLowerCase() === current.toLowerCase(),
    );
    return match || current;
  };
  const resolveDirectoryNameById = (value, directory) => {
    const idText = String(toSingleScalar(value) || "").trim();
    if (!idText || !Array.isArray(directory)) return "";
    const numericId = Number(idText);
    const hasNumeric = !Number.isNaN(numericId);
    const match = directory.find((entry) => {
      const ids = [
        String(toSingleScalar(entry?.id) || "").trim(),
        String(toSingleScalar(entry?.employeeId) || "").trim(),
      ].filter(Boolean);
      if (ids.length === 0) return false;
      if (ids.includes(idText)) return true;
      if (!hasNumeric) return false;
      return ids.some((entryIdText) => {
        const entryNumeric = Number(entryIdText);
        return !Number.isNaN(entryNumeric) && entryNumeric === numericId;
      });
    });
    return String(match?.name || "").trim();
  };
  const findRecruiterMatch = useCallback(
    (value, recruiterName = "") => {
      const idText = String(toSingleScalar(value) || "").trim();
      const normalizedName = String(recruiterName || "")
        .trim()
        .toLowerCase();
      if (!Array.isArray(recruiterDirectory)) return null;
      return (
        recruiterDirectory.find((entry) => {
          const primaryId = String(toSingleScalar(entry?.id) || "").trim();
          const employeeId = String(
            toSingleScalar(entry?.employeeId) || "",
          ).trim();
          const entryName = String(entry?.name || "")
            .trim()
            .toLowerCase();
          if (idText && (primaryId === idText || employeeId === idText))
            return true;
          if (normalizedName && entryName === normalizedName) return true;
          return false;
        }) || null
      );
    },
    [recruiterDirectory],
  );
  const updateSubmissionEditValue = (entryId, updates) =>
    setSubmissionHistoryEdits((prev) => ({
      ...prev,
      [entryId]: { ...(prev?.[entryId] || {}), ...updates },
    }));
  const fetchCandidateSubmissionDetails = useCallback(async (candidateId) => {
    const normalizedCandidateId = String(
      toSingleScalar(candidateId) || "",
    ).trim();
    if (!normalizedCandidateId) return [];
    const cached = submissionDetailsCacheRef.current.get(normalizedCandidateId);
    if (Array.isArray(cached)) return cached;
    try {
      const formData = new FormData();
      formData.append("candidate_id", normalizedCandidateId);
      const response = await fetch(GET_CANDIDATE_SUBMISSION_DETAILS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: new URLSearchParams(formData).toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json().catch(() => null);
      const rows = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
          ? result
          : [];
      submissionDetailsCacheRef.current.set(normalizedCandidateId, rows);
      return rows;
    } catch (_error) {
      return [];
    }
  }, []);
  const buildSubmissionDraftFromDetail = useCallback(
    (entry, detail) => {
      if (!entry && !detail) return null;
      const source = detail || entry || {};
      const toDateInput = (value) => {
        const text = String(value || "").trim();
        if (!text) return "";
        const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : "";
      };
      const normalizeQuarter = (value) => {
        const raw = String(value || "")
          .trim()
          .toUpperCase();
        if (!raw) return "";
        if (/^Q[1-4]$/.test(raw)) return raw.slice(1);
        if (/^[1-4]$/.test(raw)) return raw;
        return "";
      };
      const dateValue = toDateInput(
        source?.submission_date ||
          source?.submittedAt ||
          source?.date ||
          entry?.submission_date ||
          entry?.submittedAt ||
          entry?.date ||
          "",
      );
      const recruiterId = toSingleScalar(
        source?.recruiter_id || source?.recruiterId || "",
      );
      const buId = toSingleScalar(source?.bu_id || source?.buId || "");
      const teamId = toSingleScalar(source?.team_id || source?.teamId || "");
      const submissionStatusId = toSingleScalar(
        source?.submission_status_id || source?.submissionStatusId || "",
      );
      const buNameByIdMap = new Map(
        (Array.isArray(buDirectory) ? buDirectory : []).map((e) => [
          String(toSingleScalar(e?.id) || ""),
          String(e?.name || "").trim(),
        ]),
      );
      const teamNameByIdMap = new Map(
        (Array.isArray(teamDirectory) ? teamDirectory : []).map((e) => [
          String(toSingleScalar(e?.id) || ""),
          String(e?.name || "").trim(),
        ]),
      );
      const statusNameByIdMap = new Map(
        (Array.isArray(submissionStatusDirectory)
          ? submissionStatusDirectory
          : []
        ).map((e) => [
          String(toSingleScalar(e?.id) || ""),
          String(e?.name || "").trim(),
        ]),
      );
      const recruiterFromSource = toSingleScalar(
        source?.recruiter || source?.recruiter_name || "",
      );
      const buFromSource = toSingleScalar(
        source?.bu || source?.bu_name || source?.business_unit || "",
      );
      const teamFromSource = toSingleScalar(
        source?.teamName || source?.team_name || "",
      );
      const statusFromSource = toSingleScalar(
        source?.submission_status || source?.submissionStatus || "",
      );
      const recruiterMatch = findRecruiterMatch(
        recruiterId || recruiterFromSource,
        recruiterFromSource || entry?.recruiter || "",
      );
      const recruiterName = String(
        recruiterMatch?.name || recruiterFromSource || entry?.recruiter || "",
      ).trim();
      const recruiterBuName = recruiterMatch
        ? resolveDirectoryNameById(recruiterMatch?.buId, buDirectory) ||
          String(recruiterMatch?.buName || "").trim()
        : "";
      const recruiterTeamName = recruiterMatch
        ? resolveDirectoryNameById(recruiterMatch?.teamId, teamDirectory) ||
          String(recruiterMatch?.teamName || "").trim()
        : "";
      const buName = String(
        recruiterBuName ||
          buNameByIdMap.get(String(buFromSource || "")) ||
          buFromSource ||
          buNameByIdMap.get(String(buId || "")) ||
          entry?.bu ||
          "",
      ).trim();
      const resolvedTeamById = String(
        teamNameByIdMap.get(String(teamId || "")) || "",
      ).trim();
      const teamFromSourceResolved = String(
        teamNameByIdMap.get(String(teamFromSource || "")) || "",
      ).trim();
      const directTeamText = String(
        teamFromSource || entry?.teamName || "",
      ).trim();
      const teamName =
        recruiterTeamName ||
        resolvedTeamById ||
        teamFromSourceResolved ||
        (/^\d+$/.test(directTeamText) ? "" : directTeamText);
      const statusName = String(
        statusNameByIdMap.get(String(statusFromSource || "")) ||
          statusFromSource ||
          statusNameByIdMap.get(String(submissionStatusId || "")) ||
          entry?.submissionStatus ||
          "",
      ).trim();
      const quarter = normalizeQuarter(
        source?.quarter ||
          entry?.quarter ||
          (dateValue
            ? getQuarterFromDate(new Date(`${dateValue}T00:00:00`))
            : ""),
      );
      const week = String(
        source?.week ||
          entry?.week ||
          (dateValue
            ? getIsoWeekNumber(new Date(`${dateValue}T00:00:00`))
            : ""),
      ).trim();
      return {
        submission_date: dateValue,
        quarter,
        week,
        role: String(source?.role || entry?.role || "").trim(),
        requirementSource: String(
          source?.source_of_requirement ||
            source?.requirementSource ||
            entry?.requirementSource ||
            entry?.source_of_requirement ||
            "",
        ).trim(),
        client: String(
          source?.client || source?.client_name || entry?.client || "",
        ).trim(),
        recruiter: recruiterName,
        recruiter_id: recruiterId || null,
        bu: buName,
        bu_id: buId || null,
        teamName:
          teamName && teamName.toLowerCase() !== recruiterName.toLowerCase()
            ? teamName
            : "",
        team_id: teamId || null,
        submissionType: String(
          source?.submission_type ||
            source?.submissionType ||
            entry?.submissionType ||
            "",
        ).trim(),
        submissionStatus: statusName,
        submission_status_id: submissionStatusId || null,
        reason: String(source?.reason || entry?.reason || "").trim(),
      };
    },
    [buDirectory, findRecruiterMatch, submissionStatusDirectory, teamDirectory],
  );
  const prepareSubmissionEditor = useCallback(
    async (candidateId, entry) => {
      if (!entry) return;
      const normalizedCandidateId = String(
        toSingleScalar(candidateId) || "",
      ).trim();
      const normalizedEntryId = String(
        toSingleScalar(entry?.submission_id || entry?.id || "") || "",
      ).trim();
      const baseDraft = buildSubmissionDraftFromDetail(entry, null);
      if (baseDraft) updateSubmissionEditValue(entry.id, baseDraft);
      if (!normalizedCandidateId) return;
      const rows = await fetchCandidateSubmissionDetails(normalizedCandidateId);
      if (!Array.isArray(rows) || rows.length === 0) return;
      const detail =
        rows.find((row) => {
          const rowId = String(
            toSingleScalar(
              row?.submission_id || row?.id || row?.update_key || "",
            ) || "",
          ).trim();
          if (normalizedEntryId && rowId) return rowId === normalizedEntryId;
          const rowDate = String(row?.submission_date || "").trim();
          const rowRole = String(row?.role || "")
            .trim()
            .toLowerCase();
          const rowClient = String(row?.client || row?.client_name || "")
            .trim()
            .toLowerCase();
          const entryDate = String(entry?.submission_date || "").trim();
          const entryRole = String(entry?.role || "")
            .trim()
            .toLowerCase();
          const entryClient = String(entry?.client || "")
            .trim()
            .toLowerCase();
          return (
            rowDate === entryDate &&
            rowRole === entryRole &&
            rowClient === entryClient
          );
        }) || (rows.length === 1 ? rows[0] : null);
      const draft = buildSubmissionDraftFromDetail(entry, detail);
      if (draft) updateSubmissionEditValue(entry.id, draft);
    },
    [buildSubmissionDraftFromDetail, fetchCandidateSubmissionDetails],
  );

  const submitSubmissionEdits = async (candidateId, entry, totalPages) => {
    const draft = submissionHistoryEdits?.[entry.id];
    if (!draft) return;
    const draftSubmissionDate = String(
      draft.submission_date || entry?.submission_date || "",
    ).trim();
    if (isFutureDateInputValue(draftSubmissionDate)) {
      showSmallNotice("Submission date cannot be in the future.");
      return;
    }
    const submittedByValue = String(
      draft.recruiter ??
        entry?.recruiter ??
        entry?.recruiter_name ??
        entry?.submittedBy ??
        "",
    ).trim();
    if (!submittedByValue) {
      showSmallNotice("Submitted By is mandatory.");
      return;
    }
    const ok = await handleUpdateSubmissionHistory(
      candidateId,
      entry.id,
      draft,
    );
    if (!ok) return;
    setSubmissionHistoryEdits((prev) => {
      const next = { ...prev };
      delete next[entry.id];
      return next;
    });
    setShowSubmissionEditors((prev) => ({ ...prev, [entry.id]: false }));
    setActiveRecruiterEditEntryId((prev) => (prev === entry.id ? null : prev));
    if (totalPages > submissionLogPage)
      setSubmissionLogPage((prev) => Math.min(prev + 1, totalPages));
  };

  const buildRequirementRow = () => ({
    id: `req-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    requirementId: null,
    bu: "",
    client: "",
    jobTitle: "",
    status: "",
    createdBy: currentUser?.name || currentUser?.email || "Unknown",
    createdAt: new Date().toISOString(),
  });

  const resolveRequirementContext = useCallback(() => {
    const candidate = selectedRdCandidate || rdOverviewCandidate || null;
    const candidateId = toSingleScalar(
      candidate?.candidate_id ??
        candidate?.candidateId ??
        candidate?.id ??
        null,
    );
    const rdId = toSingleScalar(
      candidate?.rd_id ??
        candidate?.rdId ??
        candidate?.candidateInfo?.rd_id ??
        candidate?.candidateInfo?.rdId ??
        "",
    );
    return { candidateId, rdId };
  }, [rdOverviewCandidate, selectedRdCandidate]);

  const handleRemoveRequirement = useCallback(
    async (row) => {
      if (!row) return;
      setRequirementError("");
      if (row.requirementId == null) {
        setRequirementForm((prev) => prev.filter((item) => item.id !== row.id));
        return;
      }
      const { candidateId, rdId } = resolveRequirementContext();
      try {
        const response = await fetch(removeRequirementEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            requirement_id: toSingleScalar(row.requirementId),
            id: toSingleScalar(row.requirementId),
            candidate_id: candidateId || "",
            rd_id: rdId || "",
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json().catch(() => null);
        if (result?.success === false || result?.status === "error")
          throw new Error(result?.message || "Unable to remove requirement.");
        setRequirementForm((prev) => prev.filter((item) => item.id !== row.id));
      } catch (err) {
        setRequirementError(err?.message || "Unable to remove requirement.");
      }
    },
    [removeRequirementEndpoint, resolveRequirementContext],
  );

  const handleSaveRequirement = useCallback(async () => {
    if (isSavingRequirement) return;
    setRequirementError("");
    const rows = requirementForm.filter(
      (row) =>
        String(row?.bu || "").trim() ||
        String(row?.client || "").trim() ||
        String(row?.jobTitle || "").trim() ||
        String(row?.status || "").trim(),
    );
    if (rows.length === 0) {
      setRequirementError("Add at least one requirement before saving.");
      return;
    }
    const { candidateId, rdId } = resolveRequirementContext();
    if (!candidateId) {
      setRequirementError("Select a candidate before saving requirement.");
      return;
    }
    setIsSavingRequirement(true);
    try {
      const updatedRows = [];
      for (const row of rows) {
        if (row.requirementId) {
          updatedRows.push(row);
          continue;
        }
        const payload = {
          candidate_id: candidateId || "",
          rd_id: rdId || "",
          bu: row.bu || "",
          client: row.client || "",
          job_title: row.jobTitle || "",
          requirement_name: row.jobTitle || "",
          status: row.status || "",
          updated_by: toSingleScalar(currentUser?.id || "") || "Unknown",
        };
        const response = await fetch(addRequirementEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json().catch(() => null);
        if (result?.success === false || result?.status === "error")
          throw new Error(result?.message || "Unable to save requirement.");
        updatedRows.push({
          ...row,
          requirementId:
            result?.requirement_id ??
            result?.data?.requirement_id ??
            result?.data?.id ??
            result?.id ??
            null,
        });
      }
      setRequirementForm(updatedRows);
      setShowRequirementModal(false);
      setRequirementForm([]);
    } catch (err) {
      setRequirementError(err?.message || "Unable to save requirement.");
    } finally {
      setIsSavingRequirement(false);
    }
  }, [
    addRequirementEndpoint,
    currentUser?.id,
    isSavingRequirement,
    requirementForm,
    resolveRequirementContext,
  ]);

  const requirementSubmissionStats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0,
    );
    const weekStart = new Date(todayStart);
    const day = weekStart.getDay();
    const shift = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + shift);
    const monthStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      1,
      0,
      0,
      0,
      0,
    );
    const getEntryDate = (entry) => {
      const rawDate =
        entry?.submission_date ||
        entry?.submittedAt ||
        entry?.date ||
        entry?.created_at ||
        entry?.createdAt ||
        null;
      if (!rawDate) return null;
      const parsed = new Date(rawDate);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };
    const getCounts = (entries) => {
      const items = entries
        .map((e) => getEntryDate(e))
        .filter((d) => d instanceof Date);
      return {
        daily: items.filter((d) => d >= todayStart).length,
        weekly: items.filter((d) => d >= weekStart).length,
        monthly: items.filter((d) => d >= monthStart).length,
        total: items.length,
      };
    };
    const allEntries = Object.values(rdSubmissions || {}).flatMap((s) =>
      buildSubmissionHistory(s),
    );
    const selectedCandidateId = toSingleScalar(
      selectedRdCandidate?.id ||
        selectedRdCandidate?.candidate_id ||
        selectedRdCandidate?.candidateId ||
        "",
    );
    const selectedSubmission = selectedCandidateId
      ? rdSubmissions?.[selectedCandidateId]
      : null;
    return {
      all: getCounts(allEntries),
      selected: getCounts(buildSubmissionHistory(selectedSubmission)),
    };
  }, [rdSubmissions, selectedRdCandidate]);

  const requirementProgress = useMemo(() => {
    const rows = Array.isArray(requirementForm) ? requirementForm : [];
    const addressed = rows.filter(
      (row) => String(row?.status || "").toLowerCase() === "addressed",
    ).length;
    const pending = rows.filter(
      (row) => String(row?.status || "").toLowerCase() === "not addressed",
    ).length;
    return { total: rows.length, addressed, pending };
  }, [requirementForm]);

  const allRecruiterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(recruiterDirectory) ? recruiterDirectory : [])
            .map((entry) => String(entry?.name || "").trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [recruiterDirectory],
  );

  const recruiterSuggestionQuery = submissionForm?.recruiter || "";
  const normalizedRecruiterSuggestionQuery = recruiterSuggestionQuery
    .trim()
    .toLowerCase();
  const recruiterSuggestionMatches = normalizedRecruiterSuggestionQuery
    ? allRecruiterOptions.filter((name) =>
        name.toLowerCase().includes(normalizedRecruiterSuggestionQuery),
      )
    : allRecruiterOptions;
  const visibleRecruiterSuggestions = recruiterSuggestionMatches.slice(0, 8);
  const getEditRecruiterSuggestions = useCallback(
    (query) => {
      const normalizedQuery = String(query || "")
        .trim()
        .toLowerCase();
      const matches = normalizedQuery
        ? allRecruiterOptions.filter((name) =>
            name.toLowerCase().includes(normalizedQuery),
          )
        : allRecruiterOptions;
      return matches.slice(0, 8);
    },
    [allRecruiterOptions],
  );
  const recruiterIdByName = useMemo(() => {
    const map = new Map();
    recruiterDirectory.forEach((e) => {
      const key = String(e.name || "")
        .trim()
        .toLowerCase();
      if (key) map.set(key, e.employeeId ?? e.id ?? null);
    });
    return map;
  }, [recruiterDirectory]);
  const recruiterByName = useMemo(() => {
    const map = new Map();
    recruiterDirectory.forEach((e) => {
      const key = String(e.name || "")
        .trim()
        .toLowerCase();
      if (key) map.set(key, e);
    });
    return map;
  }, [recruiterDirectory]);
  const buIdByName = useMemo(() => {
    const map = new Map();
    buDirectory.forEach((e) => {
      const key = String(e.name || "")
        .trim()
        .toLowerCase();
      if (key && e.id != null) map.set(key, e.id);
    });
    return map;
  }, [buDirectory]);
  const teamIdByName = useMemo(() => {
    const map = new Map();
    teamDirectory.forEach((e) => {
      const key = String(e.name || "")
        .trim()
        .toLowerCase();
      if (key && e.id != null) map.set(key, e.id);
    });
    return map;
  }, [teamDirectory]);
  const submissionStatusIdByName = useMemo(() => {
    const map = new Map();
    submissionStatusDirectory.forEach((e) => {
      const key = String(e.name || "")
        .trim()
        .toLowerCase();
      if (key && e.id != null) map.set(key, e.id);
    });
    return map;
  }, [submissionStatusDirectory]);
  const resolveRecruiterLinkedFields = useCallback(
    (match) => {
      if (!match) return { bu: "", bu_id: null, teamName: "", team_id: null };
      const rawBuName = String(
        toSingleScalar(match?.buName || "") || "",
      ).trim();
      const rawBuId = toSingleScalar(match?.buId || "");
      const buId = rawBuId || (/^\d+$/.test(rawBuName) ? rawBuName : null);
      const buNameFromId =
        resolveDirectoryNameById(rawBuName, buDirectory) ||
        resolveDirectoryNameById(buId, buDirectory);
      const buName = buNameFromId || (/^\d+$/.test(rawBuName) ? "" : rawBuName);

      const rawTeamName = String(
        toSingleScalar(match?.teamName || "") || "",
      ).trim();
      const rawTeamId = toSingleScalar(match?.teamId || "");
      const teamId =
        rawTeamId || (/^\d+$/.test(rawTeamName) ? rawTeamName : null);
      const teamNameFromId = resolveDirectoryNameById(teamId, teamDirectory);
      const teamName =
        teamNameFromId || (/^\d+$/.test(rawTeamName) ? "" : rawTeamName);
      const recruiterName = String(match?.name || "")
        .trim()
        .toLowerCase();
      const safeTeamName =
        String(teamName || "")
          .trim()
          .toLowerCase() === recruiterName
          ? ""
          : teamName;

      return {
        bu: buName || "",
        bu_id: buId || null,
        teamName: safeTeamName || "",
        team_id: teamId || null,
      };
    },
    [buDirectory, teamDirectory],
  );

  useEffect(() => {
    if (
      !Array.isArray(recruiterDirectory) ||
      !Array.isArray(buDirectory) ||
      !Array.isArray(teamDirectory) ||
      !Array.isArray(submissionStatusDirectory)
    )
      return;
    setSubmissionHistoryEdits((prev) => {
      const entryIds = Object.keys(prev || {});
      if (entryIds.length === 0) return prev;
      let hasChanges = false;
      const next = { ...prev };
      entryIds.forEach((entryId) => {
        const draft = prev?.[entryId];
        if (!draft || typeof draft !== "object") return;
        const recruiterName = String(draft?.recruiter || "").trim();
        const recruiterId = toSingleScalar(draft?.recruiter_id || "");
        const buName = String(draft?.bu || "").trim();
        const buId = toSingleScalar(draft?.bu_id || "");
        const teamName = String(draft?.teamName || "").trim();
        const teamId = toSingleScalar(draft?.team_id || "");
        const statusName = String(draft?.submissionStatus || "").trim();
        const statusId = toSingleScalar(draft?.submission_status_id || "");

        const recruiterMatch = findRecruiterMatch(recruiterId, recruiterName);
        const linked = resolveRecruiterLinkedFields(recruiterMatch);
        const normalizedRecruiterName = String(
          recruiterMatch?.name || recruiterName || "",
        ).trim();
        const normalizedBuName =
          linked.bu ||
          resolveDirectoryNameById(buName, buDirectory) ||
          buName ||
          resolveDirectoryNameById(buId, buDirectory);
        const normalizedTeamName =
          linked.teamName ||
          resolveDirectoryNameById(teamName, teamDirectory) ||
          teamName ||
          resolveDirectoryNameById(teamId, teamDirectory);
        const normalizedStatusName =
          resolveDirectoryNameById(statusName, submissionStatusDirectory) ||
          statusName ||
          resolveDirectoryNameById(statusId, submissionStatusDirectory);

        if (
          normalizedRecruiterName !== recruiterName ||
          normalizedBuName !== buName ||
          normalizedTeamName !== teamName ||
          normalizedStatusName !== statusName
        ) {
          hasChanges = true;
          next[entryId] = {
            ...draft,
            recruiter: normalizedRecruiterName || recruiterName,
            recruiter_id:
              recruiterMatch?.employeeId ||
              recruiterMatch?.id ||
              recruiterId ||
              null,
            bu: normalizedBuName || buName,
            bu_id: linked.bu_id || buId || null,
            teamName: normalizedTeamName || teamName,
            team_id: linked.team_id || teamId || null,
            submissionStatus: normalizedStatusName || statusName,
          };
        }
      });
      return hasChanges ? next : prev;
    });
  }, [
    buDirectory,
    findRecruiterMatch,
    resolveRecruiterLinkedFields,
    submissionStatusDirectory,
    teamDirectory,
  ]);

  useEffect(() => {
    if (!showSubmissionHistory) return;
    setSubmissionLogPage(1);
  }, [
    showSubmissionHistory,
    submissionLogFilter,
    submissionCompanyQuery,
    submissionRecruiterQuery,
    selectedRdCandidate?.id,
  ]);

  const handleUpdateSubmissionHistory = async (
    candidateId,
    entryId,
    updates,
  ) => {
    const existing = rdSubmissions?.[candidateId];
    if (!existing) return false;
    const history = buildSubmissionHistory(existing);
    const targetEntry = history.find((e) => e.id === entryId) || history[0];
    if (!targetEntry) return false;
    const mergedEntry = { ...targetEntry, ...updates };
    const submittedByName =
      currentUser?.name || currentUser?.email || "Unknown";
    const submittedById = currentUser?.id || null;
    const normalizedStatus = String(
      mergedEntry.submissionStatus || mergedEntry.submission_status || "",
    ).trim();
    const statusIdFromName =
      submissionStatusIdByName.get(normalizedStatus.toLowerCase()) ?? null;
    const submissionStatusId =
      statusIdFromName ??
      (/^\d+$/.test(normalizedStatus) ? Number(normalizedStatus) : null);
    const recruiterName = String(mergedEntry.recruiter || "")
      .trim()
      .toLowerCase();
    const selectedRecruiter = recruiterByName.get(recruiterName) || null;
    const submissionDate =
      mergedEntry.submission_date ||
      mergedEntry.submittedAt ||
      mergedEntry.date ||
      null;
    if (isFutureDateInputValue(submissionDate)) {
      showSmallNotice("Submission date cannot be in the future.");
      return false;
    }
    const normalizeQuarter = (value) => {
      if (!value) return "";
      const raw = String(value).trim().toUpperCase();
      if (/^Q[1-4]$/.test(raw)) return raw.slice(1);
      if (/^[1-4]$/.test(raw)) return raw;
      return "";
    };
    const submissionQuarter =
      normalizeQuarter(mergedEntry.quarter) ||
      normalizeQuarter(mergedEntry.quarter?.value) ||
      (submissionDate ? getQuarterFromDate(new Date(submissionDate)) : "");
    const payload = {
      submission_id: mergedEntry.submission_id || mergedEntry.id || null,
      candidate_id: candidateId,
      quarter: submissionQuarter || "",
      week: mergedEntry.week || null,
      submission_date: submissionDate,
      role: mergedEntry.role || null,
      source_of_requirement:
        mergedEntry.requirementSource ||
        mergedEntry.source_of_requirement ||
        null,
      client: mergedEntry.client || null,
      recruiter: mergedEntry.recruiter || null,
      recruiter_id:
        selectedRecruiter?.employeeId ||
        selectedRecruiter?.id ||
        recruiterIdByName.get(recruiterName) ||
        null,
      bu_id:
        selectedRecruiter?.buId ||
        buIdByName.get(
          String(mergedEntry.bu || "")
            .trim()
            .toLowerCase(),
        ) ||
        null,
      team_id:
        selectedRecruiter?.teamId ||
        teamIdByName.get(
          String(mergedEntry.teamName || "")
            .trim()
            .toLowerCase(),
        ) ||
        null,
      submission_type:
        mergedEntry.submissionType || mergedEntry.submission_type || null,
      submission_status:
        mergedEntry.submissionStatus || mergedEntry.submission_status || null,
      submission_status_id: submissionStatusId,
      reason: mergedEntry.reason || null,
      submitted_by: mergedEntry.submittedById || submittedById,
      submitted_by_name: mergedEntry.submittedBy || submittedByName,
      updatedBy: submittedById,
      update_key: mergedEntry.submission_id || mergedEntry.id || null,
    };
    try {
      const response = await fetch(SAVE_SUBMISSION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result?.status && result.status !== "success")
        throw new Error(result?.message || "Save failed");
      const lastChangedAt =
        result?.updated_at ||
        result?.data?.updated_at ||
        result?.updated_on ||
        result?.data?.updated_on ||
        result?.submitted_at ||
        result?.data?.submitted_at ||
        new Date().toISOString();
      submissionDetailsCacheRef.current.delete(
        String(toSingleScalar(candidateId) || ""),
      );
      setRdSubmissions((prev) => {
        const existingEntry = prev?.[candidateId];
        if (!existingEntry) return prev;
        const currentHistory = buildSubmissionHistory(existingEntry);
        const nextHistory = currentHistory.map((e) =>
          e.id === entryId
            ? {
                ...e,
                ...mergedEntry,
                submissionStatus:
                  mergedEntry.submissionStatus ||
                  mergedEntry.submission_status ||
                  e.submissionStatus,
                submissionType:
                  mergedEntry.submissionType ||
                  mergedEntry.submission_type ||
                  e.submissionType,
                reason: mergedEntry.reason ?? e.reason,
                last_changed_at: lastChangedAt,
                updated_at: lastChangedAt,
              }
            : e,
        );
        if (nextHistory.length === 0) return prev;
        const orderedHistory = buildSubmissionHistory({ history: nextHistory });
        const latest = orderedHistory[0];
        return {
          ...prev,
          [candidateId]: {
            ...existingEntry,
            ...latest,
            history: orderedHistory,
          },
        };
      });
      setRecentSubmissionChange({ candidateId, entryId });
      return true;
    } catch (err) {
      return false;
    }
  };

  const openCandidateDetail = useCallback(
    async (candidate) => {
      if (!candidate) return;
      const targetId = normalizeCandidateId(
        candidate?.candidate_id ??
          candidate?.candidateId ??
          candidate?.id ??
          null,
      );
      if (!targetId) return;
      if (
        normalizeCandidateId(
          selectedRdCandidate?.id ??
            selectedRdCandidate?.candidate_id ??
            selectedRdCandidate?.candidateId,
        ) === targetId
      )
        return;

      const requestId = candidateDetailRequestRef.current + 1;
      candidateDetailRequestRef.current = requestId;
      setShowSubmissionHistory(false);
      if (typeof window !== "undefined")
        window.scrollTo({ top: 0, behavior: "auto" });

      setSelectedRdCandidate(candidate);
      setRdSidebarCandidate(candidate);

      try {
        const latest = await fetchCandidateById(targetId, candidate);
        if (candidateDetailRequestRef.current !== requestId) return;
        if (latest) {
          setSelectedRdCandidate(latest);
          setRdSidebarCandidate(latest);
        }
      } catch (_error) {
        if (candidateDetailRequestRef.current !== requestId) return;
      }
    },
    [
      fetchCandidateById,
      normalizeCandidateId,
      selectedRdCandidate,
      setRdSidebarCandidate,
      setSelectedRdCandidate,
    ],
  );

  // ─── Skeleton ────────────────────────────────────────────────────────────
  const candidateCardSkeleton = (index) => (
    <div
      key={`skeleton-${index}`}
      className="overflow-hidden rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
    >
      <div className="animate-pulse flex items-start gap-4">
        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-44 rounded-lg bg-slate-100" />
          <div className="h-3 w-32 rounded-lg bg-slate-100" />
          <div className="h-3 w-24 rounded-lg bg-slate-100" />
        </div>
        <div className="h-8 w-20 rounded-xl bg-slate-100" />
      </div>
      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-4 animate-pulse space-y-2">
        <div className="h-2 w-full rounded-full bg-slate-100" />
        <div className="flex gap-3 mt-2">
          <div className="h-3 w-24 rounded-lg bg-slate-100" />
          <div className="h-3 w-32 rounded-lg bg-slate-100" />
        </div>
      </div>
    </div>
  );

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(135deg, #f8faff 0%, #f0f4ff 50%, #faf8ff 100%)",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
        .rd-section, .rd-section * { font-family: 'DM Sans', system-ui, sans-serif; }
        .rd-section h1,.rd-section h2,.rd-section h3,.rd-section h4,.rd-section .font-heading { font-family: 'Sora', system-ui, sans-serif; }
        
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideRight { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
        @keyframes glow { 0%,100% { box-shadow: 0 0 20px rgba(99,102,241,0.15); } 50% { box-shadow: 0 0 40px rgba(99,102,241,0.3); } }
        @keyframes loading { 0% { width: 0%; margin-left: 0; } 50% { width: 60%; margin-left: 20%; } 100% { width: 0%; margin-left: 100%; } }
        @keyframes shimmer { from { background-position: -200% 0; } to { background-position: 200% 0; } }
        @keyframes pulse-ring { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(1.5); opacity: 0; } }
        
        .fade-up { animation: fadeUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .slide-right { animation: slideRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .scale-in { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .candidate-card { animation: fadeUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
        
        .skill-tag { transition: all 0.15s ease; }
        .skill-tag:hover { transform: translateY(-1px); }
        
        .bench-bar { transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1); }
        
        .card-hover { transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
        .card-hover:hover { transform: translateY(-2px); }
        
        .btn-primary { background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%); transition: all 0.2s ease; }
        .btn-primary:hover { background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%); box-shadow: 0 4px 15px rgba(99,102,241,0.35); transform: translateY(-1px); }
        .btn-primary:active { transform: translateY(0); }
        
        .btn-ghost { transition: all 0.15s ease; }
        .btn-ghost:hover { background: rgba(99,102,241,0.06); }
        
        .sidebar-tab-active { position: relative; }
        .sidebar-tab-active::after { content:''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, #6366f1, #7c3aed); border-radius: 2px 2px 0 0; }
        
        .scrollbar-thin::-webkit-scrollbar { width: 4px; height: 4px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
        
        .profile-hero { background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%); }
        
        .submission-card { transition: all 0.2s ease; }
        .submission-card:hover { border-color: rgba(99,102,241,0.3); box-shadow: 0 4px 20px rgba(99,102,241,0.08); }
        
        .notice-toast { animation: fadeUp 0.3s ease both; }
        
        @media (prefers-reduced-motion: reduce) {
          .fade-up, .slide-right, .scale-in, .candidate-card { animation: none !important; }
          .card-hover, .btn-primary, .btn-ghost { transition: none !important; }
        }
      `}</style>

      <div className="rd-section p-4 md:p-6 max-w-[1680px] mx-auto">
        {/* Toast notice */}
        {smallNotice && (
          <div className="fixed right-5 top-5 z-[1200] notice-toast">
            <div className="flex items-center gap-2.5 rounded-xl border border-rose-200/80 bg-white px-4 py-2.5 text-sm font-semibold text-rose-700 shadow-xl shadow-rose-100">
              <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              {smallNotice}
            </div>
          </div>
        )}

        {candidateDocsMenuOpen &&
          renderPortal(
            <>
              <button
                type="button"
                aria-label="Close uploaded files"
                className="fixed inset-0 z-[1290] cursor-default bg-transparent"
                onClick={() => setCandidateDocsMenuOpen(false)}
              />
              <div
                className="fixed z-[1300] flex overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/15"
                style={{
                  top: candidateDocsMenuPosition.top,
                  left: candidateDocsMenuPosition.left,
                  width: candidateDocsMenuPosition.width,
                  maxHeight: candidateDocsMenuPosition.maxHeight,
                }}
              >
                <div className="flex min-h-0 w-full flex-col">
                  <div className="shrink-0 border-b border-slate-100 bg-white px-4 py-3">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                      Uploaded Files
                    </p>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 pr-1 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
                    {candidateDocs.map((doc) => {
                      const docType = String(doc?.document_type || "Document")
                        .trim()
                        .toUpperCase();
                      const fileUrl = buildUploadedDocumentUrl(
                        doc?.file_url,
                        rdApiBaseUrl,
                      );
                      const fileName =
                        String(doc?.file_url || "")
                          .split("/")
                          .pop() || "file";
                      return (
                        <a
                          key={doc.id || `${docType}-${fileName}`}
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-emerald-50"
                        >
                          <span className="min-w-0">
                            <span className="block text-xs font-semibold text-slate-800">
                              {docType}
                            </span>
                            <span className="block truncate text-xs text-slate-500">
                              {fileName}
                            </span>
                          </span>
                          <Download className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
          )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* ── Sidebar ──────────────────────────────────────────────────── */}
          <aside className="lg:col-span-4 xl:col-span-3 space-y-4 lg:sticky lg:top-6 lg:self-start lg:z-10">
            {/* Pipeline Overview Card */}
            <GlassCard className="p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[12px] font-bold uppercase tracking-widest text-slate-400 font-heading">
                  Candidate
                </p>
                <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-sm shadow-emerald-300 animate-pulse" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="Bench"
                  value={benchCandidatesCount}
                  icon={UserCheck}
                  color="emerald"
                />
                <StatCard
                  label="Non-Bench"
                  value={nonBenchCandidatesCount}
                  icon={Users}
                  color="blue"
                />
              </div>
            </GlassCard>

            {/* Candidate Detail Sidebar (only in View Profile page) */}
            {selectedRdCandidate && (
              <GlassCard className="overflow-hidden">
                {/* Tab Nav */}
                <div className="flex border-b border-slate-100/80">
                  {["overview", "skills", "bench"].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setSidebarTab(tab)}
                      className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest capitalize transition-all duration-200 relative ${
                        sidebarTab === tab
                          ? "text-indigo-600 sidebar-tab-active"
                          : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {rdOverviewCandidate ? (
                  <div className="p-5">
                    {sidebarTab === "overview" && (
                      <div className="space-y-4 slide-right">
                        <div className="flex items-start gap-3">
                          <Avatar name={rdOverviewCandidate.name} size="md" />
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-slate-900 leading-tight font-heading">
                              {rdOverviewCandidate.name}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">
                              {rdOverviewCandidate.title}
                            </p>
                            <div className="mt-1.5">
                              <Badge variant="success">
                                {getRdAvailabilityStatus(
                                  rdOverviewCandidate,
                                  rdSubmissions[rdOverviewCandidate.id],
                                )}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {[
                            {
                              icon: MapPin,
                              color: "bg-rose-100 text-rose-600",
                              label:
                                rdOverviewCandidate.location || "Location N/A",
                              sub: rdOverviewCandidate.locationDetail,
                            },
                            {
                              icon: Briefcase,
                              color: "bg-indigo-100 text-indigo-600",
                              label: (
                                <WorkAuthorizationLabel
                                  workAuthValue={
                                    rdOverviewCandidate.workAuthId ||
                                    rdOverviewCandidate.candidateInfo
                                      ?.workAuthorizationId ||
                                    rdOverviewCandidate.workAuth ||
                                    rdOverviewCandidate.candidateInfo
                                      ?.workAuthorization
                                  }
                                  workAuthMap={workAuthMap}
                                  fallback={
                                    workAuthMapStatus === "loading"
                                      ? "Loading..."
                                      : "N/A"
                                  }
                                />
                              ),
                              sub: "Work Auth",
                            },
                          ].map(({ icon: Icon, color, label, sub }, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-3 p-3 rounded-xl bg-slate-50/60 border border-slate-100"
                            >
                              <div
                                className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-700 truncate">
                                  {label}
                                </p>
                                {sub && (
                                  <p className="text-[11px] text-slate-400 truncate">
                                    {sub}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sidebarTab === "bench" && (
                      <div className="slide-right space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 font-heading">
                          Bench Summary
                        </p>
                        {[
                          {
                            label: "Bench Days",
                            value: rdOverviewCandidate.benchDays,
                            icon: Clock,
                          },
                          {
                            label: "Availability",
                            value: rdOverviewCandidate.availability,
                            icon: CheckCircle,
                          },
                          {
                            label: "Last Client",
                            value: rdOverviewCandidate.client,
                            icon: Briefcase,
                          },
                        ].map(({ label, value, icon: Icon }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between p-3 rounded-xl bg-slate-50/60 border border-slate-100"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-xs text-slate-500">
                                {label}
                              </span>
                            </div>
                            <span className="text-xs font-bold text-slate-800">
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {sidebarTab === "skills" && (
                      <div className="slide-right">
                        {[
                          {
                            label: "Primary",
                            skills: overviewSkillGroups.primary,
                            color:
                              "bg-indigo-50 text-indigo-700 border-indigo-200/80 hover:bg-indigo-100",
                          },
                          {
                            label: "Secondary",
                            skills: overviewSkillGroups.secondary,
                            color:
                              "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100",
                          },
                        ].map(({ label, skills, color }) => (
                          <div key={label} className="mb-4 last:mb-0">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 font-heading">
                              {label}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {skills.length > 0 ? (
                                skills.map((skill) => (
                                  <span
                                    key={`${rdOverviewCandidate.id}-${label}-${skill}`}
                                    className={`skill-tag text-[11px] font-semibold px-2.5 py-1 rounded-full border ${color} transition-colors cursor-default`}
                                  >
                                    {skill}
                                  </span>
                                ))
                              ) : (
                                <p className="text-xs text-slate-400">
                                  None listed
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-10 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto mb-3">
                      <Users className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-500 font-heading">
                      No candidate selected
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Select to view profile
                    </p>
                  </div>
                )}
              </GlassCard>
            )}
          </aside>


          {/* Main Content */}
          <section className="lg:col-span-8 xl:col-span-9 space-y-5 scroll-mt-6">
            {/* Search & Filter Bar */}
            {!selectedRdCandidate && (
              <GlassCard className="overflow-hidden">
                <div className="relative">
                  <div
                    className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
                    style={{
                      background:
                        "linear-gradient(90deg, #6366f1, #7c3aed, #a855f7, #06b6d4)",
                    }}
                  />
                  <div className="p-5 pt-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900 font-heading tracking-tight">
                          RD Candidates
                        </h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Boolean + keyword search across candidate lists
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-1 sm:max-w-3xl sm:ml-6">
                        <RDCandidateSearchInput
                          variant="team"
                          value={searchInputValue}
                          onChange={setSearchInputValue}
                          placeholder='e.g. "C# Developer" AND GCP'
                        />
                        <button
                          type="button"
                          onClick={openMappingImportModal}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm font-bold text-indigo-700 transition-all hover:border-indigo-300 hover:bg-indigo-100"
                        >
                          <Upload className="h-4 w-4" />
                          Import
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setShowRdAdvancedFilters((prev) => !prev)
                          }
                          className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${
                            showRdAdvancedFilters
                              ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 btn-ghost"
                          }`}
                        >
                          <Filter className="w-4 h-4" />
                          Filters
                          {showRdAdvancedFilters ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {showRdAdvancedFilters && (
                  <div className="border-t border-slate-100/80 p-5 bg-slate-50/40 fade-up">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-heading">
                        Advanced Filters
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          setRdAdvancedFilters({
                            skills: "",
                            jobTitle: "",
                            location: "",
                            availability: "all",
                            experience: "",
                            employmentType: "",
                          })
                        }
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                      <InputField label="Skills">
                        <input
                          type="text"
                          list="rd-skill-suggestions"
                          placeholder="React, AWS..."
                          value={rdAdvancedFilters.skills || ""}
                          onChange={(e) =>
                            setRdAdvancedFilters((prev) => ({
                              ...prev,
                              skills: e.target.value,
                            }))
                          }
                          className={inputCls}
                        />
                        <datalist id="rd-skill-suggestions">
                          {rdSkillSuggestions.map((skill) => (
                            <option key={skill} value={skill} />
                          ))}
                        </datalist>
                      </InputField>
                      <InputField label="Job Title">
                        <input
                          type="text"
                          list="rd-job-title-suggestions"
                          placeholder="e.g. Backend Developer"
                          value={rdAdvancedFilters.jobTitle || ""}
                          onChange={(e) =>
                            setRdAdvancedFilters((prev) => ({
                              ...prev,
                              jobTitle: e.target.value,
                            }))
                          }
                          className={inputCls}
                        />
                        <datalist id="rd-job-title-suggestions">
                          {rdJobTitleSuggestions.map((title) => (
                            <option key={title} value={title} />
                          ))}
                        </datalist>
                      </InputField>
                      <InputField label="Location">
                        <input
                          type="text"
                          list="rd-location-suggestions"
                          placeholder="All Locations"
                          value={
                            String(
                              rdAdvancedFilters.location || "",
                            ).toLowerCase() === "all"
                              ? ""
                              : rdAdvancedFilters.location || ""
                          }
                          onChange={(e) =>
                            setRdAdvancedFilters((prev) => ({
                              ...prev,
                              location: e.target.value,
                            }))
                          }
                          className={inputCls}
                        />
                        <datalist id="rd-location-suggestions">
                          {rdLocationSuggestions.map((location) => (
                            <option key={location} value={location} />
                          ))}
                        </datalist>
                      </InputField>
                      <InputField label="Experience">
                        <input
                          type="text"
                          list="rd-experience-suggestions"
                          placeholder="All Experience"
                          value={
                            String(
                              rdAdvancedFilters.experience || "",
                            ).toLowerCase() === "all"
                              ? ""
                              : rdAdvancedFilters.experience || ""
                          }
                          onChange={(e) =>
                            setRdAdvancedFilters((prev) => ({
                              ...prev,
                              experience: e.target.value,
                            }))
                          }
                          className={inputCls}
                        />
                        <datalist id="rd-experience-suggestions">
                          {rdExperienceSuggestions.map((experience) => (
                            <option key={experience} value={experience} />
                          ))}
                        </datalist>
                      </InputField>
                      <InputField label="Employment Type">
                        <input
                          type="text"
                          list="rd-employment-type-suggestions"
                          placeholder="e.g. C2C, W2, Full Time"
                          value={
                            String(
                              rdAdvancedFilters.employmentType || "",
                            ).toLowerCase() === "all"
                              ? ""
                              : rdAdvancedFilters.employmentType || ""
                          }
                          onChange={(e) =>
                            setRdAdvancedFilters((prev) => ({
                              ...prev,
                              employmentType: e.target.value,
                            }))
                          }
                          className={inputCls}
                        />
                        <datalist id="rd-employment-type-suggestions">
                          {rdEmploymentTypeSuggestions.map((type) => (
                            <option key={type} value={type} />
                          ))}
                        </datalist>
                      </InputField>
                    </div>
                  </div>
                )}
              </GlassCard>
            )}

            {/* ── Candidate Detail View ────────────────────────────────── */}
            {selectedRdCandidate ? (
              <div className="space-y-5 fade-up">
                {/* Back Button */}
                <button
                  onClick={handleBackToCandidates}
                  className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 font-semibold transition-colors group"
                >
                  <div className="w-7 h-7 rounded-xl border border-slate-200 bg-white flex items-center justify-center group-hover:border-indigo-300 group-hover:bg-indigo-50 transition-all shadow-sm">
                    <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                  </div>
                  Back to candidates
                </button>

                {/* Hero Profile Card */}
                <GlassCard className="overflow-visible">
                  {/* Light hero header */}
                  <div className="bg-white p-6 md:p-8 relative overflow-visible">
                    {/* Background decorations */}
                    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-t-2xl">
                      <div
                        className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
                        style={{
                          background:
                            "radial-gradient(circle, #6366f1 0%, transparent 70%)",
                          transform: "translate(30%, -30%)",
                        }}
                      />
                      <div
                        className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10"
                        style={{
                          background:
                            "radial-gradient(circle, #7c3aed 0%, transparent 70%)",
                          transform: "translate(-20%, 30%)",
                        }}
                      />
                    </div>

                    <div className="relative flex flex-col lg:flex-row lg:items-start gap-5 lg:justify-between">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="relative flex-shrink-0">
                          <Avatar name={selectedRdCandidate.name} size="lg" />
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-2xl font-black text-slate-900 font-heading tracking-tight">
                              {selectedRdCandidate.name}
                            </h3>
                            {selectedLinkedinUrl ? (
                              <a
                                href={selectedLinkedinUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#0A66C2] text-white shadow-md hover:brightness-110 transition-all hover:scale-105"
                              >
                                <svg
                                  aria-hidden="true"
                                  viewBox="0 0 24 24"
                                  className="h-4 w-4 fill-current"
                                >
                                  <path d="M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14Zm-9.6 8.2H7.1V18h2.3v-6.8Zm.1-2.3a1.3 1.3 0 1 0-2.6 0 1.3 1.3 0 0 0 2.6 0ZM18 13.6c0-2.1-1.1-3.1-2.8-3.1-1.3 0-1.9.7-2.2 1.2h0v-1.1h-2.2c0 .7 0 6.8 0 6.8h2.2v-3.8c0-.2 0-.5.1-.7.2-.5.6-1 1.3-1 .9 0 1.3.7 1.3 1.8V18H18v-4.4Z" />
                                </svg>
                              </a>
                            ) : (
                              <span
                                className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-400"
                                title="LinkedIn not available"
                              >
                                <svg
                                  aria-hidden="true"
                                  viewBox="0 0 24 24"
                                  className="h-4 w-4 fill-current"
                                >
                                  <path d="M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14Zm-9.6 8.2H7.1V18h2.3v-6.8Zm.1-2.3a1.3 1.3 0 1 0-2.6 0 1.3 1.3 0 0 0 2.6 0ZM18 13.6c0-2.1-1.1-3.1-2.8-3.1-1.3 0-1.9.7-2.2 1.2h0v-1.1h-2.2c0 .7 0 6.8 0 6.8h2.2v-3.8c0-.2 0-.5.1-.7.2-.5.6-1 1.3-1 .9 0 1.3.7 1.3 1.8V18H18v-4.4Z" />
                                </svg>
                              </span>
                            )}
                            <button
                              type="button"
                              title="Add to Hotlist"
                              onClick={openHotlistDialog}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all hover:scale-110"
                            >
                              <BsFire className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Compose Email"
                              onClick={(event) =>
                                openEmailComposeDialog(
                                  event,
                                  selectedCandidatePrimaryEmail,
                                )
                              }
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 transition-all hover:scale-110"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                            <div className="relative">
                              <button
                                ref={candidateDocsButtonRef}
                                type="button"
                                title="Uploaded documents"
                                onClick={() => {
                                  if (candidateDocsMenuOpen) {
                                    setCandidateDocsMenuOpen(false);
                                    return;
                                  }
                                  updateCandidateDocsMenuPosition();
                                  loadCandidateDocuments();
                                }}
                                disabled={candidateDocsLoading}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-all hover:bg-emerald-100 hover:scale-110 disabled:cursor-wait disabled:opacity-60"
                              >
                                {candidateDocsLoading ? (
                                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Download className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          </div>

                          <p className="text-sm text-slate-600 mb-3">
                            {selectedRdCandidate.headline ||
                              selectedRdCandidate.title ||
                              "Role"}
                            {(selectedRdCandidate.workAuthId ||
                              selectedRdCandidate.workAuth) && (
                              <>
                                {" "}
                                ·&nbsp;
                                <WorkAuthorizationLabel
                                  workAuthValue={
                                    selectedRdCandidate.workAuthId ||
                                    selectedRdCandidate.candidateInfo
                                      ?.workAuthorizationId ||
                                    selectedRdCandidate.workAuth ||
                                    selectedRdCandidate.candidateInfo
                                      ?.workAuthorization
                                  }
                                  workAuthMap={workAuthMap}
                                  fallback={
                                    workAuthMapStatus === "loading"
                                      ? "..."
                                      : "N/A"
                                  }
                                />
                              </>
                            )}
                            {selectedRdCandidate.experienceYears &&
                              String(selectedRdCandidate.experienceYears) !==
                                "0" &&
                              ` · ${selectedRdCandidate.experienceYears}`}
                          </p>

                          {/* Status pills */}
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              {getRdAvailabilityStatus(
                                selectedRdCandidate,
                                rdSubmissions[selectedRdCandidate.id],
                              )}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${selectedCandidateIsOnBench ? "border-amber-200 bg-amber-50 text-amber-700" : "border-sky-200 bg-sky-50 text-sky-700"}`}
                            >
                              <UserCheck className="h-3 w-3" />
                              {selectedCandidateIsOnBench
                                ? "On Bench"
                                : "Not in Bench"}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                              <MapPin className="w-3 h-3" />
                              {selectedRdCandidate.locationDetail ||
                                selectedRdCandidate.location ||
                                "N/A"}
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                              <Clock className="w-3 h-3" />
                              {selectedRdCandidate.benchDays} days
                            </span>
                          </div>

                          {/* Contact strip */}
                          {(() => {
                            const info =
                              selectedRdCandidate.candidateInfo || {};
                            const email =
                              selectedRdCandidate.email || info.emailId;
                            const phone = info.phoneNumber;
                            const phoneCode = info.phoneCountryCode || "";
                            const phoneDigits = `${phoneCode}${phone}`.replace(
                              /\D/g,
                              "",
                            );
                            const phoneLink = phoneDigits
                              ? `tel:+${phoneDigits}`
                              : "";
                            return (
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    openEmailComposeDialog(null, email);
                                  }}
                                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all ${email ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100" : "border-slate-200 bg-slate-50 text-slate-400 cursor-default"}`}
                                >
                                  <Mail className="h-3.5 w-3.5" />
                                  {email ? email : "No email"}
                                </button>
                                <a
                                  href={phoneLink || undefined}
                                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all ${phone ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-200 bg-slate-50 text-slate-400 cursor-default"}`}
                                  onClick={(e) => {
                                    if (!phoneLink) e.preventDefault();
                                  }}
                                >
                                  <Phone className="h-3.5 w-3.5" />
                                  {phone
                                    ? `${phoneCode} ${phone}`.trim()
                                    : "No phone"}
                                </a>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCommentsError("");
                                    setCommentDraft("");
                                    setShowCommentsDialog(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-all"
                                >
                                  <MessageCircle className="h-3.5 w-3.5" />
                                  Comment
                                </button>
                                {!selectedCandidateIsOnBench && (
                                  <button
                                    type="button"
                                    onClick={handleMoveToBenchClick}
                                    disabled={isMovingToBench}
                                    className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-all hover:bg-emerald-100 disabled:opacity-60"
                                  >
                                    <UserCheck className="h-3.5 w-3.5" />
                                    {isMovingToBench
                                      ? "Saving..."
                                      : "Move to Bench"}
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                          {moveToBenchError && (
                            <p className="mt-2 text-xs font-semibold text-rose-600">
                              {moveToBenchError}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-row lg:flex-col items-center lg:items-end gap-2 flex-shrink-0">
                        <button
                          onClick={openSubmissionModal}
                          className="rounded-xl bg-indigo-600 text-white px-4 py-2.5 text-sm font-bold shadow-sm transition-all hover:bg-indigo-700 hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap"
                        >
                          + Submission
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            openCandidateInfoModal(selectedRdCandidate)
                          }
                          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all whitespace-nowrap"
                        >
                          <FileText className="h-4 w-4" />
                          Update
                        </button>
                      </div>
                    </div>

                    {/* Submission stats strip */}
                    <div className="relative mt-5 pt-5 border-t border-slate-200">
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">
                            Bench Days
                          </span>
                          <span className="text-sm font-black text-slate-900">
                            {selectedRdCandidate.benchDays}
                          </span>
                        </div>
                        <div className="h-4 w-px bg-slate-200" />
                        <button
                          type="button"
                          onClick={() => {
                            setPipelineSelectedStage("Submission");
                            setShowSubmissionHistory((value) => !value);
                          }}
                          className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition-colors group"
                        >
                          <span>Submissions</span>
                          <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-bold text-white group-hover:bg-indigo-500 transition-colors">
                            {(() => {
                              const submission =
                                rdSubmissions[selectedRdCandidate.id];
                              return buildSubmissionHistory(submission).length;
                            })()}
                          </span>
                        </button>
                        <div className="h-4 w-px bg-slate-200" />
                        <button
                          type="button"
                          onClick={() => {
                            setMoveToBenchError("");
                            setShowCandidateEngagementModal(true);
                          }}
                          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors"
                        >
                          <Users className="h-3.5 w-3.5" />
                          Candidate Engagement
                        </button>
                        <div className="h-4 w-px bg-slate-200" />
                        <button
                          type="button"
                          onClick={() => {
                            setResponseHistoryError("");
                            setShowResponseHistoryDialog(true);
                          }}
                          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors"
                        >
                          <Activity className="h-3.5 w-3.5" />
                          History
                        </button>
                      </div>
                    </div>
                  </div>
                </GlassCard>

                {/* Submission History */}
                {showSubmissionHistory && (
                  <GlassCard className="overflow-hidden fade-up">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100/80">
                      <div>
                        <h4 className="font-bold text-slate-900 font-heading">
                          Submission Log
                        </h4>
                        <p className="text-xs text-slate-400">
                          Track pipeline stages and submission activity
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowSubmissionHistory(false)}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50 transition-colors shadow-sm"
                      >
                        Hide Log
                      </button>
                    </div>
                    {(() => {
                      const submission = rdSubmissions[selectedRdCandidate.id];
                      const history = buildSubmissionHistory(submission);
                      if (history.length === 0)
                        return (
                          <div className="p-8 text-center text-sm text-slate-400">
                            No submission history yet.
                          </div>
                        );
                      const stageFilteredHistory =
                        submissionLogFilter === "all" ||
                        submissionLogFilter === "pipeline"
                          ? history
                          : history.filter(
                              (e) =>
                                getSubmissionLogStage(e) ===
                                submissionLogFilter,
                            );
                      const companyOptions = Array.from(
                        new Set(
                          [
                            ...history.map((e) => e.client),
                            ...(submissionClientOptions || []),
                          ]
                            .filter(Boolean)
                            .map((c) => String(c).trim())
                            .filter(Boolean),
                        ),
                      ).sort((a, b) => a.localeCompare(b));
                      const normalizedCompanyQuery = submissionCompanyQuery
                        .trim()
                        .toLowerCase();
                      const visibleCompanyMatches = (
                        normalizedCompanyQuery
                          ? companyOptions.filter((c) =>
                              c.toLowerCase().includes(normalizedCompanyQuery),
                            )
                          : companyOptions
                      ).slice(0, 8);
                      const normalizedRecruiterQuery = submissionRecruiterQuery
                        .trim()
                        .toLowerCase();
                      const visibleRecruiterMatches = (
                        normalizedRecruiterQuery
                          ? allRecruiterOptions.filter((n) =>
                              n
                                .toLowerCase()
                                .includes(normalizedRecruiterQuery),
                            )
                          : allRecruiterOptions
                      ).slice(0, 8);
                      const filteredHistory = stageFilteredHistory.filter(
                        (entry) => {
                          const matchesCompany = normalizedCompanyQuery
                            ? String(entry.client || "")
                                .toLowerCase()
                                .includes(normalizedCompanyQuery)
                            : true;
                          const matchesRecruiter = normalizedRecruiterQuery
                            ? String(entry.recruiter || entry.submittedBy || "")
                                .toLowerCase()
                                .includes(normalizedRecruiterQuery)
                            : true;
                          return matchesCompany && matchesRecruiter;
                        },
                      );
                      const latestVisibleEntryId =
                        filteredHistory[0]?.id || null;
                      const itemsPerPage = 5;
                      const totalPages = Math.max(
                        1,
                        Math.ceil(filteredHistory.length / itemsPerPage),
                      );
                      const currentPage = Math.min(
                        submissionLogPage,
                        totalPages,
                      );
                      const pagedHistory = filteredHistory.slice(
                        (currentPage - 1) * itemsPerPage,
                        currentPage * itemsPerPage,
                      );
                      const pipelineStages = [
                        "Pipeline",
                        "Submission",
                        "Client Submission",
                        "Interview",
                        "Confirmation",
                        "Placement",
                        "Not Joined",
                      ];

                      return (
                        <div className="p-5">
                          {/* Stage filter pills */}
                          <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-1">
                            {submissionLogStages.map((stage) => (
                              <button
                                key={stage.key}
                                type="button"
                                onClick={() =>
                                  setSubmissionLogFilter(stage.key)
                                }
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold border transition-all whitespace-nowrap ${
                                  submissionLogFilter === stage.key
                                    ? "border-indigo-400 bg-indigo-600 text-white shadow-sm shadow-indigo-200"
                                    : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:border-slate-300"
                                }`}
                              >
                                {stage.label}
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${submissionLogFilter === stage.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-400"}`}
                                >
                                  {getSubmissionLogCount(history, stage.key)}
                                </span>
                              </button>
                            ))}
                          </div>

                          {/* Filters */}
                          <div className="flex flex-wrap gap-3 mb-4">
                            {[
                              {
                                value: submissionCompanyQuery,
                                onChange: (v) => {
                                  setSubmissionCompanyQuery(v);
                                  setIsCompanyDropdownOpen(true);
                                },
                                onFocus: () => setIsCompanyDropdownOpen(true),
                                onBlur: () =>
                                  setTimeout(
                                    () => setIsCompanyDropdownOpen(false),
                                    120,
                                  ),
                                placeholder: "Filter by company",
                                open: isCompanyDropdownOpen,
                                matches: visibleCompanyMatches,
                                onSelect: (v) => {
                                  setSubmissionCompanyQuery(v);
                                  setIsCompanyDropdownOpen(false);
                                },
                                onClear: () => {
                                  setSubmissionCompanyQuery("");
                                  setIsCompanyDropdownOpen(false);
                                },
                              },
                              {
                                value: submissionRecruiterQuery,
                                onChange: (v) => {
                                  setSubmissionRecruiterQuery(v);
                                  setIsRecruiterDropdownOpen(true);
                                },
                                onFocus: () => setIsRecruiterDropdownOpen(true),
                                onBlur: () =>
                                  setTimeout(
                                    () => setIsRecruiterDropdownOpen(false),
                                    120,
                                  ),
                                placeholder: "Filter by submitted by",
                                open: isRecruiterDropdownOpen,
                                matches: visibleRecruiterMatches,
                                onSelect: (v) => {
                                  setSubmissionRecruiterQuery(v);
                                  setIsRecruiterDropdownOpen(false);
                                },
                                onClear: () => {
                                  setSubmissionRecruiterQuery("");
                                  setIsRecruiterDropdownOpen(false);
                                },
                              },
                            ].map((filter, i) => (
                              <div key={i} className="relative">
                                <input
                                  type="text"
                                  value={filter.value}
                                  onChange={(e) =>
                                    filter.onChange(e.target.value)
                                  }
                                  onFocus={filter.onFocus}
                                  onBlur={filter.onBlur}
                                  placeholder={filter.placeholder}
                                  className="w-52 rounded-xl border border-slate-200 bg-white px-3 py-2 pr-8 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                                />
                                {filter.value && (
                                  <button
                                    type="button"
                                    onClick={filter.onClear}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                                {filter.open && filter.matches.length > 0 && (
                                  <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl scrollbar-thin">
                                    {filter.matches.map((m) => (
                                      <button
                                        key={m}
                                        type="button"
                                        onMouseDown={() => filter.onSelect(m)}
                                        className="block w-full px-3 py-2 text-left text-xs text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
                                      >
                                        {m}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Pagination */}
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs text-slate-400">
                              {filteredHistory.length} submissions · Page{" "}
                              {currentPage} of {totalPages}
                            </p>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() =>
                                  setSubmissionLogPage((p) =>
                                    Math.max(1, p - 1),
                                  )
                                }
                                disabled={currentPage <= 1}
                                className="rounded-xl border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                              >
                                <ChevronLeft className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setSubmissionLogPage((p) =>
                                    Math.min(totalPages, p + 1),
                                  )
                                }
                                disabled={currentPage >= totalPages}
                                className="rounded-xl border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                              >
                                <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Submission entries */}
                          <div className="space-y-3">
                            {pagedHistory.length === 0 ? (
                              <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
                                No submissions match the current filter.
                              </div>
                            ) : (
                              pagedHistory.map((entry) => {
                                const stageForEntry =
                                  getSubmissionLogStage(entry);
                                const activeIndex = Math.max(
                                  0,
                                  pipelineStages.indexOf(
                                    stageForEntry === "Bench"
                                      ? "Pipeline"
                                      : stageForEntry,
                                  ),
                                );
                                const showDangerProgress =
                                  isRejectedOrNotPlacedEntry(entry);
                                const submittedAt = entry.submittedAt
                                  ? new Date(entry.submittedAt).toLocaleString()
                                  : "Not submitted";
                                const recruiterMatch = findRecruiterMatch(
                                  entry.recruiter_id || entry.recruiter,
                                  entry.recruiter,
                                );
                                const recruiterDisplayName = String(
                                  recruiterMatch?.name ||
                                    resolveDirectoryNameById(
                                      entry.recruiter_id || entry.recruiter,
                                      recruiterDirectory,
                                    ) ||
                                    entry.recruiter ||
                                    entry.submittedBy ||
                                    "",
                                ).trim();
                                const isLatestVisible =
                                  latestVisibleEntryId === entry.id;
                                const isRecentlyChanged =
                                  String(
                                    recentSubmissionChange?.candidateId || "",
                                  ) === String(selectedRdCandidate?.id || "") &&
                                  recentSubmissionChange?.entryId === entry.id;
                                const highlightClass = isRecentlyChanged
                                  ? "border-2 border-emerald-400 bg-emerald-50/30"
                                  : isLatestVisible
                                    ? "border-2 border-indigo-300 bg-indigo-50/20"
                                    : "border border-slate-200/80 bg-white/80";
                                return (
                                  <div
                                    key={entry.id}
                                    className={`submission-card rounded-2xl overflow-hidden transition-colors ${highlightClass}`}
                                  >
                                    <div className="p-4">
                                      {(isLatestVisible ||
                                        isRecentlyChanged) && (
                                        <div className="mb-3 flex items-center justify-end">
                                          <span
                                            className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${isRecentlyChanged ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}
                                          >
                                            {isRecentlyChanged
                                              ? "Last Changed"
                                              : "Latest"}
                                          </span>
                                        </div>
                                      )}
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                                        <div>
                                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                            Role
                                          </p>
                                          <p className="font-bold text-indigo-700">
                                            {entry.role || "Submission record"}
                                          </p>
                                          <p className="text-slate-500 mt-0.5 font-medium">
                                            {entry.client || "Client not set"}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                            Status
                                          </p>
                                          <Badge
                                            variant={
                                              showDangerProgress
                                                ? "danger"
                                                : entry.submissionStatus
                                                  ? "info"
                                                  : "default"
                                            }
                                          >
                                            {entry.submissionStatus ||
                                              entry.submissionType ||
                                              "Submitted"}
                                          </Badge>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                            Submitted
                                          </p>
                                          <p className="font-semibold text-slate-700">
                                            {(
                                              entry.submittedBy || "Unknown"
                                            ).replace(/@vdartinc\.com/i, "")}
                                          </p>
                                          <p className="text-slate-400 mt-0.5 truncate text-[11px]">
                                            {submittedAt}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                            Submitted By
                                          </p>
                                          <p className="font-semibold text-slate-700 truncate">
                                            {recruiterDisplayName.replace(
                                              /@vdartinc\.com/i,
                                              "",
                                            ) || "Unknown"}
                                          </p>
                                        </div>
                                      </div>

                                      <PipelineProgress
                                        stages={pipelineStages}
                                        activeIndex={activeIndex}
                                        isDanger={showDangerProgress}
                                      />

                                      <div className="flex justify-end mt-3">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const isOpen = Boolean(
                                              showSubmissionEditors?.[entry.id],
                                            );
                                            if (isOpen) {
                                              setShowSubmissionEditors(
                                                (prev) => ({
                                                  ...prev,
                                                  [entry.id]: false,
                                                }),
                                              );
                                              setActiveRecruiterEditEntryId(
                                                (prev) =>
                                                  prev === entry.id
                                                    ? null
                                                    : prev,
                                              );
                                              return;
                                            }
                                            setShowSubmissionEditors(
                                              (prev) => ({
                                                ...prev,
                                                [entry.id]: true,
                                              }),
                                            );
                                            setActiveRecruiterEditEntryId(
                                              entry.id,
                                            );
                                            prepareSubmissionEditor(
                                              selectedRdCandidate.id,
                                              entry,
                                            );
                                          }}
                                          className={`rounded-xl border px-3 py-1.5 text-xs font-bold transition-all ${showSubmissionEditors?.[entry.id] ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                                        >
                                          {showSubmissionEditors?.[entry.id]
                                            ? "Hide Editor"
                                            : "Edit"}
                                        </button>
                                      </div>

                                      {showSubmissionEditors?.[entry.id] && (
                                        <div className="mt-4 pt-4 border-t border-slate-100 fade-up">
                                          {/* Section: Timeframe */}
                                          <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 mb-3">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500 mb-2.5 font-heading">
                                              Timeframe
                                            </p>
                                            <div className="grid gap-3 sm:grid-cols-3">
                                              <InputField label="Date">
                                                <input
                                                  type="date"
                                                  value={getSubmissionEditValue(
                                                    entry.id,
                                                    "submission_date",
                                                    entry.submission_date ||
                                                      (entry.submittedAt
                                                        ? entry.submittedAt.slice(
                                                            0,
                                                            10,
                                                          )
                                                        : ""),
                                                  )}
                                                  onChange={(e) => {
                                                    const dateValue =
                                                      e.target.value;
                                                    if (
                                                      isFutureDateInputValue(
                                                        dateValue,
                                                      )
                                                    ) {
                                                      showSmallNotice(
                                                        "Submission date cannot be in the future.",
                                                      );
                                                      return;
                                                    }
                                                    const parsed = dateValue
                                                      ? new Date(
                                                          `${dateValue}T00:00:00`,
                                                        )
                                                      : null;
                                                    updateSubmissionEditValue(
                                                      entry.id,
                                                      {
                                                        submission_date:
                                                          dateValue,
                                                        quarter: parsed
                                                          ? getQuarterFromDate(
                                                              parsed,
                                                            )
                                                          : getSubmissionEditValue(
                                                              entry.id,
                                                              "quarter",
                                                              entry.quarter,
                                                            ),
                                                        week: parsed
                                                          ? getIsoWeekNumber(
                                                              parsed,
                                                            )
                                                          : getSubmissionEditValue(
                                                              entry.id,
                                                              "week",
                                                              entry.week,
                                                            ),
                                                      },
                                                    );
                                                  }}
                                                  max={getDateInputValue()}
                                                  className={inputCls}
                                                />
                                              </InputField>
                                              <InputField label="Week">
                                                <input
                                                  type="text"
                                                  readOnly
                                                  value={
                                                    getSubmissionEditValue(
                                                      entry.id,
                                                      "week",
                                                      entry.week,
                                                    )
                                                      ? `Week ${getSubmissionEditValue(entry.id, "week", entry.week)}`
                                                      : ""
                                                  }
                                                  placeholder="Auto from date"
                                                  className={inputCls}
                                                />
                                              </InputField>
                                              <InputField label="Quarter">
                                                <input
                                                  type="text"
                                                  readOnly
                                                  value={
                                                    getSubmissionEditValue(
                                                      entry.id,
                                                      "quarter",
                                                      entry.quarter,
                                                    )
                                                      ? `Q${getSubmissionEditValue(entry.id, "quarter", entry.quarter)}`
                                                      : ""
                                                  }
                                                  placeholder="Auto from date"
                                                  className={inputCls}
                                                />
                                              </InputField>
                                            </div>
                                          </div>

                                          {/* Section: Role & Source */}
                                          <div className="grid gap-3 sm:grid-cols-2 mb-3">
                                            <InputField label="Role">
                                              <input
                                                type="text"
                                                placeholder="e.g. Senior Java Developer"
                                                value={getSubmissionEditValue(
                                                  entry.id,
                                                  "role",
                                                  entry.role,
                                                )}
                                                onChange={(e) =>
                                                  updateSubmissionEditValue(
                                                    entry.id,
                                                    { role: e.target.value },
                                                  )
                                                }
                                                className={inputCls}
                                              />
                                            </InputField>
                                            <InputField label="Source of Requirement">
                                              {(() => {
                                                const sourceOptions = [
                                                  "CX Mail",
                                                  "Own Mail",
                                                  "Zoom",
                                                  "In-Person",
                                                  "Ceipal",
                                                  "Talent Network",
                                                ];
                                                const rawValue =
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "requirementSource",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      [
                                                        "requirementSource",
                                                        "source_of_requirement",
                                                      ],
                                                    ),
                                                  );
                                                const options =
                                                  withCurrentOption(
                                                    sourceOptions,
                                                    rawValue,
                                                  );
                                                const value =
                                                  resolveOptionValue(
                                                    rawValue,
                                                    options,
                                                  );
                                                return (
                                                  <select
                                                    value={value}
                                                    onChange={(e) =>
                                                      updateSubmissionEditValue(
                                                        entry.id,
                                                        {
                                                          requirementSource:
                                                            e.target.value,
                                                        },
                                                      )
                                                    }
                                                    className={selectCls}
                                                  >
                                                    <option value="">
                                                      Select Source
                                                    </option>
                                                    {options.map((o) => (
                                                      <option key={o} value={o}>
                                                        {o}
                                                      </option>
                                                    ))}
                                                  </select>
                                                );
                                              })()}
                                            </InputField>
                                          </div>

                                          {/* Section: Client & Submitted By */}
                                          <div className="grid gap-3 sm:grid-cols-2 mb-3">
                                            <InputField label="Client">
                                              {(() => {
                                                const rawValue =
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "client",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      ["client", "client_name"],
                                                    ),
                                                  );
                                                const options =
                                                  withCurrentOption(
                                                    submissionClientOptions,
                                                    rawValue,
                                                  );
                                                const value =
                                                  resolveOptionValue(
                                                    rawValue,
                                                    options,
                                                  );
                                                return (
                                                  <select
                                                    value={value}
                                                    onChange={(e) =>
                                                      updateSubmissionEditValue(
                                                        entry.id,
                                                        {
                                                          client:
                                                            e.target.value,
                                                        },
                                                      )
                                                    }
                                                    className={selectCls}
                                                  >
                                                    <option value="">
                                                      Select Client
                                                    </option>
                                                    {options.map((o) => (
                                                      <option key={o} value={o}>
                                                        {o}
                                                      </option>
                                                    ))}
                                                  </select>
                                                );
                                              })()}
                                            </InputField>
                                            <InputField label="Submitted By *">
                                              <div className="relative">
                                                {(() => {
                                                  const recruiterName =
                                                    getSubmissionEditValue(
                                                      entry.id,
                                                      "recruiter",
                                                      getFirstSubmissionValue(
                                                        entry,
                                                        [
                                                          "recruiter",
                                                          "recruiter_name",
                                                        ],
                                                      ) ||
                                                        findRecruiterMatch(
                                                          getSubmissionEditValue(
                                                            entry.id,
                                                            "recruiter_id",
                                                            entry.recruiter_id,
                                                          ),
                                                        )?.name ||
                                                        "",
                                                    );
                                                  const visibleSuggestions =
                                                    getEditRecruiterSuggestions(
                                                      recruiterName,
                                                    );
                                                  return (
                                                    <>
                                                      <input
                                                        type="text"
                                                        placeholder="Search submitted by..."
                                                        value={recruiterName}
                                                        onChange={(e) => {
                                                          const v =
                                                            e.target.value;
                                                          const match =
                                                            recruiterByName.get(
                                                              v
                                                                .trim()
                                                                .toLowerCase(),
                                                            ) || null;
                                                          const linked =
                                                            resolveRecruiterLinkedFields(
                                                              match,
                                                            );
                                                          updateSubmissionEditValue(
                                                            entry.id,
                                                            {
                                                              recruiter: v,
                                                              recruiter_id:
                                                                match?.employeeId ||
                                                                match?.id ||
                                                                null,
                                                              bu: linked.bu,
                                                              bu_id:
                                                                linked.bu_id,
                                                              teamName:
                                                                linked.teamName,
                                                              team_id:
                                                                linked.team_id,
                                                            },
                                                          );
                                                          setActiveRecruiterEditEntryId(
                                                            entry.id,
                                                          );
                                                        }}
                                                        onFocus={() =>
                                                          setActiveRecruiterEditEntryId(
                                                            entry.id,
                                                          )
                                                        }
                                                        onBlur={() =>
                                                          setTimeout(
                                                            () =>
                                                              setActiveRecruiterEditEntryId(
                                                                (prev) =>
                                                                  prev ===
                                                                  entry.id
                                                                    ? null
                                                                    : prev,
                                                              ),
                                                            120,
                                                          )
                                                        }
                                                        className={inputCls}
                                                        required
                                                      />
                                                      {recruiterName && (
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            updateSubmissionEditValue(
                                                              entry.id,
                                                              {
                                                                recruiter: "",
                                                                recruiter_id:
                                                                  null,
                                                                bu: "",
                                                                bu_id: null,
                                                                teamName: "",
                                                                team_id: null,
                                                              },
                                                            );
                                                            setActiveRecruiterEditEntryId(
                                                              entry.id,
                                                            );
                                                          }}
                                                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                        >
                                                          <X className="w-3.5 h-3.5" />
                                                        </button>
                                                      )}
                                                      {activeRecruiterEditEntryId ===
                                                        entry.id &&
                                                        visibleSuggestions.length >
                                                          0 && (
                                                          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl scrollbar-thin">
                                                            {visibleSuggestions.map(
                                                              (name) => (
                                                                <button
                                                                  key={name}
                                                                  type="button"
                                                                  onMouseDown={() => {
                                                                    const match =
                                                                      recruiterByName.get(
                                                                        name
                                                                          .trim()
                                                                          .toLowerCase(),
                                                                      ) || null;
                                                                    const linked =
                                                                      resolveRecruiterLinkedFields(
                                                                        match,
                                                                      );
                                                                    updateSubmissionEditValue(
                                                                      entry.id,
                                                                      {
                                                                        recruiter:
                                                                          name,
                                                                        recruiter_id:
                                                                          match?.employeeId ||
                                                                          match?.id ||
                                                                          null,
                                                                        bu: linked.bu,
                                                                        bu_id:
                                                                          linked.bu_id,
                                                                        teamName:
                                                                          linked.teamName,
                                                                        team_id:
                                                                          linked.team_id,
                                                                      },
                                                                    );
                                                                    setActiveRecruiterEditEntryId(
                                                                      null,
                                                                    );
                                                                  }}
                                                                  className="block w-full px-3 py-2 text-left text-sm text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
                                                                >
                                                                  {name}
                                                                </button>
                                                              ),
                                                            )}
                                                          </div>
                                                        )}
                                                    </>
                                                  );
                                                })()}
                                              </div>
                                            </InputField>
                                          </div>

                                          {/* Section: BU & Team */}
                                          <div className="grid gap-3 sm:grid-cols-2 mb-3">
                                            <InputField label="BU">
                                              {(() => {
                                                const rawValue =
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "bu",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      [
                                                        "bu",
                                                        "bu_name",
                                                        "business_unit",
                                                      ],
                                                    ),
                                                  );
                                                const normalizedRawValue =
                                                  resolveDirectoryNameById(
                                                    rawValue,
                                                    buDirectory,
                                                  ) || rawValue;
                                                const options =
                                                  withCurrentOption(
                                                    buOptions,
                                                    normalizedRawValue,
                                                  );
                                                const value =
                                                  resolveOptionValue(
                                                    normalizedRawValue,
                                                    options,
                                                  );
                                                return (
                                                  <select
                                                    value={value}
                                                    disabled
                                                    className={`${selectCls} cursor-not-allowed appearance-none bg-slate-100 bg-none text-slate-500`}
                                                  >
                                                    <option value="">
                                                      Select BU
                                                    </option>
                                                    {options.map((o) => (
                                                      <option key={o} value={o}>
                                                        {o}
                                                      </option>
                                                    ))}
                                                  </select>
                                                );
                                              })()}
                                            </InputField>
                                            <InputField label="Team">
                                              {(() => {
                                                const recruiterName = String(
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "recruiter",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      [
                                                        "recruiter",
                                                        "recruiter_name",
                                                      ],
                                                    ),
                                                  ) || "",
                                                )
                                                  .trim()
                                                  .toLowerCase();
                                                const rawValue =
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "teamName",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      ["teamName", "team_name"],
                                                    ),
                                                  );
                                                const normalizedRawValue =
                                                  resolveDirectoryNameById(
                                                    rawValue,
                                                    teamDirectory,
                                                  ) || rawValue;
                                                const safeRawValue =
                                                  String(
                                                    normalizedRawValue || "",
                                                  )
                                                    .trim()
                                                    .toLowerCase() ===
                                                  recruiterName
                                                    ? ""
                                                    : normalizedRawValue;
                                                const options =
                                                  withCurrentOption(
                                                    teamNameOptions.filter(
                                                      (o) =>
                                                        String(o || "")
                                                          .trim()
                                                          .toLowerCase() !==
                                                        recruiterName,
                                                    ),
                                                    safeRawValue,
                                                  );
                                                const value =
                                                  resolveOptionValue(
                                                    safeRawValue,
                                                    options,
                                                  );
                                                return (
                                                  <select
                                                    value={value}
                                                    disabled
                                                    className={`${selectCls} cursor-not-allowed appearance-none bg-slate-100 bg-none text-slate-500`}
                                                  >
                                                    <option value="">
                                                      Select Team
                                                    </option>
                                                    {options.map((o) => (
                                                      <option key={o} value={o}>
                                                        {o}
                                                      </option>
                                                    ))}
                                                  </select>
                                                );
                                              })()}
                                            </InputField>
                                          </div>

                                          {/* Section: Type, Status & Notes */}
                                          <div className="grid gap-3 sm:grid-cols-2 mb-3">
                                            <InputField label="Type">
                                              {(() => {
                                                const rawValue =
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "submissionType",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      [
                                                        "submissionType",
                                                        "submission_type",
                                                      ],
                                                    ),
                                                  );
                                                const options =
                                                  withCurrentOption(
                                                    SUBMISSION_TYPE_OPTIONS,
                                                    rawValue,
                                                  );
                                                const value =
                                                  resolveOptionValue(
                                                    rawValue,
                                                    options,
                                                  );
                                                return (
                                                  <select
                                                    value={value}
                                                    onChange={(e) =>
                                                      updateSubmissionEditValue(
                                                        entry.id,
                                                        {
                                                          submissionType:
                                                            e.target.value,
                                                        },
                                                      )
                                                    }
                                                    className={selectCls}
                                                  >
                                                    <option value="">
                                                      Select Type
                                                    </option>
                                                    {options.map((o) => (
                                                      <option key={o} value={o}>
                                                        {o}
                                                      </option>
                                                    ))}
                                                  </select>
                                                );
                                              })()}
                                            </InputField>
                                            <InputField label="Status">
                                              {(() => {
                                                const rawValue =
                                                  getSubmissionEditValue(
                                                    entry.id,
                                                    "submissionStatus",
                                                    getFirstSubmissionValue(
                                                      entry,
                                                      [
                                                        "submissionStatus",
                                                        "submission_status",
                                                      ],
                                                    ),
                                                  );
                                                const normalizedRawValue =
                                                  resolveDirectoryNameById(
                                                    rawValue,
                                                    submissionStatusDirectory,
                                                  ) || rawValue;
                                                const options =
                                                  withCurrentOption(
                                                    submissionStatusOptions,
                                                    normalizedRawValue,
                                                  );
                                                const value =
                                                  resolveOptionValue(
                                                    normalizedRawValue,
                                                    options,
                                                  );
                                                return (
                                                  <select
                                                    value={value}
                                                    onChange={(e) =>
                                                      updateSubmissionEditValue(
                                                        entry.id,
                                                        {
                                                          submissionStatus:
                                                            e.target.value,
                                                        },
                                                      )
                                                    }
                                                    className={selectCls}
                                                  >
                                                    <option value="">
                                                      Select Status
                                                    </option>
                                                    {options.length > 0 ? (
                                                      options.map((o) => (
                                                        <option
                                                          key={o}
                                                          value={o}
                                                        >
                                                          {o}
                                                        </option>
                                                      ))
                                                    ) : (
                                                      <option value="" disabled>
                                                        No statuses
                                                      </option>
                                                    )}
                                                  </select>
                                                );
                                              })()}
                                            </InputField>
                                            <div className="sm:col-span-2">
                                              <InputField label="Notes / Reason">
                                                <textarea
                                                  value={getSubmissionEditValue(
                                                    entry.id,
                                                    "reason",
                                                    entry.reason,
                                                  )}
                                                  onChange={(e) =>
                                                    updateSubmissionEditValue(
                                                      entry.id,
                                                      {
                                                        reason: e.target.value,
                                                      },
                                                    )
                                                  }
                                                  className={inputCls}
                                                  rows="2"
                                                  placeholder="Add context, notes, or reason…"
                                                />
                                              </InputField>
                                            </div>
                                          </div>

                                          <div className="flex justify-end gap-2 mt-3">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setSubmissionHistoryEdits(
                                                  (prev) => {
                                                    const next = { ...prev };
                                                    delete next[entry.id];
                                                    return next;
                                                  },
                                                );
                                                setShowSubmissionEditors(
                                                  (prev) => ({
                                                    ...prev,
                                                    [entry.id]: false,
                                                  }),
                                                );
                                                setActiveRecruiterEditEntryId(
                                                  (prev) =>
                                                    prev === entry.id
                                                      ? null
                                                      : prev,
                                                );
                                              }}
                                              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                submitSubmissionEdits(
                                                  selectedRdCandidate.id,
                                                  entry,
                                                  totalPages,
                                                )
                                              }
                                              className="btn-primary rounded-xl px-4 py-2 text-xs font-bold text-white active:scale-95"
                                            >
                                              Save Changes
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </GlassCard>
                )}

                {/* Contract + Skills + Experience Grid */}
                <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
                  <GlassCard className="p-5">
                    <h4 className="text-sm font-bold text-slate-900 mb-4 font-heading">
                      Contract Details
                    </h4>
                    <div className="space-y-0">
                      {[
                        {
                          label: "Current Rate",
                          value: selectedRdCandidate?.contractDetails?.rate,
                        },
                        {
                          label: "Expected Salary",
                          value:
                            selectedRdCandidate?.contractDetails
                              ?.expectedSalary,
                        },
                        {
                          label: "Employment Type",
                          value:
                            selectedRdCandidate?.contractDetails
                              ?.employmentType,
                        },
                      ].map(({ label, value }) => (
                        <div
                          key={label}
                          className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0"
                        >
                          <span className="text-xs text-slate-400 font-medium">
                            {label}
                          </span>
                          <span className="text-xs font-bold text-slate-800">
                            {value || "N/A"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </GlassCard>

                  <div className="space-y-4">
                    <GlassCard className="p-5">
                      <h4 className="text-sm font-bold text-slate-900 mb-4 font-heading">
                        Technical Skills
                      </h4>
                      {[
                        {
                          label: "Primary",
                          skills: selectedSkillGroups.primary,
                          chipCls:
                            "bg-indigo-50 text-indigo-700 border-indigo-200/80 hover:bg-indigo-100 hover:border-indigo-300",
                        },
                        {
                          label: "Secondary",
                          skills: selectedSkillGroups.secondary,
                          chipCls:
                            "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100",
                        },
                      ].map(({ label, skills, chipCls }) => (
                        <div key={label} className="mb-3 last:mb-0">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 font-heading">
                            {label}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {skills.length > 0 ? (
                              skills.map((skill) => (
                                <span
                                  key={skill}
                                  className={`skill-tag text-[11px] font-semibold px-2.5 py-1 rounded-full border ${chipCls} cursor-default`}
                                >
                                  {skill}
                                </span>
                              ))
                            ) : (
                              <p className="text-xs text-slate-300">
                                None listed
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </GlassCard>

                    <GlassCard className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-bold text-slate-900 font-heading">
                          Experience
                        </h4>
                        <button
                          onClick={openAddExperienceModal}
                          className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add
                        </button>
                      </div>
                      {experienceLoading && (
                        <p className="text-xs text-slate-400 flex items-center gap-1.5">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          Loading...
                        </p>
                      )}
                      {experienceLoadError && (
                        <p className="text-xs text-rose-600 bg-rose-50 rounded-lg p-2">
                          {experienceLoadError}
                        </p>
                      )}
                      {!experienceLoading &&
                        !experienceLoadError &&
                        experienceItems.length === 0 && (
                          <p className="text-xs text-slate-400">
                            No experience records found.
                          </p>
                        )}
                      <div className="space-y-3">
                        {visibleExperienceItems.map((item) => {
                          const mentionedSkills = String(item.tags || "")
                            .split(",")
                            .map((t) => t.trim())
                            .filter(Boolean);
                          const roleLabel =
                            String(item.role || "").trim() ||
                            "Job title not provided";
                          const companyLabel =
                            String(item.client || "").trim() ||
                            "Company not provided";
                          const fromDate = normalizeDateInputValue(
                            item.from_date || item.period || "",
                          );
                          const toDate = normalizeDateInputValue(
                            item.to_date ||
                              item.completed_on ||
                              item.completedOn ||
                              "",
                          );
                          const durationLabel =
                            String(item.duration || "").trim() ||
                            (fromDate
                              ? `${fromDate} to ${toDate || "Present"}`
                              : "Duration not provided");
                          const summaryLabel =
                            String(item.summary || "").trim() ||
                            "No summary provided";
                          return (
                            <div
                              key={item.exp_id || `${item.client}-${item.role}`}
                              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1 space-y-2">
                                  <div className="grid grid-cols-[92px_1fr] items-start gap-x-2">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                      Title
                                    </p>
                                    <p className="text-sm font-black text-slate-900 leading-tight font-heading">
                                      {roleLabel}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-[92px_1fr] items-start gap-x-2">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                      Company
                                    </p>
                                    <p className="text-sm font-semibold text-indigo-700">
                                      {companyLabel}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-[92px_1fr] items-start gap-x-2">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                      Duration
                                    </p>
                                    <p className="text-xs text-slate-600">
                                      {durationLabel}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-[92px_1fr] items-start gap-x-2">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                      Summary
                                    </p>
                                    <p className="text-xs text-slate-600 leading-relaxed">
                                      {summaryLabel}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Badge
                                    variant={
                                      item.job_status === "Active"
                                        ? "success"
                                        : "default"
                                    }
                                  >
                                    {item.job_status}
                                  </Badge>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openEditExperienceModal(item)
                                    }
                                    className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:border-indigo-200 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                              <p className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 font-heading">
                                Skills Mentioned
                              </p>
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {mentionedSkills.length > 0 ? (
                                  mentionedSkills.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100"
                                    >
                                      {tag}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-[11px] text-slate-400">
                                    No skills mentioned
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {!experienceLoading &&
                        !experienceLoadError &&
                        sortedExperienceItems.length > 2 && (
                          <div className="mt-3">
                            {!showAllExperience ? (
                              <button
                                type="button"
                                onClick={() => setShowAllExperience(true)}
                                className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
                              >
                                View More ({sortedExperienceItems.length - 2}{" "}
                                more)
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setShowAllExperience(false)}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                              >
                                View Less
                              </button>
                            )}
                          </div>
                        )}
                    </GlassCard>
                  </div>
                </div>
              </div>
            ) : previewOnly ? (
              <GlassCard className="p-16 text-center">
                <Users className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400 font-heading font-semibold">
                  No candidate selected
                </p>
              </GlassCard>
            ) : (
              <>
                {/* Toolbar */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-xl font-black text-slate-900 font-heading tracking-tight">
                      {candidateListTab === "nonBench"
                        ? "Non Bench Candidates"
                        : "Bench Candidates"}
                      <span className="ml-2 text-base font-semibold text-slate-400">
                        ({activeCandidatesCount})
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Switch between bench and non-bench candidates, then select
                      candidates to manage actions
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                      {[
                        {
                          key: "bench",
                          label: "Bench",
                          count: benchCandidatesCount,
                        },
                        {
                          key: "nonBench",
                          label: "Non Bench",
                          count: nonBenchCandidatesCount,
                        },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => {
                            setCandidateListTab(tab.key);
                            setBenchCurrentPage(1);
                            clearSelectedCandidates();
                            setSelectionMode(false);
                          }}
                          className={`rounded-lg px-3 py-1.5 text-xs font-black transition-colors ${
                            candidateListTab === tab.key
                              ? "bg-indigo-600 text-white shadow-sm"
                              : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                          }`}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                    {hasActiveCandidates &&
                      selectionMode &&
                      selectedCount > 0 && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white">
                          <div className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />
                          {selectedCount} selected
                        </span>
                      )}
                    {hasActiveCandidates && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectionMode(true);
                          clearSelectedCandidates();
                        }}
                        className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm btn-ghost"
                      >
                        Select
                      </button>
                    )}
                    <button
                      onClick={() => setShowAddRdModal(true)}
                      className="btn-primary rounded-xl text-white px-3.5 py-2 text-xs font-black shadow-sm"
                    >
                      + Add Candidate
                    </button>
                    {hasActiveCandidates && (
                      <button
                        type="button"
                        onClick={openHotlistDialog}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2 text-xs font-bold text-rose-600 hover:bg-rose-100 hover:border-rose-300 transition-all shadow-sm"
                      >
                        <BsFire className="h-3.5 w-3.5" />
                        Hotlist
                      </button>
                    )}
                    {hasActiveCandidates && (
                      <button
                        type="button"
                        onClick={openEmailComposeDialog}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-2 text-xs font-bold text-sky-700 hover:bg-sky-100 hover:border-sky-300 transition-all shadow-sm"
                      >
                        <Send className="h-3.5 w-3.5" />
                        Email
                      </button>
                    )}
                  </div>
                </div>

                {hasActiveCandidates && selectionMode && (
                  <div className="flex flex-wrap items-center gap-2 p-3.5 rounded-2xl border border-indigo-200 bg-indigo-50/60 fade-up">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                    <span className="text-xs font-bold text-indigo-700">
                      {selectedCount} selected
                    </span>
                    <button
                      type="button"
                      onClick={selectAllCandidates}
                      className="rounded-xl border border-indigo-200 bg-white px-3 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      Select All ({activeCandidatesCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearSelectedCandidates();
                        setSelectionMode(false);
                      }}
                      className="rounded-xl border border-rose-200 bg-white px-3 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                )}

                {/* Candidate Cards */}
                <div className="space-y-3">
                  {activeListLoading ? (
                    <>
                      {[0, 1, 2].map((i) => candidateCardSkeleton(i))}
                      <div className="text-center text-xs text-slate-400 py-2">
                        Loading candidates...
                      </div>
                    </>
                  ) : activeListError ? (
                    <GlassCard className="p-16 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
                        <X className="w-8 h-8 text-rose-400" />
                      </div>
                      <p className="text-sm font-bold text-rose-600 font-heading">
                        Unable to load candidates
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {activeListError}
                      </p>
                      {candidateListTab === "nonBench" && (
                        <button
                          type="button"
                          onClick={fetchNonBenchCandidates}
                          className="mt-4 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50"
                        >
                          Retry
                        </button>
                      )}
                    </GlassCard>
                  ) : activeCandidatesCount === 0 ? (
                    <GlassCard className="p-16 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-sm font-bold text-slate-600 font-heading">
                        No candidates found
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Try adjusting your search or filters
                      </p>
                    </GlassCard>
                  ) : (
                    (() => {
                      return paginatedBenchCandidates.map(
                        (candidate, index) => {
                          const candidateId = String(
                            toSingleScalar(
                              candidate?.candidate_id ??
                                candidate?.candidateId ??
                                candidate?.id ??
                                "",
                            ),
                          );
                          const hasLastConnectValue = Number.isFinite(
                            lastConnectDaysByCandidateId[candidateId],
                          );
                          const lastConnectDays = hasLastConnectValue
                            ? lastConnectDaysByCandidateId[candidateId]
                            : null;
                          const benchPercent = hasLastConnectValue
                            ? Math.min(
                                100,
                                Math.max((lastConnectDays / 30) * 100, 6),
                              )
                            : 0;
                          const isHighBench =
                            hasLastConnectValue && lastConnectDays > 30;
                          const lastConnectTextClass = !hasLastConnectValue
                            ? "text-slate-500"
                            : lastConnectDays > 30
                              ? "text-rose-600"
                              : lastConnectDays > 15
                                ? "text-amber-600"
                                : "text-emerald-600";
                          const lastConnectBarClass = !hasLastConnectValue
                            ? "bg-slate-300"
                            : lastConnectDays > 30
                              ? "bg-gradient-to-r from-rose-500 to-red-400"
                              : lastConnectDays > 15
                                ? "bg-gradient-to-r from-amber-400 to-yellow-500"
                                : "bg-gradient-to-r from-emerald-500 to-green-400";
                          const availabilityText =
                            candidate.availability || "Available immediately";
                          const benchDaysValue = Number(
                            candidate?.bench_days ?? candidate?.benchDays,
                          );
                          const hasBenchDaysValue =
                            Number.isFinite(benchDaysValue);
                          const candidateLinkedinUrl =
                            getLinkedinUrl(candidate);
                          return (
                            <div
                              key={candidate.id}
                              onClick={() => setRdSidebarCandidate(candidate)}
                              className="candidate-card group cursor-pointer rounded-2xl bg-white/90 backdrop-blur-sm border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_4px_12px_rgba(0,0,0,0.04)] hover:border-indigo-200 hover:shadow-[0_4px_16px_rgba(99,102,241,0.12),0_1px_3px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 transition-all duration-200"
                              style={{
                                animationDelay: `${Math.min(index * 40, 240)}ms`,
                              }}
                            >
                              {/* Top accent line on hover */}
                              <div
                                className="h-0.5 w-0 group-hover:w-full transition-all duration-300 rounded-t-2xl"
                                style={{
                                  background:
                                    "linear-gradient(90deg, #6366f1, #a855f7)",
                                }}
                              />

                              <div className="p-5">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                  <div className="flex items-start gap-3.5 flex-1 min-w-0">
                                    {selectionMode && (
                                      <label
                                        className="mt-2 flex-shrink-0"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedCandidateIds.has(
                                            candidate.id,
                                          )}
                                          onChange={(e) =>
                                            toggleCandidateSelection(
                                              e,
                                              candidate.id,
                                            )
                                          }
                                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/20"
                                        />
                                      </label>
                                    )}
                                    <Avatar
                                      name={candidate.name}
                                      size="md"
                                      className="flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                                        <h4 className="text-base font-black text-slate-900 font-heading">
                                          {candidate.name}
                                        </h4>
                                        {candidateLinkedinUrl && (
                                          <a
                                            href={candidateLinkedinUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[#0A66C2] text-white hover:brightness-110 transition-all hover:scale-105"
                                          >
                                            <svg
                                              aria-hidden="true"
                                              viewBox="0 0 24 24"
                                              className="h-3 w-3 fill-current"
                                            >
                                              <path d="M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14Zm-9.6 8.2H7.1V18h2.3v-6.8Zm.1-2.3a1.3 1.3 0 1 0-2.6 0 1.3 1.3 0 0 0 2.6 0ZM18 13.6c0-2.1-1.1-3.1-2.8-3.1-1.3 0-1.9.7-2.2 1.2h0v-1.1h-2.2c0 .7 0 6.8 0 6.8h2.2v-3.8c0-.2 0-.5.1-.7.2-.5.6-1 1.3-1 .9 0 1.3.7 1.3 1.8V18H18v-4.4Z" />
                                            </svg>
                                          </a>
                                        )}
                                        <Badge variant="success">
                                          {candidate.status}
                                        </Badge>
                                        {isHighBench && (
                                          <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-600">
                                            <BsFire className="w-2.5 h-2.5" />
                                            Long bench
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-500 font-medium mb-1.5">
                                        {candidate.title}
                                      </p>
                                      {hasBenchDaysValue && (
                                        <p className="mb-1.5 text-[11px] font-bold text-rose-500">
                                          Bench: {benchDaysValue} days
                                        </p>
                                      )}
                                      <div className="flex flex-wrap items-center gap-2">
                                        <div className="inline-flex items-center gap-1.5 rounded-full border border-sky-200/80 bg-sky-50/60 px-2.5 py-1 text-xs font-semibold text-sky-700">
                                          <MapPin className="h-3 w-3 text-sky-500" />
                                          {candidate.location || "Location N/A"}
                                        </div>
                                        <div className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-indigo-50/60 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                          <Briefcase className="h-3 w-3 text-indigo-500" />
                                          {candidate.client ||
                                            "Previous client N/A"}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* View More Button */}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      openCandidateDetail(candidate);
                                    }}
                                    className="self-start sm:self-auto rounded-xl px-4 py-2.5 text-xs font-bold transition-all flex-shrink-0 inline-flex items-center gap-1.5 border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 hover:shadow-md hover:shadow-indigo-200 active:scale-95"
                                  >
                                    <>
                                      View Profile{" "}
                                      <ChevronRight className="h-3.5 w-3.5" />
                                    </>
                                  </button>
                                </div>

                                {/* Bench stats bar */}
                                <div className="mt-4 rounded-xl border border-slate-100 bg-gradient-to-br from-slate-50/60 to-white/40 p-4">
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                          Candidate Last Connect{" "}
                                        </p>
                                        <span
                                          className={`text-xs font-black ${lastConnectTextClass}`}
                                        >
                                          {hasLastConnectValue
                                            ? `${lastConnectDays} days`
                                            : "Not yet connected"}
                                        </span>
                                      </div>
                                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                        <div
                                          className={`bench-bar h-full rounded-full ${lastConnectBarClass}`}
                                          style={{ width: `${benchPercent}%` }}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 sm:border-l sm:border-slate-200 sm:pl-4">
                                      <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                                      </div>
                                      <div>
                                        <p className="text-xs font-bold text-slate-700">
                                          {availabilityText}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        },
                      );
                    })()
                  )}
                </div>
                {!activeListLoading && activeCandidatesCount > 0 && (
                  <div className="mt-2 flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs font-medium text-slate-600">
                      Showing{" "}
                      {(Math.min(
                        Math.max(benchCurrentPage, 1),
                        benchTotalPages,
                      ) -
                        1) *
                        benchPageSize +
                        1}
                      -
                      {Math.min(
                        Math.min(
                          Math.max(benchCurrentPage, 1),
                          benchTotalPages,
                        ) * benchPageSize,
                        activeCandidatesCount,
                      )}{" "}
                      of {activeCandidatesCount}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          handleBenchPageChange(benchCurrentPage - 1)
                        }
                        disabled={benchCurrentPage <= 1}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs font-semibold text-slate-600">
                        Page{" "}
                        {Math.min(
                          Math.max(benchCurrentPage, 1),
                          benchTotalPages,
                        )}{" "}
                        of {benchTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handleBenchPageChange(benchCurrentPage + 1)
                        }
                        disabled={benchCurrentPage >= benchTotalPages}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Add Candidate Modal */}
            {showAddRdModal && (
              <CandidateUpdateModal
                isOpen={showAddRdModal}
                mode="create"
                candidate={null}
                draft={null}
                onClose={() => setShowAddRdModal(false)}
                onSave={handleCreateCandidateFromInfo}
                rdApiBaseUrl={rdApiBaseUrl}
              />
            )}

            {/* Candidate Update Modal */}
            <CandidateUpdateModal
              isOpen={showCandidateInfoModal}
              candidate={candidateInfoTarget}
              draft={candidateInfoDraft}
              onClose={closeCandidateInfoModal}
              onSave={handleSaveCandidateInfo}
              onSaveDraft={(d) => setCandidateInfoDraft(d)}
              onClearDraft={() => setCandidateInfoDraft(null)}
              rdApiBaseUrl={rdApiBaseUrl}
              enableEngagement={false}
            />

            {/* Candidate Engagement Modal */}
            <CandidateEngagementModal
              isOpen={showCandidateEngagementModal}
              candidate={selectedRdCandidate}
              rdApiBaseUrl={rdApiBaseUrl}
              onClose={() => setShowCandidateEngagementModal(false)}
              showRemoveBenchButton={selectedCandidateIsOnBench}
              onRemoveBench={handleRemoveBenchFromEngagement}
              removeBenchLoading={isMovingToBench}
              onSaved={async (savedEngagement) => {
                const normalizedCandidateResponse = String(
                  savedEngagement?.candidateResponse || "",
                )
                  .trim()
                  .toLowerCase();
                const shouldReturnToBenchList =
                  normalizedCandidateResponse === "not interested" ||
                  normalizedCandidateResponse === "backout";
                const candidateId = String(
                  savedEngagement?.candidateId ??
                    selectedRdCandidate?.candidate_id ??
                    selectedRdCandidate?.candidateId ??
                    selectedRdCandidate?.id ??
                    "",
                ).trim();
                if (candidateId && !shouldReturnToBenchList) {
                  const applyEngagementUpdate = (candidate) => {
                    if (!candidate) return candidate;
                    const currentId = String(
                      candidate?.candidate_id ??
                        candidate?.candidateId ??
                        candidate?.id ??
                        "",
                    ).trim();
                    if (currentId !== candidateId) return candidate;
                    return {
                      ...candidate,
                      candidate_connect:
                        savedEngagement?.candidateConnect ??
                        candidate?.candidate_connect ??
                        "",
                      connected_via:
                        savedEngagement?.connectedVia ??
                        candidate?.connected_via ??
                        "",
                      bench_status_id:
                        savedEngagement?.benchStatusId ??
                        candidate?.bench_status_id ??
                        candidate?.benchStatus ??
                        "",
                      benchStatus:
                        savedEngagement?.benchStatusId ??
                        candidate?.benchStatus ??
                        candidate?.bench_status_id ??
                        "",
                      candidate_response:
                        savedEngagement?.candidateResponse ??
                        candidate?.candidate_response ??
                        candidate?.candidateResponse ??
                        "",
                      candidateResponse:
                        savedEngagement?.candidateResponse ??
                        candidate?.candidateResponse ??
                        candidate?.candidate_response ??
                        "",
                      candidate_comments:
                        savedEngagement?.comments ??
                        candidate?.candidate_comments ??
                        candidate?.comments ??
                        "",
                      comments:
                        savedEngagement?.comments ??
                        candidate?.comments ??
                        candidate?.candidate_comments ??
                        "",
                      candidateInfo: {
                        ...(candidate?.candidateInfo || {}),
                        candidate_connect:
                          savedEngagement?.candidateConnect ??
                          candidate?.candidateInfo?.candidate_connect ??
                          "",
                        connected_via:
                          savedEngagement?.connectedVia ??
                          candidate?.candidateInfo?.connected_via ??
                          "",
                        bench_status_id:
                          savedEngagement?.benchStatusId ??
                          candidate?.candidateInfo?.bench_status_id ??
                          candidate?.candidateInfo?.benchStatus ??
                          "",
                        benchStatus:
                          savedEngagement?.benchStatusId ??
                          candidate?.candidateInfo?.benchStatus ??
                          candidate?.candidateInfo?.bench_status_id ??
                          "",
                        candidate_response:
                          savedEngagement?.candidateResponse ??
                          candidate?.candidateInfo?.candidate_response ??
                          candidate?.candidateInfo?.candidateResponse ??
                          "",
                        candidateResponse:
                          savedEngagement?.candidateResponse ??
                          candidate?.candidateInfo?.candidateResponse ??
                          candidate?.candidateInfo?.candidate_response ??
                          "",
                        candidate_comments:
                          savedEngagement?.comments ??
                          candidate?.candidateInfo?.candidate_comments ??
                          candidate?.candidateInfo?.comments ??
                          "",
                        comments:
                          savedEngagement?.comments ??
                          candidate?.candidateInfo?.comments ??
                          candidate?.candidateInfo?.candidate_comments ??
                          "",
                      },
                    };
                  };
                  setRdCandidates((prev) =>
                    Array.isArray(prev)
                      ? prev.map((candidate) =>
                          applyEngagementUpdate(candidate),
                        )
                      : prev,
                  );
                  setRdSidebarCandidate((prev) => applyEngagementUpdate(prev));
                  setSelectedRdCandidate((prev) => applyEngagementUpdate(prev));
                  try {
                    const latest = await fetchCandidateById(
                      candidateId,
                      selectedRdCandidate,
                    );
                    if (latest) {
                      setSelectedRdCandidate(latest);
                      setRdSidebarCandidate(latest);
                    }
                  } catch (_error) {}
                }
                try {
                  await Promise.resolve(onBenchUpdated?.());
                } catch (_error) {}
                if (shouldReturnToBenchList) {
                  setShowSubmissionHistory(false);
                  setShowResponseHistoryDialog(false);
                  setResponseHistoryError("");
                  setRdSidebarCandidate(null);
                  if (previewOnly && typeof onClosePreview === "function")
                    onClosePreview();
                  else setSelectedRdCandidate(null);
                  showSmallNotice(
                    "Candidate engagement saved. Bench list refreshed.",
                  );
                  return;
                }
                showSmallNotice("Candidate engagement saved.");
                setResponseHistoryError("");
                setShowResponseHistoryDialog(true);
              }}
            />
          </section>
        </div>
      </div>

      {/* ── PORTALS ──────────────────────────────────────────────────────────── */}

      {/* ── Experience Modal ─────────────────────────────────────────────── */}
      {showExperienceModal &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md p-0 sm:p-4">
            <div className="w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 scrollbar-thin scale-in">
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-6 py-4 z-10 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-black text-slate-900 font-heading">
                      {String(newExperience?.update_key || "").trim()
                        ? "Edit Experience"
                        : "Add Experience"}
                    </h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {String(newExperience?.update_key || "").trim()
                        ? "Update role, company and skills"
                        : "Highlight recent roles and impact"}
                    </p>
                  </div>
                  <button
                    onClick={closeExperienceModal}
                    className="w-8 h-8 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <InputField label="Job Title">
                    <input
                      type="text"
                      placeholder="e.g. Senior Developer"
                      value={newExperience.role}
                      onChange={(e) =>
                        setNewExperience((p) => ({
                          ...p,
                          role: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </InputField>
                  <InputField label="Status">
                    <select
                      value={newExperience.status}
                      onChange={(e) =>
                        setNewExperience((p) => ({
                          ...p,
                          status: e.target.value,
                          completedOn:
                            e.target.value === "Completed" ? p.completedOn : "",
                        }))
                      }
                      className={selectCls}
                    >
                      <option value="Active">Active</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </InputField>
                  <InputField label="Company" className="sm:col-span-2">
                    <input
                      type="text"
                      placeholder="Company name"
                      value={newExperience.company}
                      onChange={(e) =>
                        setNewExperience((p) => ({
                          ...p,
                          company: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </InputField>
                  <InputField label="Start Date">
                    <input
                      type="date"
                      value={newExperience.period}
                      onChange={(e) =>
                        setNewExperience((p) => ({
                          ...p,
                          period: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </InputField>
                  {newExperience.status === "Completed" && (
                    <InputField label="End Date">
                      <input
                        type="date"
                        value={newExperience.completedOn || ""}
                        onChange={(e) =>
                          setNewExperience((p) => ({
                            ...p,
                            completedOn: e.target.value,
                          }))
                        }
                        className={inputCls}
                      />
                    </InputField>
                  )}
                  <InputField label="Summary" className="sm:col-span-2">
                    <textarea
                      placeholder="Brief summary of responsibilities and achievements"
                      value={newExperience.summary}
                      onChange={(e) =>
                        setNewExperience((p) => ({
                          ...p,
                          summary: e.target.value,
                        }))
                      }
                      className={inputCls}
                      rows="3"
                    />
                  </InputField>
                  <InputField
                    label="Skills Mentioned (comma separated)"
                    className="sm:col-span-2"
                  >
                    <input
                      type="text"
                      placeholder="React, Node.js, AWS"
                      value={newExperience.tags}
                      onChange={(e) =>
                        setNewExperience((p) => ({
                          ...p,
                          tags: e.target.value,
                        }))
                      }
                      className={inputCls}
                    />
                  </InputField>
                </div>
                {experienceError && (
                  <p className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                    {experienceError}
                  </p>
                )}
              </div>
              <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-100 px-6 py-4 flex justify-end gap-2.5 rounded-b-3xl">
                <button
                  onClick={closeExperienceModal}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isSavingExperience}
                  onClick={async () => {
                    if (!selectedRdCandidate?.id || isSavingExperience) return;
                    setExperienceError("");
                    setIsSavingExperience(true);
                    const tags = newExperience.tags
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean);
                    const fromDate = newExperience.period || null;
                    const toDate =
                      newExperience.status === "Completed"
                        ? newExperience.completedOn || null
                        : null;
                    const duration =
                      fromDate && toDate
                        ? `${fromDate} to ${toDate}`
                        : fromDate
                          ? `${fromDate} to Present`
                          : null;
                    const updateKey = String(
                      newExperience.update_key || "",
                    ).trim();
                    const payload = {
                      candidateId: selectedRdCandidate.id,
                      candidate_id: selectedRdCandidate.id,
                      client: newExperience.company,
                      role: newExperience.role,
                      duration,
                      from_date: fromDate,
                      to_date: toDate,
                      job_status: newExperience.status,
                      summary: newExperience.summary,
                      tags: tags.join(", "),
                      ...(updateKey ? { update_key: updateKey } : {}),
                      updatedBy: currentUser?.id || null,
                    };
                    try {
                      const response = await fetch(SAVE_EXPERIENCE_URL, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                      });
                      if (!response.ok)
                        throw new Error(`HTTP ${response.status}`);
                      const result = await response.json();
                      if (result?.status && result.status !== "success")
                        throw new Error(result?.message || "Save failed");
                      await loadExperience(selectedRdCandidate.id);
                      closeExperienceModal();
                    } catch (err) {
                      setExperienceError(
                        err?.message || "Unable to save experience.",
                      );
                    } finally {
                      setIsSavingExperience(false);
                    }
                  }}
                  className="btn-primary rounded-xl px-4 py-2.5 text-sm font-black text-white disabled:opacity-50"
                >
                  {isSavingExperience
                    ? "Saving..."
                    : String(newExperience?.update_key || "").trim()
                      ? "Update Experience"
                      : "Save Experience"}
                </button>
              </div>
            </div>
          </div>,
        )}

      {/* ── Submission Modal ──────────────────────────────────────────────── */}
      {showSubmissionModal &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md p-0 sm:p-4">
            <div className="w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 scrollbar-thin scale-in">
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-6 py-4 z-10 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-slate-900 font-heading">
                      New Submission
                    </h4>
                    {/* <p className="text-xs text-slate-400 mt-0.5">Record a candidate submission for tracking</p> */}
                    <p className="text-lg text-slate-600 mt-1">
                      <span className="font-semibold text-slate-800">
                        {selectedRdCandidate?.name || "Candidate"}
                      </span>
                      <span className="text-slate-500">
                        {" "}
                        -{" "}
                        {selectedRdCandidate?.headline ||
                          selectedRdCandidate?.title ||
                          "Job title not set"}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={closeSubmissionModal}
                    className="w-8 h-8 rounded-xl text-slate-400 hover:bg-slate-100 transition-colors flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {/* Timeframe */}
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500 mb-3 font-heading">
                    Timeframe
                  </p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <InputField label="Date *">
                      <input
                        type="date"
                        value={submissionForm.date}
                        onChange={(e) => {
                          const dateValue = e.target.value;
                          if (isFutureDateInputValue(dateValue)) {
                            setSubmissionFieldErrors((prev) => ({
                              ...prev,
                              date: true,
                            }));
                            setSubmissionError(
                              "Submission date cannot be in the future.",
                            );
                            showSmallNotice(
                              "Submission date cannot be in the future.",
                            );
                            return;
                          }
                          const parsedDate = dateValue
                            ? new Date(`${dateValue}T00:00:00`)
                            : null;
                          clearSubmissionFieldError("date");
                          setSubmissionForm((p) => {
                            const next = { ...p, date: dateValue };
                            if (parsedDate) {
                              next.quarter = getQuarterFromDate(parsedDate);
                              next.week = getIsoWeekNumber(parsedDate);
                            } else {
                              next.quarter = "";
                              next.week = "";
                            }
                            return next;
                          });
                        }}
                        max={getDateInputValue()}
                        className={getSubmissionFieldClass("date", inputCls)}
                        required
                      />
                    </InputField>
                    <InputField label="Week">
                      <input
                        type="text"
                        value={
                          submissionForm.week
                            ? `Week ${submissionForm.week}`
                            : ""
                        }
                        readOnly
                        placeholder="Auto from date"
                        className={inputCls}
                      />
                    </InputField>
                    <InputField label="Quarter">
                      <input
                        type="text"
                        value={
                          submissionForm.quarter
                            ? `Q${submissionForm.quarter}`
                            : ""
                        }
                        readOnly
                        placeholder="Auto from date"
                        className={inputCls}
                      />
                    </InputField>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <InputField label="Role *">
                    <input
                      type="text"
                      placeholder="e.g. Senior Java Developer"
                      value={submissionForm.role}
                      onChange={(e) => {
                        clearSubmissionFieldError("role");
                        setSubmissionForm((p) => ({
                          ...p,
                          role: e.target.value,
                          customRole: "",
                        }));
                      }}
                      className={getSubmissionFieldClass("role", inputCls)}
                      required
                    />
                  </InputField>
                  <InputField label="Source of Requirement *">
                    <select
                      value={submissionForm.requirementSource}
                      onChange={(e) => {
                        clearSubmissionFieldError("requirementSource");
                        setSubmissionForm((p) => ({
                          ...p,
                          requirementSource: e.target.value,
                        }));
                      }}
                      className={getSubmissionFieldClass(
                        "requirementSource",
                        selectCls,
                      )}
                      required
                    >
                      <option value="">Select Source</option>
                      {[
                        "CX Mail",
                        "Own Mail",
                        "Zoom",
                        "In-Person",
                        "Ceipal",
                        "Talent Network",
                      ].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </InputField>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <InputField label="Client *">
                    <select
                      value={submissionForm.client}
                      onChange={(e) => {
                        clearSubmissionFieldError("client");
                        setSubmissionForm((p) => ({
                          ...p,
                          client: e.target.value,
                          customClient:
                            e.target.value === "custom" ? p.customClient : "",
                        }));
                      }}
                      className={getSubmissionFieldClass("client", selectCls)}
                      required
                    >
                      <option value="">Select Client</option>
                      {submissionClientOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                      <option value="custom">Custom Client…</option>
                    </select>
                  </InputField>
                  <InputField label="Submitted By *">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search submitted by..."
                        value={submissionForm.recruiter || ""}
                        onChange={(e) => {
                          clearSubmissionFieldError("recruiter");
                          const v = e.target.value;
                          const match =
                            recruiterByName.get(v.trim().toLowerCase()) || null;
                          const linked = resolveRecruiterLinkedFields(match);
                          setSubmissionForm((p) => ({
                            ...p,
                            recruiter: v,
                            bu: linked.bu || "",
                            teamName: linked.teamName || "",
                          }));
                          setIsRecruiterModalDropdownOpen(true);
                        }}
                        onFocus={() => setIsRecruiterModalDropdownOpen(true)}
                        onBlur={() =>
                          setTimeout(
                            () => setIsRecruiterModalDropdownOpen(false),
                            120,
                          )
                        }
                        className={getSubmissionFieldClass(
                          "recruiter",
                          inputCls,
                        )}
                        required
                      />
                      {submissionForm.recruiter && (
                        <button
                          type="button"
                          onClick={() => {
                            clearSubmissionFieldError("recruiter");
                            setSubmissionForm((p) => ({
                              ...p,
                              recruiter: "",
                              bu: "",
                              teamName: "",
                            }));
                            setIsRecruiterModalDropdownOpen(true);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isRecruiterModalDropdownOpen &&
                        visibleRecruiterSuggestions.length > 0 && (
                          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl scrollbar-thin">
                            {visibleRecruiterSuggestions.map((name) => (
                              <button
                                key={name}
                                type="button"
                                onMouseDown={() => {
                                  clearSubmissionFieldError("recruiter");
                                  const match =
                                    recruiterByName.get(
                                      name.trim().toLowerCase(),
                                    ) || null;
                                  const linked =
                                    resolveRecruiterLinkedFields(match);
                                  setSubmissionForm((p) => ({
                                    ...p,
                                    recruiter: name,
                                    bu: linked.bu || "",
                                    teamName: linked.teamName || "",
                                  }));
                                  setIsRecruiterModalDropdownOpen(false);
                                }}
                                className="block w-full px-3 py-2 text-left text-sm text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
                              >
                                {name}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </InputField>
                  <InputField label="BU">
                    <select
                      value={submissionForm.bu}
                      disabled
                      className={`${selectCls} cursor-not-allowed appearance-none bg-slate-100 bg-none text-slate-500`}
                    >
                      <option value="">Select BU</option>
                      {buOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </InputField>
                  <InputField label="Team">
                    <select
                      value={(() => {
                        const recruiterName = String(
                          submissionForm.recruiter || "",
                        )
                          .trim()
                          .toLowerCase();
                        const resolved =
                          resolveDirectoryNameById(
                            submissionForm.teamName,
                            teamDirectory,
                          ) ||
                          (/^\d+$/.test(
                            String(submissionForm.teamName || "").trim(),
                          )
                            ? ""
                            : submissionForm.teamName);
                        return String(resolved || "")
                          .trim()
                          .toLowerCase() === recruiterName
                          ? ""
                          : resolved;
                      })()}
                      disabled
                      className={`${selectCls} cursor-not-allowed appearance-none bg-slate-100 bg-none text-slate-500`}
                    >
                      <option value="">Select Team</option>
                      {(teamNameOptions.length > 0
                        ? teamNameOptions.filter((o) => {
                            const text = String(o || "").trim();
                            return (
                              !/^\d+$/.test(text) &&
                              text.toLowerCase() !==
                                String(submissionForm.recruiter || "")
                                  .trim()
                                  .toLowerCase()
                            );
                          })
                        : ["Unable to load"]
                      ).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </InputField>
                  {submissionForm.client === "custom" && (
                    <InputField label="Custom Client" className="sm:col-span-2">
                      <input
                        type="text"
                        placeholder="Enter client name"
                        value={submissionForm.customClient || ""}
                        onChange={(e) => {
                          clearSubmissionFieldError("client");
                          setSubmissionForm((p) => ({
                            ...p,
                            customClient: e.target.value,
                          }));
                        }}
                        className={getSubmissionFieldClass("client", inputCls)}
                      />
                    </InputField>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <InputField label="Type *">
                    <select
                      value={submissionForm.submissionType}
                      onChange={(e) => {
                        clearSubmissionFieldError("submissionType");
                        setSubmissionForm((p) => ({
                          ...p,
                          submissionType: e.target.value,
                        }));
                      }}
                      className={getSubmissionFieldClass(
                        "submissionType",
                        selectCls,
                      )}
                      required
                    >
                      <option value="">Select Type</option>
                      {SUBMISSION_TYPE_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </InputField>
                  <InputField label="Status *">
                    <select
                      value={submissionForm.submissionStatus}
                      onChange={(e) => {
                        clearSubmissionFieldError("submissionStatus");
                        setSubmissionForm((p) => ({
                          ...p,
                          submissionStatus: e.target.value,
                        }));
                      }}
                      className={getSubmissionFieldClass(
                        "submissionStatus",
                        selectCls,
                      )}
                      required
                    >
                      <option value="">Select Status</option>
                      {submissionStatusOptions.length > 0 ? (
                        submissionStatusOptions.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No statuses available
                        </option>
                      )}
                    </select>
                  </InputField>
                  <InputField label="Notes / Reason" className="sm:col-span-2">
                    <textarea
                      placeholder="Add context, notes, or reason…"
                      value={submissionForm.reason}
                      onChange={(e) =>
                        setSubmissionForm((p) => ({
                          ...p,
                          reason: e.target.value,
                        }))
                      }
                      className={inputCls}
                      rows="3"
                    />
                  </InputField>
                </div>
                {submissionError && (
                  <p className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                    {submissionError}
                  </p>
                )}
              </div>
              <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-100 px-6 py-4 flex justify-end gap-2.5 rounded-b-3xl">
                <button
                  onClick={closeSubmissionModal}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isSavingSubmission}
                  onClick={async () => {
                    if (isSavingSubmission) return;
                    const finalRole = submissionForm.role.trim();
                    const customClient = (
                      submissionForm.customClient || ""
                    ).trim();
                    const finalClient =
                      submissionForm.client === "custom"
                        ? customClient
                        : submissionForm.client;
                    const nextFieldErrors = {};
                    if (!String(submissionForm.date || "").trim())
                      nextFieldErrors.date = true;
                    if (isFutureDateInputValue(submissionForm.date))
                      nextFieldErrors.date = true;
                    if (!String(finalRole || "").trim())
                      nextFieldErrors.role = true;
                    if (!String(submissionForm.requirementSource || "").trim())
                      nextFieldErrors.requirementSource = true;
                    if (!String(finalClient || "").trim())
                      nextFieldErrors.client = true;
                    if (!String(submissionForm.recruiter || "").trim())
                      nextFieldErrors.recruiter = true;
                    if (!String(submissionForm.submissionType || "").trim())
                      nextFieldErrors.submissionType = true;
                    if (!String(submissionForm.submissionStatus || "").trim())
                      nextFieldErrors.submissionStatus = true;
                    if (Object.keys(nextFieldErrors).length > 0) {
                      setSubmissionFieldErrors(nextFieldErrors);
                      setSubmissionError(
                        isFutureDateInputValue(submissionForm.date)
                          ? "Submission date cannot be in the future."
                          : "Please fill all mandatory fields marked in red.",
                      );
                      return;
                    }
                    setSubmissionFieldErrors({});
                    if (finalRole)
                      setSubmissionRoleOptions((p) =>
                        p.includes(finalRole) ? p : [...p, finalRole],
                      );
                    if (finalClient)
                      setSubmissionClientOptions((p) =>
                        p.includes(finalClient) ? p : [...p, finalClient],
                      );
                    setSubmissionError("");
                    setIsSavingSubmission(true);
                    const parsedSubmissionDate = submissionForm.date
                      ? new Date(`${submissionForm.date}T00:00:00`)
                      : null;
                    const derivedQuarter = parsedSubmissionDate
                      ? getQuarterFromDate(parsedSubmissionDate)
                      : "";
                    const derivedWeek = parsedSubmissionDate
                      ? getIsoWeekNumber(parsedSubmissionDate)
                      : "";
                    const submittedByName =
                      currentUser?.name || currentUser?.email || "Unknown";
                    const submittedById = currentUser?.id || null;
                    const normalizedStatus = String(
                      submissionForm.submissionStatus || "",
                    ).trim();
                    const statusIdFromName =
                      submissionStatusIdByName.get(
                        normalizedStatus.toLowerCase(),
                      ) ?? null;
                    const submissionStatusId =
                      statusIdFromName ??
                      (/^\d+$/.test(normalizedStatus)
                        ? Number(normalizedStatus)
                        : null);
                    const selectedRecruiter =
                      recruiterByName.get(
                        String(submissionForm.recruiter || "")
                          .trim()
                          .toLowerCase(),
                      ) || null;
                    const payload = {
                      candidate_id: selectedRdCandidate?.id || null,
                      quarter: derivedQuarter || submissionForm.quarter || "",
                      week: derivedWeek || submissionForm.week || null,
                      submission_date: submissionForm.date,
                      role: finalRole || submissionForm.role,
                      source_of_requirement:
                        submissionForm.requirementSource || null,
                      client: finalClient || submissionForm.client || null,
                      recruiter: submissionForm.recruiter || null,
                      recruiter_id:
                        selectedRecruiter?.employeeId ||
                        selectedRecruiter?.id ||
                        recruiterIdByName.get(
                          String(submissionForm.recruiter || "")
                            .trim()
                            .toLowerCase(),
                        ) ||
                        null,
                      bu_id:
                        selectedRecruiter?.buId ||
                        buIdByName.get(
                          String(submissionForm.bu || "")
                            .trim()
                            .toLowerCase(),
                        ) ||
                        null,
                      team_id:
                        selectedRecruiter?.teamId ||
                        teamIdByName.get(
                          String(submissionForm.teamName || "")
                            .trim()
                            .toLowerCase(),
                        ) ||
                        null,
                      submission_type: submissionForm.submissionType || null,
                      submission_status:
                        submissionForm.submissionStatus || null,
                      submission_status_id: submissionStatusId,
                      reason: submissionForm.reason || null,
                      submitted_by: submittedById,
                      submitted_by_name: submittedByName,
                      updatedBy: submittedById,
                    };
                    try {
                      const response = await fetch(SAVE_SUBMISSION_URL, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                      });
                      if (!response.ok)
                        throw new Error(`HTTP ${response.status}`);
                      const result = await response.json();
                      if (result?.status && result.status !== "success")
                        throw new Error(result?.message || "Save failed");
                      submissionDetailsCacheRef.current.delete(
                        String(
                          toSingleScalar(selectedRdCandidate?.id || "") || "",
                        ),
                      );
                      if (selectedRdCandidate) {
                        const submittedAt =
                          result?.submission_date ||
                          result?.data?.submission_date ||
                          result?.submitted_at ||
                          payload.submission_date ||
                          new Date().toISOString();
                        const newEntry = {
                          ...submissionForm,
                          quarter: payload.quarter,
                          week: payload.week,
                          role: finalRole || submissionForm.role,
                          client: finalClient || submissionForm.client,
                          submittedBy: submittedByName,
                          submittedAt,
                          last_changed_at: submittedAt,
                          updated_at: submittedAt,
                          id: `submission-${Date.now()}`,
                        };
                        setRdSubmissions((prev) => ({
                          ...prev,
                          [selectedRdCandidate.id]: {
                            ...newEntry,
                            history: [
                              newEntry,
                              ...buildSubmissionHistory(
                                prev?.[selectedRdCandidate.id],
                              ),
                            ],
                          },
                        }));
                        setRecentSubmissionChange({
                          candidateId: selectedRdCandidate.id,
                          entryId: newEntry.id,
                        });
                      }
                      closeSubmissionModal();
                      showSmallNotice("Submission saved successfully.");
                    } catch (err) {
                      const message =
                        err?.message || "Unable to save submission.";
                      setSubmissionError(message);
                      showSmallNotice(message);
                    } finally {
                      setIsSavingSubmission(false);
                    }
                  }}
                  className="btn-primary rounded-xl px-5 py-2.5 text-sm font-black text-white disabled:opacity-50"
                >
                  {isSavingSubmission ? "Submitting…" : "Submit"}
                </button>
              </div>
            </div>
          </div>,
        )}

      {/* ── Hotlist Dialog ────────────────────────────────────────────────── */}
      {showRemoveBenchDialog &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-950/60 backdrop-blur-md p-4">
            <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/10 scale-in">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-black text-slate-900 font-heading">
                  Remove Bench
                </h4>
                <button
                  type="button"
                  onClick={closeRemoveBenchDialog}
                  className="w-8 h-8 rounded-xl text-slate-400 hover:bg-slate-100 transition-colors flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-slate-600">
                Remove{" "}
                <span className="font-semibold text-slate-900">
                  {selectedRdCandidate?.name || "this candidate"}
                </span>{" "}
                from bench?
              </p>
              <div className="mt-4">
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={removeBenchReason}
                  onChange={(event) => {
                    setRemoveBenchReason(event.target.value);
                    if (moveToBenchError) setMoveToBenchError("");
                  }}
                  placeholder="Enter reason..."
                  className={`${inputCls} ${moveToBenchError ? inputErrorCls : ""}`}
                  rows="3"
                />
                {moveToBenchError && (
                  <p className="mt-2 text-xs font-semibold text-rose-600">
                    {moveToBenchError}
                  </p>
                )}
              </div>
              <div className="mt-5 flex gap-2.5">
                <button
                  type="button"
                  onClick={closeRemoveBenchDialog}
                  className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmRemoveBench}
                  disabled={isMovingToBench}
                  className="flex-1 rounded-xl py-2.5 text-sm font-black text-white shadow-sm hover:shadow-md transition-all active:scale-95 disabled:opacity-60"
                  style={{
                    background: "linear-gradient(135deg, #d97706, #f59e0b)",
                  }}
                >
                  {isMovingToBench ? "Saving..." : "OK"}
                </button>
              </div>
            </div>
          </div>,
        )}

      {showHotlistDialog &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-950/60 backdrop-blur-md p-4">
            <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-slate-900/10 scale-in">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                    style={{
                      background: "linear-gradient(135deg, #ef4444, #f97316)",
                    }}
                  >
                    <BsFire className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900 font-heading">
                      Add to Hotlist
                    </h4>
                    <p className="text-xs text-slate-400">
                      {selectedCount > 0
                        ? `${selectedCount} candidates selected`
                        : "Select an existing hotlist"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowHotlistDialog(false)}
                  className="w-8 h-8 rounded-xl text-slate-400 hover:bg-slate-100 transition-colors flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <InputField label="Hotlist">
                <select
                  value={hotlistSelection}
                  onChange={(e) => {
                    setHotlistSelection(e.target.value);
                    setHotlistError("");
                  }}
                  className={selectCls}
                  disabled={hotlistLoading || hotlistSubmitting}
                >
                  <option value="">
                    {hotlistLoading
                      ? "Loading hotlists..."
                      : "Select from hotlists..."}
                  </option>
                  {hotlistOptions.map((group) => (
                    <option key={group.id} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </InputField>
              {hotlistError && (
                <p className="mt-2 text-xs font-semibold text-rose-600">
                  {hotlistError}
                </p>
              )}
              <div className="flex gap-2.5 mt-5">
                <button
                  type="button"
                  onClick={() => setShowHotlistDialog(false)}
                  className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleHotlistSubmit}
                  disabled={hotlistSubmitting || hotlistLoading}
                  className="flex-1 rounded-xl py-2.5 text-sm font-black text-white shadow-sm hover:shadow-md transition-all active:scale-95"
                  style={{
                    background: "linear-gradient(135deg, #ef4444, #f97316)",
                  }}
                >
                  {hotlistSubmitting ? "Adding..." : "Add to List"}
                </button>
              </div>
            </div>
          </div>,
        )}

      {showMappingImportModal &&
        renderPortal(
          <RDMappingImportModal
            isOpen={showMappingImportModal}
            onClose={() => {
              if (mappingImportConfirming) return;
              setShowMappingImportModal(false);
              setMappingImportPreview(null);
              setMappingImportError("");
            }}
            onPreview={handleMappingImportPreview}
            onConfirm={handleMappingImportConfirm}
            onDownloadTemplate={downloadMappingTemplate}
            preview={mappingImportPreview}
            loading={mappingImportLoading}
            confirming={mappingImportConfirming}
            error={mappingImportError}
          />,
        )}

      <RDEmailComposeDialog
        isOpen={showEmailComposeDialog}
        onClose={() => setShowEmailComposeDialog(false)}
        initialTo={emailDialogInitialTo}
        apiBaseUrl={commentsApiBaseUrl}
        onSent={() => showSmallNotice("Email sent successfully.")}
      />

      {/* ── Select Dialog ─────────────────────────────────────────────────── */}
      {/* ── Comments Dialog ───────────────────────────────────────────────── */}
      {showResponseHistoryDialog &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md p-0 sm:p-4">
            <div className="w-full sm:max-w-3xl rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 overflow-hidden scale-in">
              <div className="profile-hero px-6 py-5 relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 80% 20%, #6366f1 0%, transparent 50%)",
                  }}
                />
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold font-heading">
                      Candidate Response
                    </p>
                    <h3 className="mt-1 text-xl font-black text-white font-heading">
                      {selectedRdCandidate?.name || "Response History"}
                    </h3>
                    <p className="mt-0.5 text-sm text-indigo-300">
                      {responseHistoryTab === "response"
                        ? candidateResponseHistoryList.length
                        : candidateBenchActivityList.length}{" "}
                      entries
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResponseHistoryDialog(false);
                      setResponseHistoryTab("response");
                      setResponseHistoryError("");
                      setBenchActivityError("");
                    }}
                    className="w-9 h-9 rounded-xl border border-white/20 bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="max-h-[65vh] overflow-y-auto p-5 bg-slate-50/40">
                <div className="mb-5 inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setResponseHistoryTab("response")}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${responseHistoryTab === "response" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    Candidate Response
                  </button>
                  <button
                    type="button"
                    onClick={() => setResponseHistoryTab("bench")}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${responseHistoryTab === "bench" ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    Bench History
                  </button>
                </div>

                {responseHistoryTab === "response" ? (
                  responseHistoryLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                      <RefreshCw className="h-4 w-4 animate-spin text-indigo-500" />
                      Loading response history...
                    </div>
                  ) : responseHistoryError ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                      {responseHistoryError}
                    </div>
                  ) : candidateResponseHistoryList.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center">
                      <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm font-bold text-slate-500 font-heading">
                        No response history found
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {candidateResponseHistoryList.map((item, idx) => {
                        const candidateConnect = pickFirstText(
                          item,
                          ["candidate_connect", "candidateConnect"],
                          "N/A",
                        );
                        const connectedVia = pickFirstText(
                          item,
                          [
                            "connected_via",
                            "candidate_connect_via",
                            "connectedVia",
                            "candidateConnectVia",
                          ],
                          "N/A",
                        );
                        const candidateResponse = pickFirstText(
                          item,
                          [
                            "candidate_response",
                            "candidateResponse",
                            "response",
                          ],
                          "N/A",
                        );
                        const comments = pickFirstText(
                          item,
                          [
                            "candidate_reason",
                            "candidateReason",
                            "candidate_comments",
                            "candidateComments",
                            "comments",
                            "reason",
                          ],
                          "N/A",
                        );
                        const updatedBy = pickFirstText(
                          item,
                          [
                            "updated_by_name",
                            "updatedByName",
                            "updated_by",
                            "updatedBy",
                            "added_by_name",
                            "addedBy",
                          ],
                          "N/A",
                        );
                        const updatedOnRaw = pickFirstText(
                          item,
                          [
                            "updated_on",
                            "updatedOn",
                            "created_at",
                            "added_on",
                            "added_at",
                          ],
                          "",
                        );
                        const updatedOn =
                          updatedOnRaw && updatedOnRaw !== "N/A"
                            ? new Date(updatedOnRaw).toLocaleString()
                            : "N/A";
                        return (
                          <div
                            key={
                              item?.id ||
                              item?.engagement_id ||
                              `response-${idx}`
                            }
                            className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                          >
                            <div className="grid gap-2 sm:grid-cols-2">
                              <p className="text-xs text-slate-500">
                                <span className="font-semibold text-slate-700">
                                  Candidate Connect:
                                </span>{" "}
                                {candidateConnect}
                              </p>
                              <p className="text-xs text-slate-500">
                                <span className="font-semibold text-slate-700">
                                  Connected Via:
                                </span>{" "}
                                {connectedVia}
                              </p>
                              <p className="text-xs text-slate-500">
                                <span className="font-semibold text-slate-700">
                                  Candidate Response:
                                </span>{" "}
                                {candidateResponse}
                              </p>
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                              <span className="font-semibold text-slate-700">
                                Comments:
                              </span>{" "}
                              {comments}
                            </p>
                            <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                              <span>Updated by: {updatedBy}</span>
                              <span>{updatedOn}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : benchActivityLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                    <RefreshCw className="h-4 w-4 animate-spin text-indigo-500" />
                    Loading bench history...
                  </div>
                ) : benchActivityError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                    {benchActivityError}
                  </div>
                ) : candidateBenchActivityList.length === 0 ? (
                  <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center">
                    <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-500 font-heading">
                      No bench history found
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {candidateBenchActivityList.map((item, idx) => {
                      const action = pickFirstText(item, ["action"], "N/A");
                      const reason = pickFirstText(item, ["reason"], "N/A");
                      const movedByFirstName = pickFirstText(
                        item,
                        ["moved_by_fname"],
                        "",
                      );
                      const movedByLastName = pickFirstText(
                        item,
                        ["moved_by_lname"],
                        "",
                      );
                      const movedById = pickFirstText(item, ["moved_by"], "");
                      const movedBy =
                        `${movedByFirstName} ${movedByLastName}`.trim() ||
                        movedById ||
                        "N/A";
                      const movedOnRaw = pickFirstText(
                        item,
                        ["moved_on", "movedOn", "created_at", "createdAt"],
                        "",
                      );
                      const movedOn =
                        movedOnRaw && movedOnRaw !== "N/A"
                          ? new Date(movedOnRaw).toLocaleString()
                          : "N/A";
                      return (
                        <div
                          key={item?.bench_id || `bench-${idx}`}
                          className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                        >
                          <div className="grid gap-2 sm:grid-cols-2">
                            <p className="text-xs text-slate-500">
                              <span className="font-semibold text-slate-700">
                                Action:
                              </span>{" "}
                              {action}
                            </p>
                            <p className="text-xs text-slate-500">
                              <span className="font-semibold text-slate-700">
                                Moved By:
                              </span>{" "}
                              {movedBy}
                            </p>
                          </div>
                          <p className="mt-2 text-xs text-slate-500">
                            <span className="font-semibold text-slate-700">
                              Reason:
                            </span>{" "}
                            {reason}
                          </p>
                          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                            <span>
                              Bench ID:{" "}
                              {pickFirstText(
                                item,
                                ["bench_id", "benchId"],
                                "N/A",
                              )}
                            </span>
                            <span>{movedOn}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>,
        )}

      {showCommentsDialog &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md p-0 sm:p-4">
            <div className="w-full sm:max-w-3xl rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 overflow-hidden scale-in">
              {/* Header */}
              <div className="profile-hero px-6 py-5 relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 80% 20%, #6366f1 0%, transparent 50%)",
                  }}
                />
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold font-heading">
                      Candidate Discussion
                    </p>
                    <h3 className="mt-1 text-xl font-black text-white font-heading">
                      {selectedRdCandidate?.name || "Comments"}
                    </h3>
                    <p className="mt-0.5 text-sm text-indigo-300">
                      {candidateCommentsList.length} entries
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommentsDialog(false);
                      setCommentDraft("");
                      setCommentsError("");
                    }}
                    className="w-9 h-9 rounded-xl border border-white/20 bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-[1.2fr_1fr]">
                {" "}
                {/* Comments list */}
                <div
                  className="overflow-y-auto p-5 border-r border-slate-100 scrollbar-thin"
                  style={{ maxHeight: "60vh" }}
                >
                  {commentsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                      <RefreshCw className="h-4 w-4 animate-spin text-indigo-500" />
                      Loading comments…
                    </div>
                  ) : candidateCommentsList.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                      <MessageCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm font-bold text-slate-500 font-heading">
                        No comments yet
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Add a professional update
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {candidateCommentsList.map((item, idx) => (
                        <div
                          key={item.comment_id || item.id || `c-${idx}`}
                          className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm hover:border-indigo-100 transition-colors"
                        >
                          <p className="text-sm leading-relaxed text-slate-700">
                            {item.comment ||
                              item.note_text ||
                              item.comments ||
                              item.note ||
                              "N/A"}
                          </p>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-xs font-bold text-indigo-600">
                              {item.added_by_name ||
                                item.added_by ||
                                item.created_by ||
                                "Unknown"}
                            </span>
                            <span className="text-[11px] text-slate-400">
                              {item.added_on || item.added_at
                                ? new Date(
                                    item.added_on || item.added_at,
                                  ).toLocaleString()
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Add comment */}
                <div className="p-5 flex flex-col bg-slate-50/40">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 font-heading">
                    Add Comment
                  </p>
                  <textarea
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    placeholder="Write a concise, professional update…"
                    className={`${inputCls} resize-none`}
                    style={{
                      height: "120px",
                      minHeight: "120px",
                      maxHeight: "120px",
                    }}
                  />
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {commentTemplates.map((tpl, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setCommentDraft(tpl)}
                        className="rounded-xl border border-indigo-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
                      >
                        Template {i + 1}
                      </button>
                    ))}
                  </div>
                  {commentsError && (
                    <p className="mt-2 text-xs font-semibold text-rose-600">
                      {commentsError}
                    </p>
                  )}
                  <div className="flex gap-2.5 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCommentsDialog(false);
                        setCommentDraft("");
                        setCommentsError("");
                      }}
                      className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={saveCandidateComment}
                      disabled={commentsSaving || !commentDraft.trim()}
                      className="btn-primary flex-1 rounded-xl py-2.5 text-sm font-black text-white disabled:opacity-50"
                    >
                      {commentsSaving ? "Saving…" : "Save Comment"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
        )}

      {/* ── Requirement Modal ─────────────────────────────────────────────── */}
      {showRequirementModal &&
        renderPortal(
          <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center bg-slate-950/60 backdrop-blur-md p-0 sm:p-4">
            <div className="w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl ring-1 ring-slate-900/10 scrollbar-thin scale-in">
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-6 py-4 z-10 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-black text-slate-900 font-heading">
                      Requirement Center
                    </h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Plan and track candidate requirements
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setRequirementError("");
                      setShowRequirementModal(false);
                    }}
                    className="w-8 h-8 rounded-xl text-slate-400 hover:bg-slate-100 transition-colors flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    {
                      label: "Today",
                      value: requirementSubmissionStats.all.daily,
                      color: "blue",
                      icon: Zap,
                    },
                    {
                      label: "This Week",
                      value: requirementSubmissionStats.all.weekly,
                      color: "violet",
                      icon: TrendingUp,
                    },
                    {
                      label: "This Month",
                      value: requirementSubmissionStats.all.monthly,
                      color: "emerald",
                      icon: Activity,
                    },
                    {
                      label: "Total",
                      value: requirementSubmissionStats.all.total,
                      color: "amber",
                      icon: Star,
                    },
                  ].map(({ label, value, color, icon }) => (
                    <StatCard
                      key={label}
                      label={label}
                      value={value}
                      icon={icon}
                      color={color}
                    />
                  ))}
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-6">
                  {[
                    {
                      label: "Candidate Submissions",
                      value: requirementSubmissionStats.selected.total,
                      cls: "border-indigo-200 bg-indigo-50 text-indigo-900",
                    },
                    {
                      label: "Drafted Requirements",
                      value: requirementProgress.total,
                      cls: "border-emerald-200 bg-emerald-50 text-emerald-900",
                    },
                    {
                      label: `Addressed / Pending`,
                      value: `${requirementProgress.addressed}/${requirementProgress.pending}`,
                      cls: "border-amber-200 bg-amber-50 text-amber-900",
                    },
                  ].map(({ label, value, cls }) => (
                    <div
                      key={label}
                      className={`rounded-2xl border p-4 ${cls}`}
                    >
                      <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-1 font-heading">
                        {label}
                      </p>
                      <p className="text-2xl font-black">{value}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  {requirementForm.map((row, index) => (
                    <div
                      key={row.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-black uppercase tracking-widest text-slate-500 font-heading">
                          Requirement {index + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleRemoveRequirement(row)}
                          disabled={
                            requirementForm.length <= 1 || isSavingRequirement
                          }
                          className="rounded-xl border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold text-slate-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40 transition-all"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <InputField label="BU">
                          <select
                            value={row.bu}
                            onChange={(e) =>
                              setRequirementForm((p) =>
                                p.map((item) =>
                                  item.id === row.id
                                    ? { ...item, bu: e.target.value }
                                    : item,
                                ),
                              )
                            }
                            className={selectCls}
                          >
                            <option value="">Select BU</option>
                            {buOptions.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </InputField>
                        <InputField label="Client">
                          <select
                            value={row.client}
                            onChange={(e) =>
                              setRequirementForm((p) =>
                                p.map((item) =>
                                  item.id === row.id
                                    ? { ...item, client: e.target.value }
                                    : item,
                                ),
                              )
                            }
                            className={selectCls}
                          >
                            <option value="">Select Client</option>
                            {submissionClientOptions.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </InputField>
                        <InputField label="Job Title">
                          <input
                            type="text"
                            placeholder="e.g. Full Stack Engineer"
                            value={row.jobTitle}
                            onChange={(e) =>
                              setRequirementForm((p) =>
                                p.map((item) =>
                                  item.id === row.id
                                    ? { ...item, jobTitle: e.target.value }
                                    : item,
                                ),
                              )
                            }
                            className={inputCls}
                          />
                        </InputField>
                        <InputField label="Status">
                          <select
                            value={row.status}
                            onChange={(e) =>
                              setRequirementForm((p) =>
                                p.map((item) =>
                                  item.id === row.id
                                    ? { ...item, status: e.target.value }
                                    : item,
                                ),
                              )
                            }
                            className={selectCls}
                          >
                            <option value="">Select Status</option>
                            <option value="Addressed">Addressed</option>
                            <option value="Not Addressed">Not Addressed</option>
                          </select>
                        </InputField>
                      </div>
                      <p className="mt-3 text-[11px] text-slate-400">
                        Created by{" "}
                        <strong className="text-slate-600">
                          {row.createdBy}
                        </strong>{" "}
                        · {new Date(row.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setRequirementForm((p) => [...p, buildRequirementRow()])
                  }
                  disabled={isSavingRequirement}
                  className="mt-4 w-full rounded-2xl border-2 border-dashed border-slate-200 py-3.5 text-sm font-bold text-slate-400 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/30 transition-all disabled:opacity-40"
                >
                  + Add Another Requirement
                </button>

                {requirementError && (
                  <p className="mt-3 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                    {requirementError}
                  </p>
                )}
              </div>

              <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-slate-100 px-6 py-4 flex justify-end gap-2.5 rounded-b-3xl">
                <button
                  onClick={() => {
                    setRequirementError("");
                    setShowRequirementModal(false);
                  }}
                  disabled={isSavingRequirement}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRequirement}
                  disabled={isSavingRequirement}
                  className="btn-primary rounded-xl px-5 py-2.5 text-sm font-black text-white disabled:opacity-50"
                >
                  {isSavingRequirement ? "Saving…" : "Save Requirements"}
                </button>
              </div>
            </div>
          </div>,
        )}
    </div>
  );
};

export default RDTeamSection;
